import * as React from 'react';
import { useLater } from '@redhand/ui'; // MRGD7/TECH1
import { t, type MessageKey } from '@reos/i18n';
import { DomainApiError } from '../lib/api-error.js';
import { isOwnerOrAdmin } from '../lib/capabilities.js';
import { useSession } from '../stores/session.js';
import {
  Building2,
  Camera,
  ChevronLeft,
  Clock,
  CreditCard,
  Ellipsis,
  FileText,
  ListChecks,
  Megaphone,
  Phone,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { Link, useNavigate } from '@tanstack/react-router';
import type {
  R3FixtureEvidence,
  R3FixtureFact,
  R3FixtureQuestion,
  R3FixtureSitePlan,
} from '@reos/duediligence';
import { AddressAutocomplete } from '../components/AddressAutocomplete.js';
import { PreparingSection } from '../components/PreparingSection.js';
import { CaseInvoicesPanel } from '../components/invoices.js';
import { CaseDemandsPanel } from '../components/case-demands-panel.js';
import { CaseEventsPanel } from '../components/case-events-panel.js';
import { ContractsScreen } from '../components/contracts.js';
import { AnswerQuestionPanel, type AnswerTarget } from '../components/AnswerQuestionPanel.js';
import { ParcelMap } from '../components/ParcelMap.js';
import { OfferPanel } from '../components/OfferPanel.js';
import { fetchOfferGeometry, type OfferGeometryDto } from '../lib/offer-api.js';
import { ParcelPanel } from '../components/ParcelPanel.js';
// Krok K0e: ukoly, ktere u zakazky dela clovek ve verejnych registrech.
import { HumanTasksPanel } from '../components/HumanTasksPanel.js';
import { OwnerIdentityCard } from '../components/owner-identity-card.js';
import { Card, Pill, StatusBadge } from '../components/CaseUiPrimitives.js';
import { recordBrokerClaim, type BrokerClaimInput } from '../lib/claims-api.js';
import {
  exportDownloadUrl,
  listCaseExports,
  reportPreviewUrl,
  requestPdfExport,
  type DdExport,
  type ReportExportKind,
} from '../lib/exports-api.js';
import {
  accessSummary,
  areaReconciliationResult,
  areaRows,
  caseHeader,
  casePhase,
  evidenceRows,
  factImpactSeverity,
  historyEvents,
  loadCaseDetail,
  NoAuditCaseError,
  narrowingFactorsSorted,
  openQuestionsSorted,
  riskSummary,
  tabFacts,
  utilitiesSummary,
  valuationScenarios,
  type CaseDetail,
  type FactImpactSeverity,
  type GateVerdict,
} from '../lib/dd-api.js';
import {
  AI_INVOLVEMENT_CAPTIONS,
  bandVisual,
  DOMAIN_CAPTIONS,
  DOMAIN_ORDER,
  EVIDENCE_KIND_CAPTIONS,
  factRef,
  factsByDomain,
  factValueNote,
  factValueSummary,
  GATE_CAPTIONS,
  humanFactCaption,
  humanizeConnectionValue,
  IMPACT_SEVERITY_CAPTIONS,
  nodeLabelById,
  OWNER_CAPTIONS,
  svgDataUri,
  REVIEW_ROLE_CAPTIONS,
  STOP_REASON_CAPTIONS,
  stopKind,
  humanValueEntries,
} from '../lib/case-captions.js';
import {
  caseSubjectOf,
  gateCaseKindOf,
  resolveCaseSubject,
  type CaseSubject,
  type GateCaseKind,
} from '../lib/case-subject.js';
import { gateProgress } from '../lib/case-gates.js';
import {
  caseFreshness,
  cockpitStatus,
  cockpitTasks,
  type CockpitTask,
  type HumanTaskSummary,
} from '../lib/case-cockpit.js';
import { CockpitTab } from '../components/CockpitTab.js';
import { ClientReportCard } from '../components/client-report-card.js';
import { PublicListingCard } from '../components/public-listing-card.js';
import { WorkTimerCard } from '../components/work-timer-card.js';
import { AdvertisingPerformanceCard } from '../components/advertising-performance-card.js';
import { CommissionSharesCard } from '../components/commission-shares-card.js';
import { ListingDealBlock } from '../components/listing-deal-block.js';
/* Krok SEZ1 bod 2: „Zapsat" — tatáž komponenta jako u klienta. */
import { RecordActivityButton } from '../components/record-activity-button.js';
import {
  CaseAreasCard,
  CaseHero,
  CaseMoneyCard,
  CasePeopleCard,
  CasePropertyCard,
  CasePropertyFactsCard,
  casePersonsOf,
} from '../components/case-aside.js';
import { CaseFreshnessBar } from '../components/CaseFreshnessBar.js';
import {
  getAuditRunStatus,
  getReviewOptions,
  isDemoSampleRun,
  isRunPending,
  recheckCaseAudit,
  REVIEW_LEVEL_DEFAULT,
  type ReviewLevel,
  type ReviewOptionsDto,
} from '../lib/audit-api.js';
import { ReviewLevelChoice, reviewConfirmBodyFor } from '../components/ReviewLevelChoice.js';
import { brokerText, captionOrPreparing } from '../lib/broker-text.js';
/** Krok PRF1 (bod 4): chyba u pole predmetu — tentyz postup jako v pruvodci. */
import { focusField } from '../lib/form-errors.js';
/* Krok P8F6 bod 3: radky bloku „Údaje nemovitosti" (sdilene s kartou puvodu). */
import { casePropertyFactRows } from '../lib/case-origin.js';
/* Krok ZAK1 bod 5: panel „Co zbývá zjistit" nad sablonou naboru. */
import { CaseRemainingCard } from '../components/case-remaining-card.js';
import { caseRemaining } from '../lib/case-remaining.js';
/* Krok LV1: placene stazeni vypisu z LV za kredit kancelare. */
import { getLvAvailability, purchaseLv, LvPurchaseError, type LvAvailability } from '../lib/lv-api.js';
import { prefillStatus } from '../lib/case-last-event.js';
import { getChecklistTemplate, getIntakeContext } from '../lib/field-intake-api.js';
import type { ChecklistTemplateDto, IntakeContextDto } from '@reos/field-intake';
import {
  fetchPropertyDetail,
  fetchPropertyFactConflicts,
  fetchPropertyFacts,
  formatMoney,
  resolvePropertyFactConflict,
  type PropertyFact,
  type PropertyFactConflict,
} from '../lib/properties.js';
import { explainCasePriceRange, getCasePriceRange, type CasePriceRangeDto } from '../lib/price-range-api.js';
import {
  getCase,
  listCaseParticipants,
  listCaseTimeline,
  requestPropertyPrefill,
  transitionCase,
  updateCase,
  type CaseParticipant,
  type CaseTimelineItem,
  type KanbanCase,
} from '../lib/cases-api.js';
import { listMembers } from '../lib/members-api.js';
import { listOffices, type OfficeDto } from '../lib/offices-api.js';
import { getParty, listPartyContacts } from '../lib/parties-api.js';
import { listHumanTasks } from '../lib/human-tasks-api.js';
import { loadCaseCovers } from '../lib/case-covers.js';
import type { CoverInfo } from '../lib/case-rows.js';
import { caseJourney, type CaseJourney } from '../lib/case-journey.js';
import { caseDisplayName, UUID_RE, type CaseNameTexts } from '../lib/case-title.js';
import { CANCELLED_STAGE, CASE_TYPE_LABELS, type KanbanCaseType } from '../lib/case-stages.js';
import { PropertyGallery } from '../components/PropertyGallery.js';
import { PropertyImage } from '../components/PropertyImage.js';
import {
  CASE_PHASE_CAPTIONS,
  ANSWER_QUESTION_TEXTS,
  CASE_TABS,
  CASE_TAB_PREPARING,
  CASE_TEXTS,
  fixDiacritics,
  type CaseTabId,
  SITEPLAN_DEGRADED_TEXTS,
  SITEPLAN_MISSING_CAPTIONS,
  SITEPLAN_NO_ROAD_NOTE,
  INPUTS_PRIVACY_NOTE,
  CASE_TAB_DEFAULT,
  CASE_TABS_WITHOUT_AUDIT,
  isCaseTabId,
  CASE_STATUS_LABELS,
  CASE_STATE_WAITING,
  CASE_REVISION_NOT_AVAILABLE,
  CASE_REVISION_SNAPSHOT_HINT,
  CLAIM_CONFLICT_CAPTIONS,
  TECHNICAL_DETAIL_SUMMARY,
  AREA_LABELS,
  SITEPLAN_SCENARIO_TEXTS,
  corridorScenarioCaption,
  sitePlanVersionLine,
  BROKER_CLAIM_TEXTS,
  CASE_PRIMARY_TABS,
  CASE_OVERFLOW_TABS,
  CASE_AUDIT_ENTRY_TAB,
  casePrimaryTabOf,
} from '../lib/texts.js';
import { caseProgressView } from '../lib/case-progress.js';
import {
  caseReviewAreas,
  reviewProgressText,
  type CaseReviewAreas,
  type ReviewArea,
} from '../lib/case-review-areas.js';
import { caseEventLines } from '../lib/case-last-event.js';
import type { CaseAdviceAction } from '../lib/case-advice.js';
import { fetchListingDraftSummary } from '../lib/listing-draft-api.js';
import { V4Bar, V4NavBar, V4NextStep, V4PhotoCard, V4Pill, V4Row } from '../components/ux-v4.js';
import {
  CaseDetailV4,
  CaseReviewSection,
  type CaseV4Section,
} from '../components/case-detail-v4.js';
import { CaseLastActivityCard, CaseNextStepCard } from '../components/case-overview.js';
import { listCaseCosts, type CaseCostsResult } from '../lib/costs-api.js';
import { markGuideSeen } from '../lib/guide-api.js';

/**
 * DETAIL ZAKAZKY (BLOK 2, ukol 3) — dle navrhu 07-audit-detail / 06-case-
 * geometrie: hlavicka s drobecky, zalozky, tri sloupce (souhrn | obsah
 * zalozky | rizika+pristup+site). Jen data z fixture pres datovou vrstvu
 * lib/dd-api.ts; fakt bez evidence se nevykresli, chybejici pole =
 * "nezjisteno". Trust band vzdy text + stupnice, archiv "stav k datu",
 * AI kroky oznacene.
 */

const NOT_DETERMINED = t('webAdmin.caseListTexts.unknown', { locale: 'cs' });

/**
 * Krok PIL3 bod 1 (nalez F01) — VERDIKT BRANY NA OBRAZOVKU.
 *
 * Tri stavy, ne dva. `UNKNOWN` = brana v datech NENI (audit nebezel, zakazka
 * je starsi, nemovitost neni urcena) — a to NENI dukaz. Driv se chybejici
 * brana cetla jako `false` a obrazovka napsala „doloženo" / „ověřena"
 * u zakazky s 0/5 overenymi oblastmi.
 *
 * Verdikt pocita JEDNA funkce (`gateVerdict` v @reos/reports), kterou pouziva
 * i interni PDF a prezentace majiteli — tyhle mapy jsou jen jeho popisky.
 */
const LEGAL_ACCESS_TAB_KEY: Readonly<Record<GateVerdict, MessageKey>> = {
  DOCUMENTED: 'webAdmin.caseDetail.access.documented',
  OPEN: 'webAdmin.caseDetail.access.notDocumentedGateOpen',
  UNKNOWN: 'webAdmin.caseDetail.access.notKnownNoGate',
};

const LEGAL_ACCESS_SIDE_KEY: Readonly<Record<GateVerdict, MessageKey>> = {
  DOCUMENTED: 'webAdmin.caseDetail.right.documented',
  OPEN: 'webAdmin.caseDetail.right.notDocumented',
  UNKNOWN: 'webAdmin.caseDetail.right.notKnown',
};

const SEWER_CAPACITY_SIDE_KEY: Readonly<Record<GateVerdict, MessageKey>> = {
  DOCUMENTED: 'webAdmin.caseDetail.utilities.sewerCapacityVerified',
  OPEN: 'webAdmin.caseDetail.utilities.sewerCapacityNotVerified',
  UNKNOWN: 'webAdmin.caseDetail.utilities.sewerCapacityUnknown',
};

/**
 * Krok UXV4a2 bod 4 — druh nemovitosti (`properties.kind`) na druh auditniho
 * pripadu. `complex` (soubor nemovitosti) zamerne chybi: prověření takovy druh
 * nezna a prelozit ho na dum nebo pozemek by znamenalo spustit beh nad necim
 * jinym, nez zakazka je. Pak zustane vyber prazdny a vybere ho makler
 * (ROZHODNUTI FUNK1-1).
 */
const DD_KIND_BY_PROPERTY_KIND: Readonly<Record<string, GateCaseKind>> = {
  land: 'LAND',
  building: 'HOUSE',
  unit: 'FLAT',
};

/** Nabidka druhu pro prvni prověření — tytez tri druhy, jaké zna brana auditu. */
const REVIEW_KIND_OPTIONS: readonly { readonly value: GateCaseKind; readonly label: string }[] = [
  { value: 'LAND', label: t('webAdmin.caseSubject.land', { locale: 'cs' }) },
  { value: 'HOUSE', label: t('webAdmin.caseSubject.house', { locale: 'cs' }) },
  { value: 'FLAT', label: t('webAdmin.caseSubject.flat', { locale: 'cs' }) },
];

/**
 * Krok QAFIX14 bod 2 — jak casto a jak dlouho se stranka pta na beh ve fronte.
 * Pet sekund je rad, ve kterem se stav behu meni (fronta -> bezi -> hotovo);
 * strop dve minuty je doba, po ktere uz „za chvilku hotovo" neni pravda.
 */
const REVIEW_POLL_MS = 5000;
const REVIEW_POLL_MAX = 24;

function czDateTime(iso: string | null): string {
  if (iso === null) return '—';
  return new Date(iso).toLocaleString('cs-CZ', { dateStyle: 'medium', timeStyle: 'short' });
}

function czDate(iso: string | null): string {
  if (iso === null) return '—';
  return new Date(iso).toLocaleDateString('cs-CZ');
}

function m2(value: number | null): string {
  return value === null ? NOT_DETERMINED : `${value.toLocaleString('cs-CZ')} m²`;
}

// ---- drobne prezentacni atomy (sdilene napric zalozkami, viz CaseUiPrimitives.tsx) ----

function Detail({ summary, children }: { summary: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <details style={{ marginTop: 8 }}>
      <summary style={{ cursor: 'pointer', color: 'var(--rh-text-muted)', fontSize: 'var(--rh-size-meta)' }}>{summary}</summary>
      <div style={{ marginTop: 6, fontSize: 'var(--rh-size-meta)', color: 'var(--rh-text-muted)', lineHeight: 1.6 }}>{children}</div>
    </details>
  );
}

/** Trust band VZDY text + stupnice (nikdy jen barva). */
function TrustBadge({ band, index }: { band: string | null; index?: number | null }): React.JSX.Element {
  const v = bandVisual(band);
  return (
    <Pill
      caption={`${v.meter} ${v.caption}`}
      bg={v.bg}
      fg={v.fg}
      title={
        index !== undefined && index !== null
          ? t('webAdmin.caseDetail.trustIndexTitle', { locale: 'cs', values: { index: String(index) } })
          : undefined
      }
    />
  );
}

function AiBadge({ types }: { types: readonly string[] }): React.JSX.Element | null {
  if (types.length === 0) return null;
  const caption = types.map((t) => captionOrPreparing(AI_INVOLVEMENT_CAPTIONS[t], t)).join(', ');
  const isAi = types.some((t) => t.startsWith('AI_'));
  return (
    <Pill
      caption={`${isAi ? '✨' : '⚙'} ${caption}`}
      bg={isAi ? 'var(--rh-broker-bg)' : 'var(--rh-rule)'}
      fg={isAi ? 'var(--rh-broker)' : 'var(--rh-text-muted)'}
      title={t('webAdmin.caseDetail.aiInvolvementTitle', { locale: 'cs' })}
    />
  );
}

const IMPACT_DOT_COLOR: Readonly<Record<FactImpactSeverity, string>> = {
  critical: 'var(--rh-danger)',
  high: 'var(--rh-warning-dot)',
  medium: 'var(--rh-caution-dot)',
  low: 'var(--rh-icon-muted)',
  none: 'var(--rh-border)',
};

/** Tecka dopadu v zalozce "Co vime" (13-dukazy-otazky) - vzdy i textovy title, ne jen barva. */
function ImpactDot({ severity }: { severity: FactImpactSeverity }): React.JSX.Element {
  return (
    <span
      title={IMPACT_SEVERITY_CAPTIONS[severity]}
      aria-label={IMPACT_SEVERITY_CAPTIONS[severity]}
      role="img"
      style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 999, background: IMPACT_DOT_COLOR[severity] }}
    />
  );
}

/**
 * Prouzek metriky v hlavicce (pripravenost/duvera, navrh 07-audit-detail) —
 * vzdy cislo + stupnice + kratke vysvetleni pod cislem (BLOK 10 Ukol 2), aby
 * procento nezustalo nevysvetlene ("82 %" samo o sobe rika malo).
 */
function MetricBar({ label, pct, explain }: { label: string; pct: number | null; explain: string }): React.JSX.Element {
  const tone = pct === null ? 'warning' : pct >= 70 ? 'success' : pct >= 40 ? 'warning' : 'danger';
  return (
    <div style={{ minWidth: 168 }} data-testid={`case-metric-${label}`}>
      <div style={{ fontSize: 'var(--rh-size-meta)', color: 'var(--rh-text-muted)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 'var(--rh-size-title2)', fontWeight: 700, lineHeight: 1.1 }}>{pct === null ? NOT_DETERMINED : `${String(pct)} %`}</div>
      <span className="rh-meter__track" style={{ display: 'block', marginTop: 6, width: 160 }}>
        <span className={`rh-meter__fill rh-meter__fill--${tone}`} style={{ width: `${String(pct ?? 0)}%` }} />
      </span>
      <div style={{ fontSize: 'var(--rh-size-tag)', color: 'var(--rh-text-muted)', marginTop: 4, maxWidth: 200 }}>{explain}</div>
    </div>
  );
}

function EvidenceLine({ evidence }: { evidence: R3FixtureEvidence }): React.JSX.Element {
  return (
    <div style={{ borderTop: '1px solid var(--rh-rule)', padding: '4px 0', overflowWrap: 'anywhere' }}>
      <strong>{evidence.sourceAdapter}</strong>
      {' · '}
      {t('webAdmin.caseDetail.downloadedAt', { locale: 'cs', values: { date: czDateTime(evidence.fetchedAt) } })}
      {evidence.archive !== null && evidence.archive.archiveTimestamp !== null && (
        <>
          {t('webAdmin.caseDetail.archiveSnapshotSuffix', {
            locale: 'cs',
            values: { date: czDateTime(evidence.archive.archiveTimestamp) },
          })}
        </>
      )}
      {evidence.capabilityClass !== null && <> · {evidence.capabilityClass}</>}
      {evidence.url !== null && <div style={{ color: 'var(--rh-icon-muted)' }}>{evidence.url}</div>}
      {evidence.archive !== null && evidence.archive.originalUrl !== null && (
        <div style={{ color: 'var(--rh-icon-muted)' }}>
          {t('webAdmin.caseDetail.originallyPrefix', { locale: 'cs', values: { url: evidence.archive.originalUrl } })}
        </div>
      )}
    </div>
  );
}

function FactRow({
  fact,
  detail,
  nodeLabels,
}: {
  fact: R3FixtureFact;
  detail: CaseDetail;
  nodeLabels: ReadonlyMap<string, string>;
}): React.JSX.Element {
  const ref = factRef(fact.factKey);
  /**
   * Krok CZ1: v UI se nikdy netiskne surové `note:` ani `key: value` — popisky
   * jsou české (`humanValueEntries`) a poznámka je celá věta bez názvu pole.
   */
  const entries = humanValueEntries(fact.value);
  const note = factValueNote(fact);
  const sources = fact.independentSources;
  const firstEvidence = fact.evidenceIds[0] !== undefined ? detail.evidenceById.get(fact.evidenceIds[0]) : undefined;
  const dated = fact.sourceDate ?? firstEvidence?.fetchedAt ?? null;
  const impact = factImpactSeverity(fact);
  return (
    <div style={{ borderTop: '1px solid var(--rh-rule)', padding: '10px 0' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        {impact !== 'none' && <ImpactDot severity={impact} />}
        <strong style={{ fontSize: 'var(--rh-size-body)' }}>{humanFactCaption(fact.factKey)}</strong>
        {ref !== null && <span style={{ color: 'var(--rh-icon-muted)', fontSize: 'var(--rh-size-meta)' }}>{ref}</span>}
        {fact.nodeId !== null && ref === null && (
          <span style={{ color: 'var(--rh-icon-muted)', fontSize: 'var(--rh-size-meta)' }}>{nodeLabels.get(fact.nodeId)}</span>
        )}
      </div>
      <div style={{ fontSize: 'var(--rh-size-body)', margin: '2px 0 6px' }} data-testid={`fact-summary-${fact.id}`}>
        {factValueSummary(fact)}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <TrustBadge band={fact.trustBand} index={fact.trustIndex} />
        <span data-testid={`fact-status-${fact.id}`}>
          <StatusBadge status={fact.status} />
        </span>
        <Pill
          caption={
            sources === 1
              ? t('webAdmin.caseDetail.oneSource', { locale: 'cs' })
              : t('webAdmin.caseDetail.independentSources', { locale: 'cs', values: { count: String(sources) } })
          }
          bg="var(--rh-canvas)"
          fg="var(--rh-text-muted)"
        />
        <AiBadge types={fact.aiInvolvement} />
        {dated !== null && <span style={{ fontSize: 'var(--rh-size-tag)', color: 'var(--rh-icon-muted)' }}>{czDate(dated)}</span>}
      </div>
      <Detail
        summary={t('webAdmin.caseDetail.technicalDetail', { locale: 'cs', values: { count: String(fact.evidenceIds.length) } })}
      >
        {/* Krok D2: technický klíč faktu patří sem, ne do hlavního pohledu. */}
        <div style={{ overflowWrap: 'anywhere' }} data-testid={`fact-key-${fact.id}`}>
          <span style={{ color: 'var(--rh-icon-muted)' }}>
            {t('webAdmin.caseDetail.technicalKey', { locale: 'cs' })}:
          </span>{' '}
          <code>{fact.factKey}</code>
        </div>
        {fact.evidenceIds.map((id) => {
          const evidence = detail.evidenceById.get(id);
          return evidence !== undefined ? <EvidenceLine key={id} evidence={evidence} /> : null;
        })}
        {note !== null && (
          <div style={{ marginTop: 6, overflowWrap: 'anywhere' }} data-testid={`fact-note-${fact.id}`}>
            {note}
          </div>
        )}
        {entries.length > 0 && (
          <div style={{ marginTop: 6 }}>
            {entries.map(([k, v]) => (
              <div key={k} style={{ overflowWrap: 'anywhere' }}>
                <span style={{ color: 'var(--rh-icon-muted)' }}>{k}:</span> {v}
              </div>
            ))}
          </div>
        )}
      </Detail>
    </div>
  );
}

function FactList({
  facts,
  detail,
  nodeLabels,
}: {
  facts: readonly R3FixtureFact[];
  detail: CaseDetail;
  nodeLabels: ReadonlyMap<string, string>;
}): React.JSX.Element {
  return (
    <>
      {facts.map((fact) => (
        <FactRow key={fact.id} fact={fact} detail={detail} nodeLabels={nodeLabels} />
      ))}
    </>
  );
}

const IMPACT_BADGE: Readonly<Record<string, { caption: string; bg: string; fg: string }>> = {
  CRITICAL: { caption: t('webAdmin.caseDetail.impact.CRITICAL', { locale: 'cs' }), bg: 'var(--rh-danger-bg)', fg: 'var(--rh-danger)' },
  HIGH: { caption: t('webAdmin.caseDetail.impact.HIGH', { locale: 'cs' }), bg: 'var(--rh-warning-bg)', fg: 'var(--rh-warning)' },
  MEDIUM: { caption: t('webAdmin.caseDetail.impact.MEDIUM', { locale: 'cs' }), bg: 'var(--rh-warning-bg)', fg: 'var(--rh-warning)' },
  LOW: { caption: t('webAdmin.caseDetail.impact.LOW', { locale: 'cs' }), bg: 'var(--rh-rule)', fg: 'var(--rh-text-muted)' },
};

/** Karta otevrene otazky pro zalozku "Co nevime" (13-dukazy-otazky): blokuje / kdo muze uzavrit / co jsme zkusili / alternativa. */
function QuestionRow({ question, onAnswer }: { question: R3FixtureQuestion; onAnswer: (target: AnswerTarget) => void }): React.JSX.Element {
  const kind = stopKind(question);
  const tone =
    kind === 'waiting'
      ? { border: 'var(--rh-warning-line)', bg: 'var(--rh-warning-bg)', caption: t('webAdmin.caseDetail.toneWaiting', { locale: 'cs' }) }
      : { border: 'var(--rh-border)', bg: 'var(--rh-canvas)', caption: t('webAdmin.caseDetail.toneExhausted', { locale: 'cs' }) };
  const impact = question.impact !== null ? IMPACT_BADGE[question.impact] : undefined;
  return (
    <div style={{ border: `1px solid ${tone.border}`, background: tone.bg, borderRadius: 10, padding: 12, marginBottom: 10 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 'var(--rh-size-body)' }}>{brokerText(question.text)}</strong>
        {impact !== undefined && <Pill caption={impact.caption} bg={impact.bg} fg={impact.fg} />}
        <Pill caption={tone.caption} bg={kind === 'waiting' ? 'var(--rh-warning-bg)' : 'var(--rh-border)'} fg={kind === 'waiting' ? 'var(--rh-warning)' : 'var(--rh-text-muted)'} />
      </div>
      {question.whyItMatters !== null && (
        <div style={{ fontSize: 'var(--rh-size-footnote)', marginTop: 6 }}>
          <strong>{t('webAdmin.caseDetail.blocksLabel', { locale: 'cs' })}</strong>{' '}
          {brokerText(question.whyItMatters, { fallback: '—' })}
        </div>
      )}
      <div style={{ fontSize: 'var(--rh-size-footnote)', marginTop: 4 }}>
        <strong>{t('webAdmin.caseDetail.whoCanCloseLabel', { locale: 'cs' })}</strong>{' '}
        {captionOrPreparing(OWNER_CAPTIONS[question.owner], question.owner)}
      </div>
      {question.stopNote !== null && (
        <div style={{ fontSize: 'var(--rh-size-footnote)', marginTop: 4 }}>
          <strong>{t('webAdmin.caseDetail.triedSoFarLabel', { locale: 'cs' })}</strong>{' '}
          {brokerText(fixDiacritics(question.stopNote), { fallback: '—' })}
        </div>
      )}
      {question.closableBy !== null && (
        <div style={{ fontSize: 'var(--rh-size-footnote)', marginTop: 4 }}>
          <strong>{t('webAdmin.caseDetail.alternativePathLabel', { locale: 'cs' })}</strong>{' '}
          {brokerText(fixDiacritics(question.closableBy), { fallback: '—' })}
        </div>
      )}
      <button
        type="button"
        className="rh-btn mt-2"
        onClick={() => { onAnswer({ kind: 'question', question }); }}
        data-testid={`case-question-answer-${question.questionKey}`}
      >
        {ANSWER_QUESTION_TEXTS.buttonLabel}
      </button>
    </div>
  );
}

/** Radek „popisek: hodnota" pro karty v levem a pravem sloupci. */
function InfoRow({
  label,
  children,
  testId,
}: {
  label: string;
  children: React.ReactNode;
  testId?: string;
}): React.JSX.Element {
  return (
    <div
      className="flex items-baseline justify-between gap-2 py-1 border-b last:border-0 text-sm"
      style={{ borderColor: 'var(--rh-border)' }}
      {...(testId !== undefined ? { 'data-testid': testId } : {})}
    >
      <span style={{ color: 'var(--rh-text-muted)' }}>{label}</span>
      <span className="text-right font-medium" style={{ overflowWrap: 'anywhere' }}>{children}</span>
    </div>
  );
}

/** Popisek nad hodnotou — pro uzke sloupce, kde by se hodnota lamala. */
function StackedRow({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="py-1 border-b last:border-0 text-sm" style={{ borderColor: 'var(--rh-border)' }}>
      <div style={{ color: 'var(--rh-text-muted)' }}>{label}</div>
      <div className="font-medium">{children}</div>
    </div>
  );
}

// ---- zalozky ----------------------------------------------------------------

/*
 * Krok UX2: pas 6 dlazdice, sloupce „Co víme / situační plán / Co to mění“ a
 * karta „Další nejlepší kroky“ z této záložky ODESLY — nahradila je nová
 * `CockpitTab`, která odpovídá na tři otázky makléře (viz
 * `components/CockpitTab.tsx` a `lib/case-cockpit.ts`). Brány, pásma jistoty,
 * revize a všech 30 zjištění zůstávají beze změny v záložce AUDIT.
 */

/**
 * Vymery ze SDILENEHO resolveru (`areaReconciliationResult`) — stejny vysledek
 * pouziva PDF. Dokud neni zavrena brana rozsahu, zadne cislo se neoznaci za
 * potvrzenou vymeru predmetu prodeje (Milnik 1, bod 7).
 */
function AreaSummary({ detail }: { detail: CaseDetail }): React.JSX.Element {
  const area = areaReconciliationResult(detail);
  const conflict = area.reconciliationStatus === 'CONFLICT';
  return (
    <div
      style={{ borderTop: '1px solid var(--rh-border)', padding: '8px 0 0', marginTop: 4, fontSize: 'var(--rh-size-body)' }}
      data-testid="case-area-summary"
    >
      <div>
        {AREA_LABELS.knCandidate}: <strong>{m2(area.knCandidateTotalM2)}</strong>
      </div>
      {area.manualInputTotalM2 !== null && (
        <div>
          {AREA_LABELS.manualInput}: <strong>{m2(area.manualInputTotalM2)}</strong>
        </div>
      )}
      <div data-testid="case-area-confirmed">
        {AREA_LABELS.confirmed}:{' '}
        <strong>{area.confirmedSubjectTotalM2 === null ? AREA_LABELS.notConfirmed : m2(area.confirmedSubjectTotalM2)}</strong>
      </div>
      {area.differenceM2 !== null && area.differenceM2 > 0 && (
        <div data-testid="case-area-difference">
          {AREA_LABELS.difference}: <strong>{m2(area.differenceM2)}</strong>{' '}
          <Pill caption={t('webAdmin.caseDetail.areaSummary.conflictPill', { locale: 'cs' })} bg="var(--rh-warning-bg)" fg="var(--rh-warning)" />
        </div>
      )}
      {!conflict && area.reconciliationStatus === 'MATCH' && (
        <Pill caption={t('webAdmin.caseDetail.areaSummary.matchPill', { locale: 'cs' })} bg="var(--rh-success-bg)" fg="var(--rh-success)" />
      )}
    </div>
  );
}

/** Jeden ze ct yrich oddelenych stavu v hlavicce (Milnik 1, bod 1). */
function StatusItem({
  label,
  children,
  testId,
}: {
  label: string;
  children: React.ReactNode;
  testId?: string;
}): React.JSX.Element {
  return (
    <div {...(testId !== undefined ? { 'data-testid': testId } : {})}>
      <div style={{ fontSize: 'var(--rh-size-tag)', color: 'var(--rh-icon-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 'var(--rh-size-body)', fontWeight: 600 }}>{children}</div>
    </div>
  );
}

const KNOWN_LEGEND_STATUSES = ['VERIFIED', 'SUPPORTED', 'INFERRED', 'CONFLICT'] as const;

/** Zalozka "Co vime" (13-dukazy-otazky): fakta po domenach se stavem, zdroji, datem a dopadem. */
function CoVimeTab({ detail, nodeLabels }: { detail: CaseDetail; nodeLabels: ReadonlyMap<string, string> }): React.JSX.Element {
  const byDomain = factsByDomain(detail.facts);
  const domains = DOMAIN_ORDER.filter((d) => (byDomain.get(d)?.length ?? 0) > 0);
  if (domains.length === 0) {
    return <PreparingSection copy={CASE_TAB_PREPARING['co-vime']} compact />;
  }
  return (
    <div className="flex flex-col gap-4">
      {domains.map((domain) => (
        <Card key={domain} heading={DOMAIN_CAPTIONS[domain]} testId={`case-known-${domain}`}>
          <FactList facts={byDomain.get(domain) ?? []} detail={detail} nodeLabels={nodeLabels} />
        </Card>
      ))}
      {detail.hiddenFactCount > 0 && (
        <p style={{ color: 'var(--rh-icon-muted)', fontSize: 'var(--rh-size-meta)', margin: 0 }}>
          {t('webAdmin.caseDetail.hiddenFactsSuffix', { locale: 'cs', values: { count: String(detail.hiddenFactCount) } })}
        </p>
      )}
      <Card heading={t('webAdmin.caseDetail.legend.heading', { locale: 'cs' })} testId="case-known-legend">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 'var(--rh-size-meta)' }}>
          <div>
            <strong style={{ display: 'block', marginBottom: 4 }}>{t('webAdmin.caseDetail.legend.status', { locale: 'cs' })}</strong>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {KNOWN_LEGEND_STATUSES.map((s) => <StatusBadge key={s} status={s} />)}
            </div>
          </div>
          <div>
            <strong style={{ display: 'block', marginBottom: 4 }}>{t('webAdmin.caseDetail.legend.origin', { locale: 'cs' })}</strong>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              <AiBadge types={['AI_EXTRACTED']} />
              <AiBadge types={['DETERMINISTIC']} />
            </div>
          </div>
          <div>
            <strong style={{ display: 'block', marginBottom: 4 }}>{t('webAdmin.caseDetail.legend.impact', { locale: 'cs' })}</strong>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
              {(['critical', 'high', 'medium', 'low'] as const).map((s) => (
                <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <ImpactDot severity={s} /> {IMPACT_SEVERITY_CAPTIONS[s]}
                </span>
              ))}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

/** Zalozka "Co nevime" (13-dukazy-otazky): otevrene otazky + checklist podkladu + otazky k vyrizeni. */
function CoNevimeTab({
  detail,
  onAnswer,
}: {
  detail: CaseDetail;
  onAnswer: (target: AnswerTarget) => void;
}): React.JSX.Element {
  const { fixture } = detail;
  const openQuestions = openQuestionsSorted(detail);
  const answeredQuestions = fixture.questions.filter((q) => q.status === 'ANSWERED');
  const gateStateById = new Map(fixture.gates.map((g) => [g.gateId, g.state]));
  if (fixture.questions.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {/* Krok K0e: ukoly muzou existovat driv nez otazky (kolektor je zaklada). */}
        <HumanTasksPanel caseId={fixture.caseInfo.id} caseKind={fixture.caseInfo.kind} />
        {/*
          Krok QAFIX3 bod 1 (nalez N05): identita vlastnika je vstup CLOVEKA
          a patri tam, kde stoji ostatni („co nevíme"). `caseInfo.id` je
          `dd_cases.id`, ktery si UI dohledalo samo (`loadCaseDetail`).
        */}
        <OwnerIdentityCard ddCaseId={fixture.caseInfo.id} facts={fixture.facts} />
        <PreparingSection copy={CASE_TAB_PREPARING['co-nevime']} compact />
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      {/*
        Krok K0e: „co nevime" je presne misto, kam ukoly pro cloveka patri -
        vlastnik, omezeni, insolvence a exekuce jsou otazky, ktere dnes
        neodpovi zadny strojovy zdroj.
      */}
      <HumanTasksPanel caseId={fixture.caseInfo.id} caseKind={fixture.caseInfo.kind} />
      {/* Krok QAFIX3 bod 1 (nalez N05) — viz vetev bez otazek vyse. */}
      <OwnerIdentityCard ddCaseId={fixture.caseInfo.id} facts={fixture.facts} />
      <Card
        heading={t('webAdmin.caseDetail.coNevime.heading', { locale: 'cs' })}
        hint={t('webAdmin.caseDetail.coNevime.hint', { locale: 'cs' })}
        testId="case-unknown"
      >
        {openQuestions.length === 0 ? (
          <p style={{ color: 'var(--rh-text-muted)', margin: 0 }}>{t('webAdmin.caseDetail.coNevime.noOpenQuestion', { locale: 'cs' })}</p>
        ) : (
          openQuestions.map((q) => <QuestionRow key={q.id} question={q} onAnswer={onAnswer} />)
        )}
        {answeredQuestions.length > 0 && (
          <Detail
            summary={t('webAdmin.caseDetail.coNevime.answeredQuestionsSummary', {
              locale: 'cs',
              values: { count: String(answeredQuestions.length) },
            })}
          >
            {answeredQuestions.map((q) => (
              <div key={q.id} style={{ borderTop: '1px solid var(--rh-rule)', padding: '6px 0' }}>
                <strong>{brokerText(q.text)}</strong>
                {q.stopReason !== null && <> — {captionOrPreparing(STOP_REASON_CAPTIONS[q.stopReason], q.stopReason)}</>}
              </div>
            ))}
          </Detail>
        )}
      </Card>

      <Card
        heading={t('webAdmin.caseDetail.coNevime.checklistHeading', { locale: 'cs' })}
        hint={t('webAdmin.caseDetail.coNevime.checklistHint', { locale: 'cs' })}
        testId="case-checklist"
      >
        {fixture.checklist.length === 0 ? (
          <p style={{ color: 'var(--rh-text-muted)', margin: 0 }}>{t('webAdmin.caseDetail.coNevime.checklistEmpty', { locale: 'cs' })}</p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 'var(--rh-size-body)' }}>
            {fixture.checklist.map((item) => (
              <li key={`${item.kind}:${item.ref}`} style={{ marginBottom: 8 }}>
                {item.kind === 'gate' ? (
                  <>
                    <strong>{captionOrPreparing(GATE_CAPTIONS[item.ref], item.ref)}:</strong>{' '}
                    {item.needs.map((k) => captionOrPreparing(EVIDENCE_KIND_CAPTIONS[k], k)).join(t('webAdmin.caseDetail.coNevime.orSeparator', { locale: 'cs' }))}
                    {item.reviewRole !== null && (
                      <span style={{ color: 'var(--rh-text-muted)' }}>
                        {t('webAdmin.caseDetail.coNevime.checksSuffix', {
                          locale: 'cs',
                          values: { role: captionOrPreparing(REVIEW_ROLE_CAPTIONS[item.reviewRole], item.reviewRole) },
                        })}
                      </span>
                    )}
                    {gateStateById.get(item.ref) === 'CLOSED' ? (
                      <Pill caption={t('webAdmin.caseDetail.coNevime.gateClosedPill', { locale: 'cs' })} bg="var(--rh-success-bg)" fg="var(--rh-success)" />
                    ) : (
                      <button
                        type="button"
                        className="rh-btn ml-2"
                        onClick={() => { onAnswer({ kind: 'gate', gateId: item.ref, needs: item.needs }); }}
                        data-testid={`case-gate-answer-${item.ref}`}
                      >
                        {ANSWER_QUESTION_TEXTS.buttonLabel}
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <strong>{captionOrPreparing(undefined, item.ref)}:</strong>{' '}
                    {brokerText(item.note !== null ? fixDiacritics(item.note) : null, { fallback: '—' })}
                    {item.ownerHint !== null && (
                      <span style={{ color: 'var(--rh-text-muted)' }}>
                        {t('webAdmin.caseDetail.coNevime.providesSuffix', {
                          locale: 'cs',
                          values: { owner: captionOrPreparing(OWNER_CAPTIONS[item.ownerHint], item.ownerHint) },
                        })}
                      </span>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <QuestionsForSellerCard questions={openQuestions} />
    </div>
  );
}

/** Otazky k vyrizeni s kopirovanim a pripravou e-mailu (mailto) — nic se neodesila automaticky. */
function QuestionsForSellerCard({ questions }: { questions: readonly R3FixtureQuestion[] }): React.JSX.Element | null {
  const [answered, setAnswered] = React.useState<ReadonlySet<string>>(new Set());
  if (questions.length === 0) return null;
  const copy = (text: string): void => {
    void navigator.clipboard?.writeText(text);
  };
  const mailtoHref = (question: R3FixtureQuestion): string =>
    `mailto:?subject=${encodeURIComponent(
      t('webAdmin.caseDetail.questionsForSeller.mailSubject', { locale: 'cs', values: { questionKey: question.questionKey } }),
    )}&body=${encodeURIComponent(question.text)}`;
  return (
    <Card
      heading={t('webAdmin.caseDetail.questionsForSeller.heading', { locale: 'cs' })}
      hint={t('webAdmin.caseDetail.questionsForSeller.hint', { locale: 'cs' })}
      testId="case-questions-outbox"
    >
      {questions.map((q) => (
        <div key={q.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', borderTop: '1px solid var(--rh-rule)', padding: '8px 0' }}>
          <span style={{ flex: '1 1 260px', fontSize: 'var(--rh-size-footnote)', color: answered.has(q.id) ? 'var(--rh-icon-muted)' : undefined }}>
            {brokerText(q.text)}
          </span>
          <button type="button" className="rh-btn" onClick={() => { copy(q.text); }} data-testid={`case-question-copy-${q.questionKey}`}>
            {t('webAdmin.caseDetail.questionsForSeller.copy', { locale: 'cs' })}
          </button>
          <a className="rh-btn" href={mailtoHref(q)} data-testid={`case-question-mail-${q.questionKey}`}>
            {t('webAdmin.caseDetail.questionsForSeller.prepareEmail', { locale: 'cs' })}
          </a>
          <button
            type="button"
            className="rh-btn"
            onClick={() => { setAnswered((prev) => new Set(prev).add(q.id)); }}
            disabled={answered.has(q.id)}
            data-testid={`case-question-mark-${q.questionKey}`}
          >
            {answered.has(q.id)
              ? t('webAdmin.caseDetail.questionsForSeller.marked', { locale: 'cs' })
              : t('webAdmin.caseDetail.questionsForSeller.markAnswered', { locale: 'cs' })}
          </button>
        </div>
      ))}
    </Card>
  );
}

/** Zalozka „Konflikty": srovnani protichudnych zjisteni. V R3 fixture dnes 0 konfliktu — poctivy prazdny stav, ne vymyslena data. */
/**
 * Jedna hodnota ve sporu. Rozporny vstup BEZ evidence se nikdy nevykresli
 * jako potvrzeny fakt — nese vlastni stitek (Milnik 1, bod 6).
 */
function ConflictValue({
  fact,
  detail,
}: {
  fact: CaseDetail['facts'][number];
  detail: CaseDetail;
}): React.JSX.Element {
  const unverified = fact.conflict === 'UNVERIFIED_CONTRADICTORY_INPUT';
  const evidence = fact.evidenceIds
    .map((id) => detail.evidenceById.get(id))
    .filter((e): e is NonNullable<typeof e> => e !== undefined);
  return (
    <div
      style={{ border: '1px solid var(--rh-border)', borderRadius: 10, padding: 10, flex: '1 1 260px' }}
      data-testid={`conflict-value-${fact.id}`}
    >
      <div style={{ fontSize: 'var(--rh-size-subhead)', fontWeight: 700 }}>{factValueSummary(fact)}</div>
      <p style={{ fontSize: 'var(--rh-size-meta)', color: 'var(--rh-text-muted)', margin: '4px 0 0' }}>{humanFactCaption(fact.factKey)}</p>
      {unverified ? (
        <p style={{ fontSize: 'var(--rh-size-meta)', color: 'var(--rh-warning)', margin: '6px 0 0', fontWeight: 600 }} data-testid="conflict-unverified">
          {CLAIM_CONFLICT_CAPTIONS.UNVERIFIED_CONTRADICTORY_INPUT}
        </p>
      ) : (
        <p style={{ fontSize: 'var(--rh-size-meta)', color: 'var(--rh-icon-muted)', margin: '6px 0 0' }}>
          {evidence.length === 0
            ? NOT_DETERMINED
            : evidence.map((e) => e.sourceAdapter).join(', ')}
          {fact.sourceDate !== null && (
            <>{t('webAdmin.caseDetail.asOfDate', { locale: 'cs', values: { date: czDate(fact.sourceDate) } })}</>
          )}
        </p>
      )}
      {fact.conflict === 'NEEDS_REVALIDATION' && (
        <p style={{ fontSize: 'var(--rh-size-meta)', color: 'var(--rh-warning)', margin: '6px 0 0' }}>{CLAIM_CONFLICT_CAPTIONS.NEEDS_REVALIDATION}</p>
      )}
      <Detail summary={TECHNICAL_DETAIL_SUMMARY}>
        <p style={{ fontSize: 'var(--rh-size-meta)', color: 'var(--rh-icon-muted)', margin: 0, overflowWrap: 'anywhere' }}>
          {fact.factKey} · {fact.status} · {fact.id}
        </p>
      </Detail>
    </div>
  );
}

/**
 * Konflikty se ctou ze SKUPIN claimu (detail.conflictGroups), ne z jiz
 * deduplikovaneho seznamu faktu. Do 2. 9. 2026 tady stalo „zadny konflikt",
 * pritom Lysa nesla 2 078 i 2 048 m2 — dedupe je slucoval podle factKey a
 * nechal jen hodnotu s vyssim trust bandem (Milnik 1, bod 6).
 */
function KonfliktyTab({ detail }: { detail: CaseDetail }): React.JSX.Element {
  const groups = detail.conflictGroups;
  if (detail.facts.length === 0) {
    return <PreparingSection copy={CASE_TAB_PREPARING['konflikty']} compact />;
  }
  if (groups.length === 0) {
    return (
      <Card
        heading={t('webAdmin.caseDetail.conflicts.heading', { locale: 'cs' })}
        hint={t('webAdmin.caseDetail.conflicts.hint', { locale: 'cs' })}
        testId="case-conflicts-empty"
      >
        <p style={{ color: 'var(--rh-success)', margin: 0 }}>{t('webAdmin.caseDetail.conflicts.none', { locale: 'cs' })}</p>
      </Card>
    );
  }
  return (
    <div className="flex flex-col gap-4" data-testid="case-conflicts">
      {groups.map((group) => (
        <Card
          key={group.scopeKey}
          heading={humanFactCaption(group.factKey)}
          hint={t('webAdmin.caseDetail.conflicts.groupHint', { locale: 'cs', values: { count: String(group.distinctValues) } })}
          testId={`case-conflict-${group.factKey}`}
        >
          <div className="flex flex-wrap gap-3">
            {group.facts.map((fact) => (
              <ConflictValue key={fact.id} fact={fact} detail={detail} />
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}

/**
 * Krok G3: prepinac scenaru koridoru (AS_IS / AFTER_REMOVAL:<ref>) — jen kdyz
 * je scenaru vic nez jeden; s jednim scenarem zustava jen textovy souhrn
 * (beze zmeny oproti stavu pred G3).
 */
function CorridorScenarioPanel({ sitePlan }: { sitePlan: R3FixtureSitePlan }): React.JSX.Element | null {
  // `?? []` — starsi fixtures (pred krokem G3) pole `corridors` jeste nemaji.
  const corridors = sitePlan.geometry.corridors ?? [];
  const [selected, setSelected] = React.useState<string>(corridors[0]?.scenario ?? '');
  if (corridors.length === 0) return null;
  const active = corridors.find((c) => c.scenario === selected) ?? corridors[0];
  if (active === undefined) return null;
  return (
    <div style={{ marginTop: 10 }} data-testid="case-siteplan-corridor-panel">
      {corridors.length > 1 && (
        <div
          style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}
          data-testid="case-siteplan-scenario-switcher"
        >
          <span style={{ fontSize: 'var(--rh-size-meta)', color: 'var(--rh-text-muted)' }}>{SITEPLAN_SCENARIO_TEXTS.switcherLabel}:</span>
          {corridors.map((c) => (
            <button
              key={c.scenario}
              type="button"
              className="rh-btn"
              aria-pressed={c.scenario === active.scenario}
              onClick={() => { setSelected(c.scenario); }}
              data-testid={`case-siteplan-scenario-${c.scenario}`}
              style={c.scenario === active.scenario ? { fontWeight: 700 } : undefined}
            >
              {corridorScenarioCaption(c.scenario)}
            </button>
          ))}
        </div>
      )}
      <p style={{ fontSize: 'var(--rh-size-footnote)', color: 'var(--rh-text-muted)', margin: 0 }} data-testid="case-siteplan-scenario-stats">
        {t('webAdmin.caseDetail.corridorScenarioLine', {
          locale: 'cs',
          values: {
            caption: corridorScenarioCaption(active.scenario),
            width: String(active.widthM),
            max: String(active.maxWidthM),
            status: active.passable ? SITEPLAN_SCENARIO_TEXTS.passable : SITEPLAN_SCENARIO_TEXTS.notPassable,
          },
        })}
      </p>
    </div>
  );
}

/** Krok G3: radek pod planem se zdroji budov a verzi planu (geometryVersion · generatedAt). */
function SitePlanVersionRow({ sitePlan }: { sitePlan: R3FixtureSitePlan }): React.JSX.Element | null {
  // Starsi fixtures (pred krokem G3) `version`/`buildings` jeste nemaji — radek se proste nezobrazi.
  if (sitePlan.version === undefined) return null;
  const sources = [...new Set((sitePlan.geometry.buildings ?? []).map((b) => b.source))];
  return (
    <p style={{ fontSize: 'var(--rh-size-tag)', color: 'var(--rh-icon-muted)', margin: '8px 0 0' }} data-testid="case-siteplan-version">
      {sitePlanVersionLine(sitePlan.version, sources)}
    </p>
  );
}

/**
 * Degradovany plan (krok O5a): rekne, CO v planu chybi a KTERA zjisteni by to
 * doplnila — `degraded.missing`/`missingFacts` z `/detail`. Prazdna mapa bez
 * vysvetleni vypadala jako chyba aplikace, i kdyz slo o poctivy stav dat.
 */
function SitePlanDegraded({ sitePlan }: { sitePlan: R3FixtureSitePlan }): React.JSX.Element | null {
  const degraded = sitePlan.degraded ?? null;
  if (degraded === null || degraded.missing.length === 0) return null;
  return (
    <div style={{ fontSize: 'var(--rh-size-footnote)', color: 'var(--rh-text-muted)', margin: '8px 0 0' }} data-testid="case-siteplan-degraded">
      {degraded.missing.includes('ROAD') && <p style={{ margin: 0 }}>{SITEPLAN_NO_ROAD_NOTE}</p>}
      <p style={{ margin: '4px 0 0' }}>
        {SITEPLAN_DEGRADED_TEXTS.heading}{' '}
        {degraded.missing.map((part) => captionOrPreparing(SITEPLAN_MISSING_CAPTIONS[part], part)).join(', ')}
      </p>
      {degraded.missingFacts.length > 0 && (
        <p style={{ margin: '4px 0 0' }}>
          {SITEPLAN_DEGRADED_TEXTS.missingFactsLabel}{' '}
          {degraded.missingFacts.map((factKey) => humanFactCaption(factKey)).join(', ')}
        </p>
      )}
    </div>
  );
}

type BrokerClaimStatus =
  | { readonly kind: 'idle' }
  | { readonly kind: 'saving' }
  | { readonly kind: 'done'; readonly questionCount: number }
  | { readonly kind: 'error'; readonly message: string };

/**
 * Tvrzeni maklere u situacniho planu (krok G4): oznaceni budovy k demolici/
 * ponechani + strana koridoru. Zapis jde primo na `POST /claims` (stejny
 * vzor jako `StartAuditModal`/`audit-api.ts`) - plan sam je staticky export,
 * takze vysledek se zobrazi jako hlaseni pod tlacitky, ne zmenou vykresu.
 */
function BrokerClaimsPanel({
  caseId,
  sitePlan,
}: {
  readonly caseId: string;
  readonly sitePlan: R3FixtureSitePlan;
}): React.JSX.Element | null {
  const [status, setStatus] = React.useState<BrokerClaimStatus>({ kind: 'idle' });
  const buildings = sitePlan.geometry.buildings ?? [];
  const hasCorridorScenarios = (sitePlan.geometry.corridors ?? []).length > 0;
  if (buildings.length === 0 && !hasCorridorScenarios) return null;
  const submit = (input: BrokerClaimInput): void => {
    setStatus({ kind: 'saving' });
    recordBrokerClaim(caseId, input)
      .then((result) => { setStatus({ kind: 'done', questionCount: result.questions.length }); })
      .catch((error: unknown) => {
        setStatus({ kind: 'error', message: error instanceof Error ? error.message : String(error) });
      });
  };

  return (
    <div style={{ marginTop: 12, borderTop: '1px solid var(--rh-rule)', paddingTop: 10 }} data-testid="case-broker-claims">
      <h4 style={{ margin: '0 0 6px', fontSize: 'var(--rh-size-footnote)' }}>{BROKER_CLAIM_TEXTS.heading}</h4>
      <p style={{ fontSize: 'var(--rh-size-meta)', color: 'var(--rh-text-muted)', margin: '0 0 8px' }}>{BROKER_CLAIM_TEXTS.hint}</p>
      {buildings.map((building) => (
        <div key={building.matchRef} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 'var(--rh-size-footnote)' }}>{building.ref}</span>
          {building.status === 'PLANNED_DEMOLITION' ? (
            <Pill caption={BROKER_CLAIM_TEXTS.buildingIntentDemolished} bg="var(--rh-danger-bg)" fg="var(--rh-danger)" />
          ) : (
            <button
              type="button"
              className="rh-btn"
              disabled={status.kind === 'saving'}
              onClick={() => { submit({ kind: 'BUILDING_INTENT', buildingRef: building.matchRef, intent: 'DEMOLISH' }); }}
              data-testid={`case-broker-claim-demolish-${building.matchRef}`}
            >
              {BROKER_CLAIM_TEXTS.buildingDemolishButton}
            </button>
          )}
          <button
            type="button"
            className="rh-btn"
            disabled={status.kind === 'saving'}
            onClick={() => { submit({ kind: 'BUILDING_INTENT', buildingRef: building.matchRef, intent: 'KEEP' }); }}
            data-testid={`case-broker-claim-keep-${building.matchRef}`}
          >
            {BROKER_CLAIM_TEXTS.buildingKeepButton}
          </button>
        </div>
      ))}
      {hasCorridorScenarios && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
          <span style={{ fontSize: 'var(--rh-size-footnote)' }}>{BROKER_CLAIM_TEXTS.corridorSideLabel}</span>
          <button
            type="button"
            className="rh-btn"
            disabled={status.kind === 'saving'}
            onClick={() => { submit({ kind: 'CORRIDOR_SIDE', side: 'WEST' }); }}
            data-testid="case-broker-claim-corridor-west"
          >
            {BROKER_CLAIM_TEXTS.corridorSideWest}
          </button>
          <button
            type="button"
            className="rh-btn"
            disabled={status.kind === 'saving'}
            onClick={() => { submit({ kind: 'CORRIDOR_SIDE', side: 'EAST' }); }}
            data-testid="case-broker-claim-corridor-east"
          >
            {BROKER_CLAIM_TEXTS.corridorSideEast}
          </button>
        </div>
      )}
      {status.kind === 'saving' && <p style={{ fontSize: 'var(--rh-size-meta)', color: 'var(--rh-text-muted)', margin: '6px 0 0' }}>{BROKER_CLAIM_TEXTS.saving}</p>}
      {status.kind === 'done' && (
        <p style={{ fontSize: 'var(--rh-size-meta)', color: 'var(--rh-success)', margin: '6px 0 0' }} data-testid="case-broker-claim-done">
          {BROKER_CLAIM_TEXTS.savedNote(status.questionCount)}
        </p>
      )}
      {status.kind === 'error' && (
        <p style={{ fontSize: 'var(--rh-size-meta)', color: 'var(--rh-danger)', margin: '6px 0 0' }} data-testid="case-broker-claim-error">
          {BROKER_CLAIM_TEXTS.errorPrefix}{status.message}
        </p>
      )}
    </div>
  );
}

/** Krok M1 — mapa + panel vedle sebe; drzi vymeru z geometrie pro "Shoda" radek panelu. */
function ParcelMapSection({
  caseId,
  parcelId,
  facts,
  offerGeometry,
  onParcelPicked,
}: {
  readonly caseId: string;
  readonly parcelId: string | null;
  readonly facts: CaseDetail['fixture']['facts'];
  /** Krok L1: parcely sdruzene nabidky vybarvene podle stavu. */
  readonly offerGeometry?: OfferGeometryDto | null | undefined;
  readonly onParcelPicked?:
    | ((parcel: { readonly label: string; readonly kuCode: number | null }) => void)
    | undefined;
}): React.JSX.Element {
  const [geometryAreaM2, setGeometryAreaM2] = React.useState<number | null>(null);
  return (
    <div className="rh-wizard-grid" data-testid="case-parcel-map-section">
      <ParcelMap
        caseId={caseId}
        parcelId={parcelId}
        onGeometryLoaded={setGeometryAreaM2}
        offerGeometry={offerGeometry as never}
        onParcelPicked={onParcelPicked}
      />
      <ParcelPanel caseId={caseId} facts={facts} geometryAreaM2={geometryAreaM2} />
    </div>
  );
}

/**
 * Krok L1 — MAPA A PANEL NABIDKY DRZI JEDEN STAV.
 *
 * Mapa musi vybarvit prave ten vyber, ktery panel ukazuje, a klik do mapy musi
 * dojit do panelu. Spolecny stav proto zije TADY (nejnizsi spolecny rodic), ne
 * v globalnim store: je to stav jedne obrazovky, ne aplikace.
 */
function OfferGeometryBridge({
  caseId,
  parcelId,
  facts,
}: {
  readonly caseId: string;
  readonly parcelId: string | null;
  readonly facts: CaseDetail['fixture']['facts'];
}): React.JSX.Element {
  const [offerGeometry, setOfferGeometry] = React.useState<OfferGeometryDto | null>(null);
  const [picked, setPicked] = React.useState<{ label: string; kuCode: number | null } | null>(null);
  const [token, setToken] = React.useState(0);

  React.useEffect(() => {
    let alive = true;
    // Vybarveni je kosmetika mapy - kdyz volani selze, mapa vyber neukaze;
    // panel nabidky si svou chybu hlasi sam.
    void fetchOfferGeometry(caseId)
      .then((geometry) => {
        if (alive) setOfferGeometry(geometry);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [caseId, token]);

  return (
    <>
      <ParcelMapSection
        caseId={caseId}
        parcelId={parcelId}
        facts={facts}
        offerGeometry={offerGeometry}
        onParcelPicked={setPicked}
      />
      {/*
        Sdruzena nabidka je zamerne V SEKCI „Geometrie", ne jako nova zalozka:
        vyber parcel je prace nad TOUZ mapou a tymiz vymerami, ktere uz sekce
        ukazuje, a nova zalozka by znamenala zasah do routeru a jeho testu.
      */}
      <OfferPanel
        caseId={caseId}
        picked={picked}
        onChanged={() => {
          setToken((value) => value + 1);
        }}
      />
    </>
  );
}

function GeometryTab({ detail, nodeLabels }: { detail: CaseDetail; nodeLabels: ReadonlyMap<string, string> }): React.JSX.Element {
  const { fixture } = detail;
  const rows = areaRows(detail);
  const facts = tabFacts(detail, 'geometrie');
  const sitePlan = fixture.sitePlan;
  if (rows.length === 0 && facts.length === 0 && sitePlan === null) {
    return <PreparingSection copy={CASE_TAB_PREPARING['geometrie']} compact />;
  }
  const identityFact = fixture.facts.find((f) => f.factKey.startsWith('parcel.identity#'));
  const identityValue = identityFact?.value as { readonly label?: string } | undefined;
  return (
    <div className="flex flex-col gap-4">
      <OfferGeometryBridge caseId={fixture.caseInfo.id} parcelId={identityValue?.label ?? null} facts={fixture.facts} />
      <Card
        heading={t('webAdmin.caseDetail.geometry.areasHeading', { locale: 'cs' })}
        hint={t('webAdmin.caseDetail.geometry.areasHint', { locale: 'cs' })}
        testId="case-areas"
      >
        {rows.length === 0 ? (
          <p style={{ color: 'var(--rh-text-muted)', margin: 0 }}>
            {t('webAdmin.caseDetail.geometry.areasNotAvailable', { locale: 'cs', values: { value: NOT_DETERMINED } })}
          </p>
        ) : (
          <>
            {rows.map((row) => (
              <div key={row.parcelLabel} style={{ borderTop: '1px solid var(--rh-rule)', padding: '8px 0', display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 'var(--rh-size-body)', minWidth: 90 }}>{row.parcelLabel}</strong>
                <span style={{ fontSize: 'var(--rh-size-body)' }}>{m2(row.areaM2)}</span>
                <TrustBadge band={row.trustBand} />
              </div>
            ))}
            <AreaSummary detail={detail} />
          </>
        )}
      </Card>

      <Card
        heading={t('webAdmin.caseDetail.geometry.sitePlanHeading', { locale: 'cs' })}
        hint={t('webAdmin.caseDetail.geometry.sitePlanHint', { locale: 'cs' })}
        testId="case-siteplan"
      >
        {sitePlan === null ? (
          <p style={{ color: 'var(--rh-text-muted)', margin: 0 }}>{t('webAdmin.caseDetail.geometry.noSitePlan', { locale: 'cs' })}</p>
        ) : (
          <>
            <img
              src={svgDataUri(sitePlan.svg)}
              alt={t('webAdmin.caseDetail.geometry.alt', {
                locale: 'cs',
                values: {
                  address: fixture.caseInfo.addressQuery,
                  street:
                    sitePlan.geometry.road !== null
                      ? t('webAdmin.caseDetail.geometry.altStreetSuffix', { locale: 'cs', values: { street: sitePlan.geometry.road.streetName } })
                      : '',
                },
              })}
              style={{ maxWidth: '100%', height: 'auto', display: 'block', border: '1px solid var(--rh-border)', borderRadius: 10 }}
            />
            {/*
              VYJIMKA z pravidla „barva jen pres tokeny" (krok UXV4a): ctverecky
              legendy musi mit PRESNE ty barvy, jakymi je nakreslen situacni
              plan (obrazek ze serveru, `@reos/reports`). Tokenem by se v tmavem
              rezimu zmenily a legenda by prestala sedet s obrazkem — a ten se
              podle predlohy v4 nebarvi („Fotky, mapy a PDF reporty se v tmavem
              rezimu nebarvi").
            */}
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 'var(--rh-size-meta)', color: 'var(--rh-text-muted)', marginTop: 10 }}>
              <span>
                <span style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid #333', verticalAlign: 'middle', marginRight: 4 }} />
                {t('webAdmin.caseDetail.geometry.legend.subjectParcels', { locale: 'cs' })}
              </span>
              {sitePlan.geometry.road !== null && (
                <span>
                  <span style={{ display: 'inline-block', width: 12, height: 12, background: '#e6e6e6', border: '1px solid #8c8c8c', verticalAlign: 'middle', marginRight: 4 }} />
                  {t('webAdmin.caseDetail.geometry.legend.road', { locale: 'cs', values: { street: sitePlan.geometry.road.streetName } })}
                </span>
              )}
              {sitePlan.geometry.building !== null && (
                <span>
                  <span style={{ display: 'inline-block', width: 12, height: 12, background: '#bfbfbf', verticalAlign: 'middle', marginRight: 4 }} />
                  {t('webAdmin.caseDetail.geometry.legend.building', { locale: 'cs' })}
                </span>
              )}
              {sitePlan.geometry.corridor !== null && (
                <span>
                  <span style={{ display: 'inline-block', width: 12, height: 12, background: '#7da7c9', opacity: 0.6, verticalAlign: 'middle', marginRight: 4 }} />
                  {t('webAdmin.caseDetail.geometry.legend.corridor', { locale: 'cs' })}
                </span>
              )}
              <span>{t('webAdmin.caseDetail.geometry.legend.compass', { locale: 'cs' })}</span>
            </div>
            <SitePlanDegraded sitePlan={sitePlan} />
            {(sitePlan.geometry.corridors ?? []).length > 0 ? (
              <CorridorScenarioPanel sitePlan={sitePlan} />
            ) : (
              sitePlan.corridorStats !== null && (
                <p style={{ fontSize: 'var(--rh-size-footnote)', color: 'var(--rh-text-muted)', margin: '8px 0 0' }}>
                  {t('webAdmin.caseDetail.geometry.corridorStatsLine', {
                    locale: 'cs',
                    values: {
                      width: String(sitePlan.corridorStats.widthM),
                      max: String(sitePlan.corridorStats.maxWidthM),
                      area: String(sitePlan.corridorStats.areaM2),
                      passable: sitePlan.corridorStats.passable
                        ? t('webAdmin.caseDetail.geometry.passable', { locale: 'cs' })
                        : t('webAdmin.caseDetail.geometry.notPassable', { locale: 'cs' }),
                    },
                  })}
                </p>
              )
            )}
            <SitePlanVersionRow sitePlan={sitePlan} />
            <BrokerClaimsPanel caseId={fixture.caseInfo.id} sitePlan={sitePlan} />
          </>
        )}
      </Card>

      {facts.length > 0 && (
        <Card heading={t('webAdmin.caseDetail.geometry.factsHeading', { locale: 'cs' })} testId="case-geometry-facts">
          <FactList facts={facts} detail={detail} nodeLabels={nodeLabels} />
        </Card>
      )}
    </div>
  );
}

function AccessTab({ detail, nodeLabels }: { detail: CaseDetail; nodeLabels: ReadonlyMap<string, string> }): React.JSX.Element {
  const facts = tabFacts(detail, 'pristup');
  const summary = accessSummary(detail);
  if (facts.length === 0) {
    return <PreparingSection copy={CASE_TAB_PREPARING['pristup']} compact />;
  }
  return (
    <div className="flex flex-col gap-4">
      <Card heading={t('webAdmin.caseDetail.access.summaryHeading', { locale: 'cs' })} testId="case-access-summary">
        <InfoRow label={t('webAdmin.caseDetail.access.streetFrontage', { locale: 'cs' })}>
          {summary.frontageM !== null ? `${String(summary.frontageM)} m` : NOT_DETERMINED}
        </InfoRow>
        <InfoRow label={t('webAdmin.caseDetail.access.maxCorridorWidth', { locale: 'cs' })}>
          {summary.corridorMaxWidthM !== null ? `${String(summary.corridorMaxWidthM)} m` : NOT_DETERMINED}
        </InfoRow>
        <InfoRow label={t('webAdmin.caseDetail.access.corridorInSitePlan', { locale: 'cs' })}>
          {summary.corridorWidthM !== null
            ? `${String(summary.corridorWidthM)} m${
                summary.corridorPassable === true
                  ? t('webAdmin.caseDetail.access.passableSuffix', { locale: 'cs' })
                  : summary.corridorPassable === false
                    ? t('webAdmin.caseDetail.access.notPassableSuffix', { locale: 'cs' })
                    : ''
              }`
            : NOT_DETERMINED}
        </InfoRow>
        <InfoRow label={t('webAdmin.caseDetail.access.legalAccess', { locale: 'cs' })}>
          {t(LEGAL_ACCESS_TAB_KEY[summary.legalAccess], { locale: 'cs' })}
        </InfoRow>
      </Card>
      <Card heading={t('webAdmin.caseDetail.access.factsHeading', { locale: 'cs' })} testId="case-access-facts">
        <FactList facts={facts} detail={detail} nodeLabels={nodeLabels} />
      </Card>
    </div>
  );
}

/** Humanizovana ciselnikova hodnota pripojky — hlavni text bez kodu, kod jen v title (AUDIT-OBRAZOVKY bod 3). */
function ConnectionValue({ raw }: { raw: string | null }): React.JSX.Element {
  if (raw === null) return <>{NOT_DETERMINED}</>;
  const humanized = humanizeConnectionValue(raw);
  const title =
    humanized.code !== null
      ? t('webAdmin.caseDetail.connectionValue.title', {
          locale: 'cs',
          values: {
            code: humanized.code,
            codelistSuffix:
              humanized.codelist !== null
                ? t('webAdmin.caseDetail.connectionValue.codelistSuffix', { locale: 'cs', values: { codelist: humanized.codelist } })
                : '',
          },
        })
      : undefined;
  return <span title={title}>{humanized.text}</span>;
}

function UtilitiesTab({ detail, nodeLabels }: { detail: CaseDetail; nodeLabels: ReadonlyMap<string, string> }): React.JSX.Element {
  const facts = tabFacts(detail, 'site');
  const summary = utilitiesSummary(detail);
  if (facts.length === 0) {
    return <PreparingSection copy={CASE_TAB_PREPARING['site']} compact />;
  }
  return (
    <div className="flex flex-col gap-4">
      {summary.sewerCapacity === 'OPEN' && (
        <div style={{ border: '1px solid var(--rh-warning-line)', background: 'var(--rh-warning-bg)', borderRadius: 10, padding: 12, fontSize: 'var(--rh-size-footnote)' }} data-testid="case-sewer-warning">
          <strong>{t('webAdmin.caseDetail.utilities.sewerWarningHeading', { locale: 'cs' })}</strong>{' '}
          {t('webAdmin.caseDetail.utilities.sewerWarningBody', { locale: 'cs', values: { gate: GATE_CAPTIONS['GATE_SEWER_CAPACITY'] ?? '' } })}
        </div>
      )}
      <Card heading={t('webAdmin.caseDetail.utilities.connectionsHeading', { locale: 'cs' })} testId="case-utilities-summary">
        <InfoRow label={t('webAdmin.caseDetail.utilities.water', { locale: 'cs' })}><ConnectionValue raw={summary.water} /></InfoRow>
        <InfoRow label={t('webAdmin.caseDetail.utilities.sewer', { locale: 'cs' })}><ConnectionValue raw={summary.sewer} /></InfoRow>
        <InfoRow label={t('webAdmin.caseDetail.utilities.gas', { locale: 'cs' })}><ConnectionValue raw={summary.gas} /></InfoRow>
      </Card>
      <Card heading={t('webAdmin.caseDetail.utilities.factsHeading', { locale: 'cs' })} testId="case-utilities-facts">
        <FactList facts={facts} detail={detail} nodeLabels={nodeLabels} />
      </Card>
    </div>
  );
}

function PlanningTab({ detail, nodeLabels }: { detail: CaseDetail; nodeLabels: ReadonlyMap<string, string> }): React.JSX.Element {
  const facts = tabFacts(detail, 'uzemni-plan');
  if (facts.length === 0) {
    return <PreparingSection copy={CASE_TAB_PREPARING['uzemni-plan']} compact />;
  }
  return (
    <Card
      heading={t('webAdmin.caseDetail.planning.heading', { locale: 'cs' })}
      hint={t('webAdmin.caseDetail.planning.hint', { locale: 'cs' })}
      testId="case-planning-facts"
    >
      <FactList facts={facts} detail={detail} nodeLabels={nodeLabels} />
    </Card>
  );
}

function RisksTab({ detail, nodeLabels }: { detail: CaseDetail; nodeLabels: ReadonlyMap<string, string> }): React.JSX.Element {
  const facts = tabFacts(detail, 'rizika');
  const affects = detail.fixture.relations.filter((r) => r.relationType === 'AFFECTS');
  if (facts.length === 0) {
    return <PreparingSection copy={CASE_TAB_PREPARING['rizika']} compact />;
  }
  return (
    <div className="flex flex-col gap-4">
      <Card
        heading={t('webAdmin.caseDetail.risks.heading', { locale: 'cs' })}
        hint={t('webAdmin.caseDetail.risks.hint', { locale: 'cs' })}
        testId="case-risk-facts"
      >
        <FactList facts={facts} detail={detail} nodeLabels={nodeLabels} />
        {affects.length > 0 && (
          <div style={{ fontSize: 'var(--rh-size-footnote)', color: 'var(--rh-text-muted)', marginTop: 10 }}>
            {affects.map((rel) => (
              <div key={rel.id}>
                <strong>{nodeLabels.get(rel.fromNodeId)}</strong>
                {t('webAdmin.caseDetail.risks.affectsSuffix', { locale: 'cs' })}
                {typeof rel.properties['mechanism'] === 'string' && <> — {rel.properties['mechanism']}</>}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

/** Zalozka "Oceneni" (12-ocenovani) - jen s daty, ktera mame: zadne CAPEX/GDV/pocty domu/mapa comparables. */
/**
 * Krok UI2 — "Cena v okolí": srovnatelne nabidky akvizicniho radaru. Vysvetleni
 * (C1) se pocita jen na kliknuti "Vysvětlit" (zadani, cena tokenu jen za uzitek).
 */
function PriceRangeCard({ caseId }: { caseId: string }): React.JSX.Element | null {
  const [data, setData] = React.useState<CasePriceRangeDto | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [explanation, setExplanation] = React.useState<string | null>(null);
  const [explaining, setExplaining] = React.useState(false);

  React.useEffect(() => {
    let active = true;
    getCasePriceRange(caseId)
      .then((result) => {
        if (active) setData(result);
      })
      .catch(() => {
        if (active) setError(t('webAdmin.caseDetail.priceRange.loadError', { locale: 'cs' }));
      });
    return () => {
      active = false;
    };
  }, [caseId]);

  async function explain(): Promise<void> {
    setExplaining(true);
    try {
      const result = await explainCasePriceRange(caseId);
      setExplanation(result.explanation);
    } finally {
      setExplaining(false);
    }
  }

  if (error !== null) {
    return (
      <Card heading={t('webAdmin.caseDetail.priceRange.heading', { locale: 'cs' })} testId="case-price-range-error">
        <p role="alert" style={{ color: 'var(--rh-danger)', fontSize: 'var(--rh-size-footnote)', margin: 0 }}>
          {error}
        </p>
      </Card>
    );
  }
  if (data === null) return null;

  return (
    <Card
      heading={t('webAdmin.caseDetail.priceRange.heading', { locale: 'cs' })}
      hint={t('webAdmin.caseDetail.priceRange.hint', { locale: 'cs' })}
      testId="case-price-range"
    >
      {data.status === 'insufficient' ? (
        <p style={{ fontSize: 'var(--rh-size-footnote)', color: 'var(--rh-text-muted)', margin: 0 }} data-testid="case-price-range-insufficient">
          {data.message}
        </p>
      ) : (
        <>
          <p style={{ fontSize: 'var(--rh-size-title2)', fontWeight: 700, margin: '0 0 4px' }}>
            {formatMoney(data.band!.low)} – {formatMoney(data.band!.high)}
          </p>
          <p style={{ fontSize: 'var(--rh-size-footnote)', color: 'var(--rh-text-muted)', margin: '0 0 10px' }}>
            {t('webAdmin.caseDetail.priceRange.count', { locale: 'cs', values: { count: data.count } })}
            {data.median !== null ? ` · ${t('webAdmin.caseDetail.priceRange.medianLabel', { locale: 'cs' })}: ${formatMoney(data.median)}` : ''}
          </p>
          {data.nearest.length > 0 && (
            <>
              <strong style={{ fontSize: 'var(--rh-size-footnote)' }}>
                {t('webAdmin.caseDetail.priceRange.nearestHeading', { locale: 'cs' })}
              </strong>
              <ul style={{ margin: '6px 0 10px', paddingLeft: 18, fontSize: 'var(--rh-size-footnote)' }}>
                {data.nearest.map((item, index) => (
                  <li key={`${item.label}-${String(index)}`}>
                    {item.label} · {formatMoney(item.price)}
                    {item.url !== null && (
                      <>
                        {' '}
                        <a href={item.url} target="_blank" rel="noreferrer">
                          {t('webAdmin.caseDetail.priceRange.openListing', { locale: 'cs' })}
                        </a>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
          {explanation === null ? (
            <button type="button" className="rh-btn" onClick={() => void explain()} disabled={explaining} data-testid="case-price-range-explain">
              {explaining
                ? t('webAdmin.caseDetail.priceRange.explaining', { locale: 'cs' })
                : t('webAdmin.caseDetail.priceRange.explainButton', { locale: 'cs' })}
            </button>
          ) : (
            <p style={{ fontSize: 'var(--rh-size-footnote)', margin: '6px 0 0' }} data-testid="case-price-range-explanation">
              {explanation}
            </p>
          )}
        </>
      )}
      {/*
        Krok CENY2 bod 5 — CENOVA CAST STOJI I PRI `insufficient`.
        Nabidek z radaru muze byt malo (tehdy `insufficient`), ale realizovane
        ceny z katastru, prumer CSU nebo odhad porad odpoved jsou. Oddil je
        proto MIMO vetveni podle stavu nabidek; prazdny nikdy neni — bez
        radku se tiskne veta `marketEmpty`.
      */}
      <div style={{ marginTop: 12 }} data-testid="case-price-market">
        <strong style={{ fontSize: 'var(--rh-size-footnote)' }}>
          {t('webAdmin.caseDetail.priceRange.marketHeading', { locale: 'cs' })}
        </strong>
        {/*
          `?? []` zamerne: odpoved prichazi ze SITE a pole `market` pridal az
          krok CENY2. Pri nasazeni se obrazovka a server nemeni ve stejne
          sekunde, takze klient musi snest i odpoved bez nej — tehdy ukaze
          tutez vetu jako pri prazdnem vyctu, ne bilou kartu.
        */}
        {(data.market ?? []).length === 0 ? (
          <p
            style={{ fontSize: 'var(--rh-size-footnote)', color: 'var(--rh-text-muted)', margin: '6px 0 0' }}
            data-testid="case-price-market-empty"
          >
            {data.marketEmpty ?? ''}
          </p>
        ) : (
          <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 'var(--rh-size-footnote)' }}>
            {(data.market ?? []).map((item) => (
              <li key={item.key} data-testid={`case-price-market-${item.key}`}>
                {item.label}: <strong>{item.value}</strong>
                {item.note === null ? null : (
                  <>
                    <br />
                    <span style={{ color: 'var(--rh-text-muted)' }}>{item.note}</span>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      <p style={{ fontSize: 'var(--rh-size-meta)', color: 'var(--rh-text-muted)', margin: '10px 0 0' }}>{data.disclaimer}</p>
    </Card>
  );
}

function OceneniTab({ detail, nodeLabels, caseId }: { detail: CaseDetail; nodeLabels: ReadonlyMap<string, string>; caseId: string }): React.JSX.Element {
  const { fixture } = detail;
  const band = fixture.valuationBand;
  const facts = tabFacts(detail, 'analyzy');
  const narrowing = narrowingFactorsSorted(detail);
  const scenarios = valuationScenarios(detail);
  // Krok UI2: "Cena v okolí" nezávisí na due diligence auditu zakázky (jiný
  // zdroj dat, akvizicni radar) - zobrazuje se i kdyz DD ocenění jeste "chystáme".
  if (facts.length === 0 && band.low === null && band.blockedBy.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <PriceRangeCard caseId={caseId} />
        <PreparingSection copy={CASE_TAB_PREPARING['oceneni']} compact />
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <PriceRangeCard caseId={caseId} />
      {band.low === null ? (
        <Card heading={t('webAdmin.caseDetail.valuation.blockedHeading', { locale: 'cs' })} testId="case-valuation-blocked">
          <p style={{ margin: '0 0 8px', fontSize: 'var(--rh-size-footnote)', color: 'var(--rh-warning)' }}>
            {t('webAdmin.caseDetail.valuation.blockedIntro', { locale: 'cs' })}
          </p>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 'var(--rh-size-footnote)' }}>
            {band.blockedBy.map((reason) => <li key={reason}>{fixDiacritics(reason)}</li>)}
          </ul>
        </Card>
      ) : (
        <Card heading={t('webAdmin.caseDetail.valuation.bandHeading', { locale: 'cs' })} testId="case-valuation-band">
          <p style={{ fontSize: 'var(--rh-size-title2)', fontWeight: 700, margin: '0 0 6px' }}>
            {formatMoney(band.low)} – {formatMoney(band.high)}
          </p>
        </Card>
      )}

      {narrowing.length > 0 && (
        <Card
          heading={t('webAdmin.caseDetail.valuation.narrowingHeading', { locale: 'cs' })}
          hint={t('webAdmin.caseDetail.valuation.narrowingHint', { locale: 'cs' })}
          testId="case-valuation-narrowing"
        >
          {narrowing.map((w) => (
            <div key={w.gateId} style={{ borderTop: '1px solid var(--rh-rule)', padding: '8px 0' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', justifyContent: 'space-between' }}>
                <strong style={{ fontSize: 'var(--rh-size-footnote)' }}>{captionOrPreparing(GATE_CAPTIONS[w.gateId], w.gateId)}</strong>
                <Pill
                  caption={t('webAdmin.caseDetail.valuation.narrowingPill', { locale: 'cs', values: { pct: String(Math.round(w.narrowsByPct * 100)) } })}
                  bg="var(--rh-success-bg)"
                  fg="var(--rh-success)"
                />
              </div>
              <p style={{ margin: '4px 0 0', fontSize: 'var(--rh-size-meta)', color: 'var(--rh-text-muted)' }}>{fixDiacritics(w.reason)}</p>
            </div>
          ))}
        </Card>
      )}

      {scenarios.length > 0 && (
        <Card
          heading={t('webAdmin.caseDetail.valuation.scenariosHeading', { locale: 'cs' })}
          hint={t('webAdmin.caseDetail.valuation.scenariosHint', { locale: 'cs' })}
          testId="case-valuation-scenarios"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {scenarios.map((s) => (
              <div key={s.key} className="rh-card" style={{ padding: 12 }}>
                <strong style={{ fontSize: 'var(--rh-size-footnote)', textTransform: 'capitalize' }}>{s.title}</strong>
                {s.popis !== null && <p style={{ margin: '6px 0 0', fontSize: 'var(--rh-size-footnote)' }}>{s.popis}</p>}
                {s.indikace !== null && (
                  <p style={{ margin: '6px 0 0', fontSize: 'var(--rh-size-body)', fontWeight: 650 }}>{s.indikace}</p>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {facts.length > 0 && (
        <Card
          heading={t('webAdmin.caseDetail.valuation.marketPointsHeading', { locale: 'cs' })}
          hint={t('webAdmin.caseDetail.valuation.marketPointsHint', { locale: 'cs' })}
          testId="case-market-facts"
        >
          <FactList facts={facts} detail={detail} nodeLabels={nodeLabels} />
        </Card>
      )}
    </div>
  );
}

/**
 * Krok O5b: export reportu. Nahled se otevira jako ODKAZ do noveho okna
 * (prohlizec HTML rovnou vykresli a posle session cookie sam), PDF se objedna
 * a vyrobi ho worker — proto historie exportu se stavem, ne slib „hned".
 * Zadny soubor v repu: obsah vznika z aktualnich dat auditu a brandu kancelare.
 */
function ExportCard({
  ddCaseId,
  caseKind,
}: {
  ddCaseId: string;
  /** Krok REPB1: druh nemovitosti — u BYTU se exporty jmenuji podle predlohy. */
  caseKind: string;
}): React.JSX.Element {
  const [items, setItems] = React.useState<readonly DdExport[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  // Krok K1b: auto-export po dobehu auditu zarazuje PDF bez kliknuti - banner
  // se ukaze, kdyz mezi dvema nacteni pribude DONE polozka, kterou uzivatel
  // jeste nevidel (ne uz pri prvnim mountu - to jsou stare exporty).
  const [justReadyKind, setJustReadyKind] = React.useState<ReportExportKind | null>(null);
  /**
   * Krok PIL3 bod 4 (nalez F04): druhy exportu, ktere server odmitl sestavit,
   * a JEHO veta proc. Nabizet dal tlacitko, ktere skonci chybou, je past —
   * misto nej stoji vysvetleni s dalsim krokem.
   */
  const [unavailable, setUnavailable] = React.useState<
    Readonly<Partial<Record<ReportExportKind, string>>>
  >({});
  const seenDoneIds = React.useRef<Set<string> | null>(null);

  const load = React.useCallback(async () => {
    try {
      const next = await listCaseExports(ddCaseId);
      const doneIds = new Set(next.filter((i) => i.status === 'DONE').map((i) => i.id));
      if (seenDoneIds.current !== null) {
        const fresh = next.find((i) => i.status === 'DONE' && !seenDoneIds.current!.has(i.id));
        if (fresh !== undefined) setJustReadyKind(fresh.kind);
      }
      seenDoneIds.current = doneIds;
      setItems(next);
    } catch {
      setError(t('webAdmin.exports.loadError', { locale: 'cs' }));
    }
  }, [ddCaseId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  // Auto-refresh, dokud je neco ve fronte - makler(ka) nemusi klikat na
  // "Obnovit stav", aby uvidel(a) auto-export z workera po dobehu auditu.
  React.useEffect(() => {
    if (items === null || !items.some((i) => i.status === 'QUEUED' || i.status === 'RUNNING')) return;
    const timer = setInterval(() => void load(), 4000);
    return () => clearInterval(timer);
  }, [items, load]);

  async function order(kind: ReportExportKind): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await requestPdfExport(ddCaseId, kind);
      // Krok PRU1: objednane PDF reportu je taky „otevřený report".
      void markGuideSeen('report');
      await load();
    } catch (error: unknown) {
      /*
       * Krok PIL3 bod 4 (nalez F04): „Objednání PDF se nepodařilo" nerika nic.
       * Kdyz server odpovi, ze podklad pro report jeste neni, ma jeho VETU
       * (uz lokalizovanou, i s dalsim krokem) uvidet uzivatelka — a dokud
       * plati, uz se tenhle format nenabizi.
       */
      if (
        error instanceof DomainApiError &&
        error.type === 'dd-exports/report-model-missing'
      ) {
        setUnavailable((prev) => ({ ...prev, [kind]: error.message }));
      } else {
        setError(t('webAdmin.exports.requestError', { locale: 'cs' }));
      }
    } finally {
      setBusy(false);
    }
  }

  /**
   * Nazvy druhu exportu na JEDNOM miste - pouziva je seznam tlacitek i historie.
   * Do kroku REP1 mela historie ternarni podminku, takze kazdy druh krome
   * interniho auditu se v ni hlasil jako „Prezentace pro majitele".
   */
  /**
   * Krok REPB1: u BYTU jsou oba reporty v5 HLAVNI dokumenty (makler
   * a kupujici) a `technicky-report` je jejich zaloha a zdroj obsahu —
   * patri proto pod „Další exporty", ne mezi ne.
   */
  const isFlat = caseKind === 'FLAT';
  /*
   * Krok UXP4c (P3, nalez „Interní audit“ jako typ reportu pro makléře):
   * tenhle export je vnitrni kontrolni dokument kancelare, ne nic, co by
   * maklér objednaval — nabidka je proto jen pro owner/admin (stejny zdroj
   * pravdy jako `ownerAdminOnly` v `lib/nav.ts`/`capabilities.ts`).
   */
  const roles = useSession((s) => s.user?.roles) ?? [];
  const canSeeInternalAudit = isOwnerOrAdmin(roles);
  const kindLabels: Readonly<Record<ReportExportKind, string>> = {
    'interni-audit': t('webAdmin.exports.kinds.internalAudit', { locale: 'cs' }),
    'technicky-report': t('webAdmin.exports.kinds.technicalReport', { locale: 'cs' }),
    'prezentace-majiteli': t('webAdmin.exports.kinds.ownerPresentation', { locale: 'cs' }),
    'report-podrobny': t(
      isFlat ? 'webAdmin.exports.kinds.reportBroker' : 'webAdmin.exports.kinds.reportDetailed',
      { locale: 'cs' },
    ),
    'report-klient': t(
      isFlat ? 'webAdmin.exports.kinds.reportBuyer' : 'webAdmin.exports.kinds.reportClient',
      { locale: 'cs' },
    ),
  };
  /*
   * Krok UXP4b bod 2 (nalez P2-6): REPORT PRO KLIENTA MA V DETAILU JEDNO
   * MISTO — kartu „Co dám klientovi?" (`ClientReportCard`), ktera umi nahled,
   * vyrobu PDF, schvaleni i odeslani. V „Export reportů" proto `report-klient`
   * uz nestoji: makléř tu drive videl dve sady akci nad TYMZ dokumentem
   * a jedna z nich (holé „Objednat PDF") klientovi nic neodeslala.
   */
  const primaryKindsAll: readonly ReportExportKind[] = isFlat
    ? ['report-podrobny']
    : [
        'interni-audit',
        // Krok K0c: klientsky report ze sablony K1 - do ted sel vyrobit jen z CLI.
        'technicky-report',
        'prezentace-majiteli',
        // Krok REP1: dokument rendereru v5 nad `ReportModelV2` pro maklere.
        'report-podrobny',
      ];
  const secondaryKindsAll: readonly ReportExportKind[] = isFlat
    ? ['technicky-report', 'interni-audit', 'prezentace-majiteli']
    : [];
  const primaryKinds = primaryKindsAll.filter((kind) => kind !== 'interni-audit' || canSeeInternalAudit);
  const secondaryKinds = secondaryKindsAll.filter((kind) => kind !== 'interni-audit' || canSeeInternalAudit);
  const kinds: readonly { readonly id: ReportExportKind; readonly label: string }[] =
    primaryKinds.map((id) => ({ id, label: kindLabels[id] }));

  return (
    <Card
      heading={t('webAdmin.exports.heading', { locale: 'cs' })}
      hint={t('webAdmin.exports.hint', { locale: 'cs' })}
      testId="case-export"
    >
      {error !== null && (
        <p role="alert" style={{ color: 'var(--rh-danger)', fontSize: 'var(--rh-size-footnote)', margin: '0 0 8px' }}>
          {error}
        </p>
      )}
      {justReadyKind !== null && (
        <p
          role="status"
          style={{ color: 'var(--rh-success)', fontSize: 'var(--rh-size-footnote)', margin: '0 0 8px' }}
          data-testid="case-export-auto-ready"
        >
          {t('webAdmin.exports.autoExportReady', { locale: 'cs' })}
        </p>
      )}
      {/* Krok PRU1: cil „Ukaž mi" kroku „otevřít report" (`guide-highlight.tsx`). */}
      <div data-guide="report">
      {kinds.map((kind) => (
        <div
          key={kind.id}
          style={{ borderTop: '1px solid var(--rh-rule)', padding: '10px 0', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}
        >
          <span style={{ flex: '1 1 200px', fontSize: 'var(--rh-size-body)', fontWeight: 600 }}>{kind.label}</span>
          {/*
            Krok PIL3 bod 4 (F04): format, ktery server sestavit neumi, se uz
            nenabizi — na jeho miste stoji VETA SERVERU s dalsim krokem.
          */}
          {unavailable[kind.id] !== undefined ? (
            <span
              style={{ flex: '2 1 280px', fontSize: 'var(--rh-size-footnote)', color: 'var(--rh-text-muted)' }}
              data-testid={`case-export-unavailable-${kind.id}`}
            >
              {unavailable[kind.id]}
            </span>
          ) : (
            <>
              <a
                className="rh-btn"
                href={reportPreviewUrl(ddCaseId, kind.id)}
                target="_blank"
                rel="noreferrer"
                // Krok PRU1: otevreny report odskrtne krok pruvodce (skutecna akce).
                onClick={() => void markGuideSeen('report')}
                data-testid={`case-export-preview-${kind.id}`}
              >
                {t('webAdmin.exports.preview', { locale: 'cs' })}
              </a>
              <button
                type="button"
                className="rh-btn rh-btn--primary"
                disabled={busy}
                onClick={() => void order(kind.id)}
                data-testid={`case-export-pdf-${kind.id}`}
              >
                {t('webAdmin.exports.pdf', { locale: 'cs' })}
              </button>
            </>
          )}
        </div>
      ))}
      </div>

      {secondaryKinds.length > 0 && (
        <>
          <h3 style={{ fontSize: 'var(--rh-size-footnote)', margin: '12px 0 4px' }}>
            {t('webAdmin.exports.otherHeading', { locale: 'cs' })}
          </h3>
          {secondaryKinds.map((id) => (
            <div
              key={id}
              style={{ borderTop: '1px solid var(--rh-rule)', padding: '8px 0', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}
            >
              <span style={{ flex: '1 1 200px', fontSize: 'var(--rh-size-footnote)' }}>{kindLabels[id]}</span>
              <a
                className="rh-btn"
                href={reportPreviewUrl(ddCaseId, id)}
                target="_blank"
                rel="noreferrer"
                // Krok PRU1: otevreny report odskrtne krok pruvodce (skutecna akce).
                onClick={() => void markGuideSeen('report')}
                data-testid={`case-export-preview-${id}`}
              >
                {t('webAdmin.exports.preview', { locale: 'cs' })}
              </a>
              <button
                type="button"
                className="rh-btn"
                disabled={busy}
                onClick={() => void order(id)}
                data-testid={`case-export-pdf-${id}`}
              >
                {t('webAdmin.exports.pdf', { locale: 'cs' })}
              </button>
            </div>
          ))}
        </>
      )}

      <h3 style={{ fontSize: 'var(--rh-size-footnote)', margin: '12px 0 4px' }}>{t('webAdmin.exports.historyHeading', { locale: 'cs' })}</h3>
      {items === null || items.length === 0 ? (
        <p style={{ color: 'var(--rh-text-muted)', fontSize: 'var(--rh-size-footnote)', margin: 0 }}>{t('webAdmin.exports.historyEmpty', { locale: 'cs' })}</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} data-testid="case-export-history">
          {items.map((item) => {
            const href = exportDownloadUrl(item);
            return (
              <li
                key={item.id}
                style={{ borderTop: '1px solid var(--rh-rule)', padding: '8px 0', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 'var(--rh-size-footnote)' }}
              >
                <span style={{ minWidth: 130, color: 'var(--rh-icon-muted)' }}>{czDateTime(item.createdAt)}</span>
                <span style={{ flex: '1 1 160px' }}>{kindLabels[item.kind] ?? item.kind}</span>
                <span style={{ color: 'var(--rh-text-muted)' }}>{t(`webAdmin.exports.statuses.${item.status}`, { locale: 'cs' })}</span>
                {href !== null && (
                  <a className="rh-btn" href={href} data-testid={`case-export-download-${item.id}`}>
                    {t('webAdmin.exports.download', { locale: 'cs' })}
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {items !== null && items.some((i) => i.status === 'QUEUED' || i.status === 'RUNNING') && (
        <p style={{ color: 'var(--rh-text-muted)', fontSize: 'var(--rh-size-meta)', margin: '8px 0 0' }}>{t('webAdmin.exports.queuedNote', { locale: 'cs' })}</p>
      )}
      <button type="button" className="rh-btn" style={{ marginTop: 8 }} onClick={() => void load()} data-testid="case-export-refresh">
        {t('webAdmin.exports.refresh', { locale: 'cs' })}
      </button>
    </Card>
  );
}

/**
 * Sekce „Fotografie" zakazky (krok O6). Zije v zalozce Dokumenty — fotky jsou
 * soubory nemovitosti a nova polozka IA by kvuli nim byla navic (ROZHODNUTI O6).
 * `propertyId` chodi z `GET /v1/cases/:id`; zakazka bez nemovitosti sekci nema.
 */
function PhotosCard({ propertyId, caseId }: { propertyId: string | null; caseId: string }): React.JSX.Element | null {
  if (propertyId === null) return null;
  return (
    <Card
      heading={t('webAdmin.gallery.sectionTitle', { locale: 'cs' })}
      hint={t('webAdmin.gallery.uploadHint', { locale: 'cs' })}
      testId="case-photos"
    >
      {/* Krok STG1: s `caseId` ma kazda fotka tlacitko Virtualne zaridit (AI). */}
      <PropertyGallery propertyId={propertyId} caseId={caseId} />
    </Card>
  );
}

function DocumentsTab({
  detail,
  propertyId,
  zakazkaId,
  tasks,
}: {
  detail: CaseDetail;
  propertyId: string | null;
  /** `cases.id` — fronta uvolneni reportu zna zakazku, ne auditni pripad. */
  zakazkaId: string;
  tasks: readonly CockpitTask[];
}): React.JSX.Element {
  const rows = evidenceRows(detail);
  const inputs = detail.fixture.research.inputs;
  // Krok UXR2 (ROZHODNUTI UXR2-4): „Co dám klientovi?" se presunulo z kokpitu
  // sem — report je dokument a stoji vedle objednavek exportu.
  const reportCard = <ClientReportCard detail={detail} zakazkaId={zakazkaId} tasks={tasks} />;
  // Krok UI4: veřejná stránka nemovitosti (SR5) stojí vedle reportu — obě
  // karty řeší „co je vidět venku", jen jiný odkaz.
  const publicListingCard = <PublicListingCard caseId={zakazkaId} />;
  if (rows.length === 0 && inputs.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {reportCard}
        {publicListingCard}
        <ExportCard ddCaseId={detail.fixture.caseInfo.id} caseKind={detail.fixture.caseInfo.kind} />
        <PhotosCard propertyId={propertyId} caseId={zakazkaId} />
        <PreparingSection copy={CASE_TAB_PREPARING['dokumenty']} compact />
      </div>
    );
  }
  const byAdapter = new Map<string, R3FixtureEvidence[]>();
  for (const row of rows) {
    const bucket = byAdapter.get(row.sourceAdapter);
    if (bucket) bucket.push(row);
    else byAdapter.set(row.sourceAdapter, [row]);
  }
  return (
    <div className="flex flex-col gap-4">
      {reportCard}
      {publicListingCard}
      <ExportCard ddCaseId={detail.fixture.caseInfo.id} caseKind={detail.fixture.caseInfo.kind} />
      <PhotosCard propertyId={propertyId} caseId={zakazkaId} />
      <Card
        heading={t('webAdmin.caseDetail.documents.evidenceHeading', { locale: 'cs' })}
        hint={t('webAdmin.caseDetail.documents.evidenceHint', { locale: 'cs' })}
        testId="case-evidence"
      >
        <p style={{ fontSize: 'var(--rh-size-footnote)', color: 'var(--rh-text-muted)', margin: '0 0 8px' }}>
          {t('webAdmin.caseDetail.documents.totalSummary', {
            locale: 'cs',
            values: { count: String(rows.length), sources: String(byAdapter.size) },
          })}
        </p>
        {[...byAdapter.entries()].map(([adapter, items]) => (
          <Detail
            key={adapter}
            summary={t('webAdmin.caseDetail.documents.adapterSummary', { locale: 'cs', values: { adapter, count: String(items.length) } })}
          >
            {items.map((evidence) => (
              <EvidenceLine key={evidence.id} evidence={evidence} />
            ))}
          </Detail>
        ))}
      </Card>
      {inputs.length > 0 && (
        <Card
          heading={t('webAdmin.caseDetail.documents.inputsHeading', { locale: 'cs' })}
          hint={t('webAdmin.caseDetail.documents.inputsHint', { locale: 'cs' })}
          testId="case-inputs"
        >
          {inputs.map((inp) => (
            <div
              key={inp.filename}
              style={{ borderTop: '1px solid var(--rh-rule)', padding: '10px 0', display: 'flex', gap: 12, flexWrap: 'wrap' }}
            >
              <div style={{ flex: '1 1 240px' }}>
                <div style={{ fontSize: 'var(--rh-size-body)', fontWeight: 600 }}>{inp.filename}</div>
                {inp.note !== null && (
                  <p style={{ fontSize: 'var(--rh-size-footnote)', color: 'var(--rh-text-muted)', margin: '4px 0 0' }}>{inp.note}</p>
                )}
                <p style={{ fontSize: 'var(--rh-size-meta)', color: 'var(--rh-icon-muted)', margin: '4px 0 0' }}>
                  {t('webAdmin.caseDetail.documents.mimeReceivedLine', { locale: 'cs', values: { mime: inp.mime, date: czDate(inp.uploadedAt) } })}
                </p>
              </div>
            </div>
          ))}
          <p style={{ fontSize: 'var(--rh-size-meta)', color: 'var(--rh-icon-muted)', margin: '10px 0 0' }}>{INPUTS_PRIVACY_NOTE}</p>
        </Card>
      )}
    </div>
  );
}

/**
 * Historie pripadu (pro maklere) je oddelena od technickeho logu (pro
 * administratora) — Milnik 1, bod 9. Driv byla na zalozce jen telemetrie
 * behu: fetch pozadavky, adaptery, robots disallow, AI kapacita.
 */
function ActivityTab({ detail }: { detail: CaseDetail }): React.JSX.Element {
  const research = detail.fixture.research;
  /*
   * Krok D6b bod 1: casomíra je PRED branou „beh jeste nic nenamerilo" —
   * clovek na zakazce pracuje i tehdy, kdyz autonomni beh jeste nebezel
   * (typicky prave rucni Komplet), a prave ty minuty jsou nejdrazsi.
   */
  const workTimer = <WorkTimerCard caseId={detail.fixture.caseInfo.id} />;
  if (research.startedAt === null && research.sources.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {workTimer}
        <PreparingSection copy={CASE_TAB_PREPARING['aktivity']} compact />
      </div>
    );
  }
  const events = historyEvents(detail);
  return (
    <div className="flex flex-col gap-4">
      {workTimer}
      <Card
        heading={t('webAdmin.caseDetail.activity.heading', { locale: 'cs' })}
        hint={t('webAdmin.caseDetail.activity.hint', { locale: 'cs' })}
        testId="case-history"
      >
        {events.length === 0 ? (
          <p style={{ color: 'var(--rh-text-muted)', margin: 0 }}>{NOT_DETERMINED}</p>
        ) : (
          events.map((e) => (
            <div key={e.id} style={{ borderTop: '1px solid var(--rh-rule)', padding: '8px 0', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 'var(--rh-size-footnote)', color: 'var(--rh-icon-muted)', minWidth: 130 }}>{czDateTime(e.at)}</span>
              <span style={{ fontSize: 'var(--rh-size-body)' }}>{e.caption}</span>
            </div>
          ))
        )}
        <p style={{ fontSize: 'var(--rh-size-meta)', color: 'var(--rh-icon-muted)', margin: '10px 0 0' }}>
          {t('webAdmin.caseDetail.activity.futureStorageNote', { locale: 'cs' })}
        </p>
      </Card>

      <Detail summary={TECHNICAL_DETAIL_SUMMARY}>
        <div className="flex flex-col gap-4">
      <Card heading={t('webAdmin.caseDetail.activity.runHeading', { locale: 'cs' })} testId="case-run">
        <InfoRow label={t('webAdmin.caseDetail.activity.started', { locale: 'cs' })}>{czDateTime(research.startedAt)}</InfoRow>
        <InfoRow label={t('webAdmin.caseDetail.activity.finished', { locale: 'cs' })}>{czDateTime(research.finishedAt)}</InfoRow>
        <InfoRow label={t('webAdmin.caseDetail.activity.duration', { locale: 'cs' })}>
          {research.durationMinutes !== null
            ? t('webAdmin.caseDetail.activity.durationMinutes', { locale: 'cs', values: { minutes: String(research.durationMinutes) } })
            : NOT_DETERMINED}
        </InfoRow>
        <InfoRow label={t('webAdmin.caseDetail.activity.fetching', { locale: 'cs' })}>
          {t('webAdmin.caseDetail.activity.fetchSummary', {
            locale: 'cs',
            values: { total: String(research.fetch.total), ok: String(research.fetch.ok), failed: String(research.fetch.failed) },
          })}
        </InfoRow>
        <InfoRow label={t('webAdmin.caseDetail.activity.dataSnapshot', { locale: 'cs' })}>
          {t('webAdmin.caseDetail.activity.dataSnapshotValue', {
            locale: 'cs',
            values: { revision: detail.fixture.trust.revision ?? NOT_DETERMINED, hint: CASE_REVISION_SNAPSHOT_HINT },
          })}
        </InfoRow>
      </Card>
      <Card heading={t('webAdmin.caseDetail.activity.sourcesHeading', { locale: 'cs' })} testId="case-run-sources">
        {research.sources.map((s) => (
          <InfoRow key={s.sourceId} label={s.sourceId}>
            {String(s.count)}×
            {s.failed > 0 ? t('webAdmin.caseDetail.activity.failedSuffix', { locale: 'cs', values: { count: String(s.failed) } }) : ''}
          </InfoRow>
        ))}
      </Card>
      {research.failures.length > 0 && (
        <Card heading={t('webAdmin.caseDetail.activity.failuresHeading', { locale: 'cs' })} testId="case-run-failures">
          {research.failures.map((f, i) => (
            <div key={`${f.actionKind}-${String(i)}`} style={{ borderTop: '1px solid var(--rh-rule)', padding: '6px 0', fontSize: 'var(--rh-size-footnote)', overflowWrap: 'anywhere' }}>
              {f.actionKind}
              {f.sourceId !== null && <> · {f.sourceId}</>} — {f.reason ?? t('webAdmin.caseDetail.activity.unknownReason', { locale: 'cs' })}
              {f.alternativeTried !== null && (
                <span style={{ color: 'var(--rh-success)' }}>
                  {t('webAdmin.caseDetail.activity.alternativeTriedSuffix', { locale: 'cs', values: { alternative: f.alternativeTried } })}
                </span>
              )}
            </div>
          ))}
        </Card>
      )}
      <Card
        heading={t('webAdmin.caseDetail.activity.archiveHeading', { locale: 'cs' })}
        hint={t('webAdmin.caseDetail.activity.archiveHint', { locale: 'cs' })}
        testId="case-run-archive"
      >
        <p style={{ fontSize: 'var(--rh-size-footnote)', margin: '0 0 6px' }}>
          {t('webAdmin.caseDetail.activity.archiveSummary', {
            locale: 'cs',
            values: { discovered: String(research.archive.discovered), fetched: String(research.archive.fetched) },
          })}
        </p>
        {research.archive.snapshots.map((s, i) => (
          <div key={`${s.originalUrl ?? ''}-${String(i)}`} style={{ fontSize: 'var(--rh-size-meta)', color: 'var(--rh-icon-muted)', overflowWrap: 'anywhere' }}>
            {t('webAdmin.caseDetail.activity.archiveSnapshotLine', {
              locale: 'cs',
              values: { date: czDateTime(s.archiveTimestamp), url: s.originalUrl ?? '—' },
            })}
          </div>
        ))}
      </Card>
      <Card
        heading={t('webAdmin.caseDetail.activity.aiHeading', { locale: 'cs' })}
        hint={t('webAdmin.caseDetail.activity.aiHint', { locale: 'cs' })}
        testId="case-run-ai"
      >
        <p style={{ fontSize: 'var(--rh-size-footnote)', margin: '0 0 6px' }}>
          {t('webAdmin.caseDetail.activity.aiSteps', { locale: 'cs', values: { count: String(research.ai.steps) } })}
        </p>
        {research.ai.byTask.map((task, i) => (
          <InfoRow key={`${task.taskType ?? ''}-${String(i)}`} label={task.taskType ?? '—'}>
            {t('webAdmin.caseDetail.activity.aiTaskLine', {
              locale: 'cs',
              values: {
                count: String(task.count),
                model: task.model ?? '—',
                capacity: task.capacityType ?? t('webAdmin.caseDetail.activity.capacityNotRecorded', { locale: 'cs' }),
              },
            })}
          </InfoRow>
        ))}
        <p style={{ marginTop: 8, marginBottom: 0, color: 'var(--rh-icon-muted)', fontSize: 'var(--rh-size-meta)' }}>
          {t('webAdmin.caseDetail.activity.fixtureLine', {
            locale: 'cs',
            values: {
              source: detail.fixture.generated.source,
              exportedAt: czDateTime(detail.fixture.generated.exportedAt),
              policyVersion: detail.fixture.trust.policyVersion,
            },
          })}
        </p>
      </Card>
        </div>
      </Detail>
    </div>
  );
}

// ---- sloupce ----------------------------------------------------------------

function LeftColumn({
  detail,
  gateKind,
}: {
  detail: CaseDetail;
  /** Krok UX2: jmenovatel podle ZJISTENEHO predmetu, ne zadani maklere. */
  gateKind: GateCaseKind | null;
}): React.JSX.Element {
  const header = caseHeader(detail);
  const subject = caseSubjectOf(detail);
  const rows = areaRows(detail);
  const area = areaReconciliationResult(detail);
  const events = historyEvents(detail);
  // Krok UX1: misto primeho cteni `trust.decisionReadiness` (ktere nikdo
  // neupravi) jde vsechno pres gateProgress — jinak se cisla rozjela.
  // Krok UX2 navic bere ZJISTENY typ predmetu, ne `dd_cases.kind`.
  const gates = gateProgress(detail, gateKind);
  const phase = casePhase(detail);
  const phaseBadgeClass = phase === 'AUDIT_DOKONCENO' ? 'rh-badge--success' : phase === 'AUDIT_PROBIHA' ? 'rh-badge--warning' : 'rh-badge--neutral';
  return (
    <div className="case-detail__aside case-detail__aside--left">
      <Card heading={t('webAdmin.caseDetail.left.summaryHeading', { locale: 'cs' })} testId="case-summary">
        <InfoRow label={t('webAdmin.caseDetail.left.kind', { locale: 'cs' })}>{subject.label}</InfoRow>
        <InfoRow label={t('webAdmin.caseDetail.left.phase', { locale: 'cs' })}>
          <span className={`rh-badge ${phaseBadgeClass}`}>{CASE_PHASE_CAPTIONS[phase]}</span>
        </InfoRow>
        <InfoRow label={t('webAdmin.caseDetail.left.opened', { locale: 'cs' })}>{czDate(header.openedAt)}</InfoRow>
        <InfoRow label={t('webAdmin.caseDetail.left.cadastralArea', { locale: 'cs' })}>{header.cadastralAreaName ?? NOT_DETERMINED}</InfoRow>
        {/*
          „Predmet: nezjisteno" bylo nepravdive, kdyz se pracuje se tremi
          kandidatnimi parcelami. Kdyz chybi fakt `scope.parcels`, spocitame
          parcely, ktere skutecne mame (vymery dle KN), a NAZVEME je
          kandidatnimi — potvrzeny rozsah je jina vec (brana GATE_SCOPE).
        */}
        <InfoRow label={t('webAdmin.caseDetail.left.subject', { locale: 'cs' })}>
          {header.parcelCount !== null
            ? t('webAdmin.caseDetail.left.subjectParcels', { locale: 'cs', values: { count: String(header.parcelCount) } })
            : rows.length > 0
              ? t('webAdmin.caseDetail.left.subjectCandidateParcels', { locale: 'cs', values: { count: String(rows.length) } })
              : NOT_DETERMINED}
        </InfoRow>
        <InfoRow label={t('webAdmin.caseDetail.left.gates', { locale: 'cs' })}>
          {t('webAdmin.caseDetail.left.gatesClosedSummary', {
            locale: 'cs',
            values: { closed: String(gates.closed), total: String(gates.total) },
          })}
        </InfoRow>
      </Card>
      <Card heading={t('webAdmin.caseDetail.left.areasHeading', { locale: 'cs' })} testId="case-areas-side">
        {rows.length === 0 ? (
          <p className="text-sm m-0" style={{ color: 'var(--rh-text-muted)' }}>{NOT_DETERMINED}</p>
        ) : (
          <>
            {rows.map((row) => (
              <InfoRow key={row.parcelLabel} label={row.parcelLabel}>{m2(row.areaM2)}</InfoRow>
            ))}
            {/* Uzky sloupec: dlouhy popisek nad hodnotou, aby se cislo nelamalo. */}
            <StackedRow label={AREA_LABELS.knCandidate}>{m2(area.knCandidateTotalM2)}</StackedRow>
            {area.manualInputTotalM2 !== null && (
              <StackedRow label={AREA_LABELS.manualInput}>{m2(area.manualInputTotalM2)}</StackedRow>
            )}
            <StackedRow label={AREA_LABELS.confirmed}>
              {area.confirmedSubjectTotalM2 === null ? AREA_LABELS.notConfirmed : m2(area.confirmedSubjectTotalM2)}
            </StackedRow>
          </>
        )}
      </Card>
      {/* Nejsou to revize auditu (ty jeste neexistuji), ale udalosti pripadu. */}
      <Card heading={t('webAdmin.caseDetail.activity.heading', { locale: 'cs' })} testId="case-history-summary">
        {events.map((event) => (
          <div key={event.id} style={{ borderTop: '1px solid var(--rh-rule)', padding: '6px 0', fontSize: 'var(--rh-size-footnote)' }}>
            <div className="font-medium">{event.caption}</div>
            <div style={{ color: 'var(--rh-text-muted)', fontSize: 'var(--rh-size-meta)' }}>{czDateTime(event.at)}</div>
          </div>
        ))}
      </Card>
      <Card heading={t('webAdmin.caseDetail.left.invariantsHeading', { locale: 'cs' })} testId="case-invariants">
        <div style={{ fontSize: 'var(--rh-size-footnote)', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div className="flex items-baseline justify-between gap-2">
            <span>{t('webAdmin.caseDetail.left.planVsCadastre', { locale: 'cs' })}</span>
            {area.reconciliationStatus === 'MATCH' ? (
              <Pill caption={t('webAdmin.caseDetail.left.withinTolerancePill', { locale: 'cs' })} bg="var(--rh-success-bg)" fg="var(--rh-success)" />
            ) : area.reconciliationStatus === 'CONFLICT' ? (
              <Pill
                caption={
                  area.differenceM2 !== null
                    ? t('webAdmin.caseDetail.left.conflictPill', { locale: 'cs', values: { diff: m2(area.differenceM2) } })
                    : t('webAdmin.caseDetail.areaSummary.conflictPill', { locale: 'cs' })
                }
                bg="var(--rh-warning-bg)"
                fg="var(--rh-warning)"
              />
            ) : (
              <Pill caption={NOT_DETERMINED} bg="var(--rh-rule)" fg="var(--rh-text-muted)" />
            )}
          </div>
          <div className="flex items-baseline justify-between gap-2">
            <span>{t('webAdmin.caseDetail.left.factWithoutEvidence', { locale: 'cs' })}</span>
            <Pill
              caption={
                detail.hiddenFactCount === 0
                  ? t('webAdmin.caseDetail.left.holdsPill', { locale: 'cs' })
                  : t('webAdmin.caseDetail.left.holdsHiddenPill', { locale: 'cs', values: { count: String(detail.hiddenFactCount) } })
              }
              bg="var(--rh-success-bg)"
              fg="var(--rh-success)"
            />
          </div>
          <div className="flex items-baseline justify-between gap-2">
            <span>{t('webAdmin.caseDetail.left.mandatoryGatesClosed', { locale: 'cs' })}</span>
            {gates.allClosed ? (
              <Pill caption={t('webAdmin.caseDetail.left.allClosedPill', { locale: 'cs' })} bg="var(--rh-success-bg)" fg="var(--rh-success)" />
            ) : (
              <Pill
                caption={t('webAdmin.caseDetail.left.partialClosedPill', {
                  locale: 'cs',
                  values: { closed: String(gates.closed), total: String(gates.total) },
                })}
                bg="var(--rh-danger-bg)"
                fg="var(--rh-danger)"
              />
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

function RightColumn({
  detail,
  gateKind,
  onOpenTab,
}: {
  detail: CaseDetail;
  gateKind: GateCaseKind | null;
  onOpenTab: (tab: CaseTabId) => void;
}): React.JSX.Element {
  const risk = riskSummary(detail);
  const access = accessSummary(detail);
  const utilities = utilitiesSummary(detail);
  const gates = gateProgress(detail, gateKind);
  const detailButton = (tab: CaseTabId): React.JSX.Element => (
    <button
      type="button"
      className="rh-btn mt-2"
      onClick={() => { onOpenTab(tab); }}
      data-testid={`case-side-open-${tab}`}
    >
      {t('webAdmin.caseDetail.right.detailButton', { locale: 'cs' })}
    </button>
  );
  return (
    <div className="case-detail__aside case-detail__aside--right">
      <Card heading={t('webAdmin.caseDetail.risks.heading', { locale: 'cs' })} testId="case-side-risks">
        <InfoRow label={t('webAdmin.caseDetail.right.floodQ100', { locale: 'cs' })}>
          {risk.inQ100 === null
            ? NOT_DETERMINED
            : risk.inQ100
              ? t('webAdmin.caseDetail.right.affected', { locale: 'cs' })
              : t('webAdmin.caseDetail.right.notAffected', { locale: 'cs' })}
        </InfoRow>
        {/* Krok PRF5 bod 2: STUPEN rizika z teze stupnice jako report
            (`floodZoneRiskLevel` v @reos/duediligence) — karta rizik a dokument
            nesmi o tomtez Q100 rict jine. */}
        <InfoRow label={t('webAdmin.caseDetail.right.floodRisk', { locale: 'cs' })} testId="case-flood-risk">
          {t(`webAdmin.caseDetail.right.floodRiskLevel.${risk.floodRisk}` as MessageKey, { locale: 'cs' })}
        </InfoRow>
        <InfoRow label={t('webAdmin.caseDetail.right.nearestFloodZone', { locale: 'cs' })}>{risk.nearestFloodZone ?? NOT_DETERMINED}</InfoRow>
        <InfoRow label={t('webAdmin.caseDetail.right.futureProjects', { locale: 'cs' })}>{String(risk.futureProjectCount)}</InfoRow>
        {/* Krok UX1: stejne cislo jako dlaždice i hlavička (drive riskSummary.openGateCount). */}
        <InfoRow label={t('webAdmin.caseDetail.right.openGates', { locale: 'cs' })} testId="case-open-gates">
          {String(gates.open)}
        </InfoRow>
        {detailButton('rizika')}
      </Card>
      <Card heading={t('webAdmin.caseDetail.right.accessHeading', { locale: 'cs' })} testId="case-side-access">
        <InfoRow label={t('webAdmin.caseDetail.right.streetFrontage', { locale: 'cs' })}>
          {access.frontageM !== null ? `${String(access.frontageM)} m` : NOT_DETERMINED}
        </InfoRow>
        <InfoRow label={t('webAdmin.caseDetail.right.corridorMax', { locale: 'cs' })}>
          {access.corridorMaxWidthM !== null ? `${String(access.corridorMaxWidthM)} m` : NOT_DETERMINED}
        </InfoRow>
        <InfoRow label={t('webAdmin.caseDetail.right.legalAccess', { locale: 'cs' })}>
          {t(LEGAL_ACCESS_SIDE_KEY[access.legalAccess], { locale: 'cs' })}
        </InfoRow>
        {detailButton('pristup')}
      </Card>
      <Card heading={t('webAdmin.caseDetail.right.utilitiesHeading', { locale: 'cs' })} testId="case-side-utilities">
        <InfoRow label={t('webAdmin.caseDetail.utilities.water', { locale: 'cs' })}><ConnectionValue raw={utilities.water} /></InfoRow>
        <InfoRow label={t('webAdmin.caseDetail.utilities.sewer', { locale: 'cs' })}><ConnectionValue raw={utilities.sewer} /></InfoRow>
        <InfoRow label={t('webAdmin.caseDetail.utilities.gas', { locale: 'cs' })}><ConnectionValue raw={utilities.gas} /></InfoRow>
        <InfoRow label={t('webAdmin.caseDetail.right.sewerCapacity', { locale: 'cs' })}>
          {t(SEWER_CAPACITY_SIDE_KEY[utilities.sewerCapacity], { locale: 'cs' })}
        </InfoRow>
        {detailButton('site')}
      </Card>
    </div>
  );
}

// ---- stranka ----------------------------------------------------------------

export function ZakazkaDetailPage({
  caseId: zakazkaId,
  section,
}: {
  /** `cases.id` zakazky z URL — detail si k ni sam najde auditni pripad. */
  readonly caseId: string;
  /** Aktivni sekce z `?sekce=` (router uz ji zvalidoval). */
  readonly section?: string;
}): React.JSX.Element {
  const navigate = useNavigate();
  /**
   * Krok MRGD7 (dotazeno v TECH1) — ODLOZENA AKCE SE PRI ODCHODU Z OBRAZOVKY RUSI.
   *
   * Detail ma dva druhy odlozenych volani: posun na sekci po prekresleni (0 ms)
   * a znovunacteni detailu po dobehnutem behu (4 s). Bez uklidu bezel timer dal
   * i po odchodu ze zakazky — `reloadDetail()` pak menil stav odmontovane
   * komponenty a poslal dotaz na detail, ktery uz nikdo necte. Pravidlo zije
   * v `useLater` (`@redhand/ui`), aby ho dalsi obrazovky nemusely opisovat;
   * `window.setTimeout` se tu uz nepouziva.
   */
  const later = useLater();
  // Krok DEMO7 bod 1: na verejnem demu je prevereni ukazka zdarma (server
  // prehraje pripraveny vysledek). Rozhoduje TYZ priznak ze `GET /api/me`,
  // podle ktereho pruh rika „data se kazdou hodinu vraceji".
  const publicDemo = useSession((s) => s.publicDemo);
  const [rawDetail, setRawDetail] = React.useState<CaseDetail | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  // Zdrojem pravdy o aktivni sekci je URL, ne stav komponenty — reload,
  // back/forward i sdileny odkaz otevrou tutez sekci (Milnik 1, bod 5).
  const tab: CaseTabId = isCaseTabId(section) ? (section as CaseTabId) : CASE_TAB_DEFAULT;
  const setTab = React.useCallback(
    (next: CaseTabId): void => {
      // `replace: false` => back/forward prochazi sekce; scroll se neresetuje.
      void navigate({ to: '.', search: { sekce: next }, resetScroll: false });
    },
    [navigate],
  );
  const [noAudit, setNoAudit] = React.useState(false);
  const [reloadToken, setReloadToken] = React.useState(0);
  const [answerTarget, setAnswerTarget] = React.useState<AnswerTarget | null>(null);
  /* Krok UXR5 bod 15: mobil ma jine rozvrzeni hlavicky (bez popisku u prouzku
     fazi a bez lepene akce) — to uz neni jen CSS, musi to vedet DOM. */
  // Krok UX2: „Prověřit znovu“ — nový běh nad stejnou zakázkou. Ne jeste
  // automaticky a nikdy hromadně (zadání UX2: stojí peníze).
  const [rechecking, setRechecking] = React.useState(false);
  const [recheckError, setRecheckError] = React.useState<string | null>(null);
  /*
   * Krok UXR2: hlavicka predlohy rika „typ · prodej/pronájem · makléř ·
   * fáze" a treti otazka kokpitu kresli cestu zakazky — to vsechno je v
   * ZAKAZCE (`cases`), ne v auditnim pripadu. Detail si ji proto drzi celou,
   * ne jen `propertyId` (O6).
   *
   * Vsechny tyhle dotazy jsou DOPLNKOVE: kdyz nektery selze, obrazovka se
   * vykresli bez toho udaje (makler bez jmena, cesta bez dat), ale nespadne.
   */
  const [kase, setKase] = React.useState<KanbanCase | null>(null);
  /**
   * Krok TOKYFIX2 (prekazka P4/3): zakazka NENI nase, nebo neexistuje.
   *
   * `getCase` pro cizi zakazku vrati 404/403 a ten se tu driv tise spolkl
   * (`catch(() => undefined)`), takze stranka zustala na „Načítám zakázku…"
   * a pod tim ukazovala KOSTRU karty („strany zakázky nejsou zadané"…) —
   * tedy tvrzeni o zakazce, kterou uzivatel videt nesmi. 403 dostane TUTEZ
   * odpoved jako 404 zamerne: jestli zakazka ciziho tenanta existuje, neni
   * nic, co by se mel dozvedet.
   */
  const [caseMissing, setCaseMissing] = React.useState(false);
  /** Krok UXP1 (nalez P1-8) — „Upravit" z nabidky listy otevre editaci zakazky. */
  const [editOpen, setEditOpen] = React.useState(false);
  // Krok UXV4b: stav inzeratu pro kartu „Další krok" (jen prodej/pronajem).
  const [listingState, setListingState] = React.useState<'none' | 'draft' | 'submitted' | null>(null);
  const [journey, setJourney] = React.useState<CaseJourney | null>(null);
  /**
   * Krok UXR3 — SUROVA historie zakazky. „Poslední dění" (predloha) z ni
   * sklada vety pres `caseEventLines`; `journey` z teze historie bere data
   * vstupu do fazi. Jedno volani `GET /v1/cases/:id/timeline`, dva pohledy.
   */
  const [timeline, setTimeline] = React.useState<readonly CaseTimelineItem[]>([]);
  /** Krok UXR3 — karta „Peníze" vpravo: provize a naklady jednim volanim. */
  const [costs, setCosts] = React.useState<CaseCostsResult | null>(null);
  const [costsLoading, setCostsLoading] = React.useState(true);
  const [brokerName, setBrokerName] = React.useState<string | null>(null);
  const [participants, setParticipants] = React.useState<readonly CaseParticipant[]>([]);
  const [partyNames, setPartyNames] = React.useState<ReadonlyMap<string, string>>(new Map());
  /** Krok UXV4a2 — sekce „Lidé (volat)": telefon strany z `…/contacts`. */
  const [phoneByParty, setPhoneByParty] = React.useState<ReadonlyMap<string, string>>(new Map());
  const [cover, setCover] = React.useState<CoverInfo | null>(null);
  const [humanTasks, setHumanTasks] = React.useState<readonly HumanTaskSummary[]>([]);
  const [humanTasksToken, setHumanTasksToken] = React.useState(0);
  /*
   * Krok UXV4a2 bod 4 — spusteni PRVNIHO proverení ze zakazky, ktera zadne
   * nema. Druh nemovitosti server sam neuhodne (bez predchoziho auditniho
   * pripadu nema odkud, viz `audit-runs.ts` → `kind-required`), takze se
   * precte z nemovitosti zakazky; zadny novy udaj se nevymysli.
   */
  const [propertyKind, setPropertyKind] = React.useState<string | null>(null);
  /** Druh vybrany maklerem; `null` = jeste nesahl, plati predvyplneny z nemovitosti. */
  const [pickedReviewKind, setPickedReviewKind] = React.useState<GateCaseKind | '' | null>(null);
  const [propertyAddress, setPropertyAddress] = React.useState<string | null>(null);
  /** Krok P8F6 bod 3: fakta karty nemovitosti (cena, plocha, dispozice, patro, jednotka). */
  const [propertyFacts, setPropertyFacts] = React.useState<readonly PropertyFact[]>([]);
  /** Krok ZAK1 bod 5: podklad panelu „Co zbývá zjistit" (sablona a stav naboru). */
  const [intakeContext, setIntakeContext] = React.useState<IntakeContextDto | null>(null);
  const [intakeTemplate, setIntakeTemplate] = React.useState<ChecklistTemplateDto | null>(null);
  const [startingReview, setStartingReview] = React.useState(false);
  /**
   * Krok UXP4a bod 2 (nalez P2-2): beh uz je ve FRONTE. Tlacitko pak zustava
   * zamcene — driv se po `startingReview = false` zase rozsvitilo a druhy klik
   * objednal DRUHY placeny beh.
   */
  const [reviewQueued, setReviewQueued] = React.useState(false);
  /**
   * Krok QAFIX14 — UKAZKOVY (PREHRANY) BEH DEMO KANCELARE.
   *
   * Plati i po refreshi: nestoji na odpovedi na „Spustit prověření", ale na
   * behu, ze ktereho je slozeny detail (`loadCaseDetail` vraci oboji).
   */
  const [demoSampleRun, setDemoSampleRun] = React.useState(false);
  /**
   * Krok QAFIX14 — BEH, NA KTERY SE CEKA (`QUEUED`/`RUNNING`).
   *
   * Do QAFIX14 stranka odpoved na spusteni zahodila, takze „Prověření je ve
   * frontě…" svitilo navzdy a o dobehnutem behu se makler dozvedel jen tim,
   * ze stranku nacetl znovu. `null` = na nic se neceka.
   */
  const [queuedRunId, setQueuedRunId] = React.useState<string | null>(null);
  /** Vlastni potvrzeni (ne `window.confirm`) — vzor je `CaseFreshnessBar`. */
  const [confirmingReview, setConfirmingReview] = React.useState(false);
  /**
   * Krok ZDARMA1 bod 1 — ROZSAH proverení. Vychozi je BEZPLATNA uroven
   * (`REVIEW_LEVEL_DEFAULT`); makler ji smi prepnout na kompletni, ktere se
   * odecte z kreditu kancelare.
   */
  const [reviewLevel, setReviewLevel] = React.useState<ReviewLevel>(REVIEW_LEVEL_DEFAULT);
  /**
   * Krok NOC6 body 3+4 (N27, N28): cena kompletniho provereni, zustatek
   * kreditu kancelare a zbytek mesicniho stropu bezplatnych behu. `null` =
   * jeste nevime; popisky pak zadne cislo netvrdi.
   */
  const [reviewOptions, setReviewOptions] = React.useState<ReviewOptionsDto | null>(null);
  const [startReviewNotice, setStartReviewNotice] = React.useState<string | null>(null);
  const [startReviewError, setStartReviewError] = React.useState<string | null>(null);
  const propertyId = kase?.propertyId ?? null;
  /**
   * Zivy pruchod 13. 10. (V10): `/zakazky/nova` (preklep za `/zakazky/novy`)
   * poslal „nova" jako `zakazkaId` do API, ktere ho zamitlo 3x 400 — stranka
   * ale zustala navzdy na „Načítám zakázku…", protoze kazdy z efektu nize
   * chybu tise spolkl. ID zakazky je VZDY UUID (`newId()`), takze spatny tvar
   * jde rozpoznat BEZ dotazu na server a misto nekonecneho nacitani ukazat
   * 404 (`caseNotFoundTitle` nize).
   */
  const validCaseId = UUID_RE.test(zakazkaId);

  React.useEffect(() => {
    if (!validCaseId) return;
    let alive = true;
    setKase(null);
    setCaseMissing(false);
    setListingState(null);
    setJourney(null);
    setTimeline([]);
    setCover(null);
    void getCase(zakazkaId)
      .then(async (item) => {
        if (!alive) return;
        setKase(item);
        if (item.type === 'brokerage_sale' || item.type === 'brokerage_rent') {
          void fetchListingDraftSummary(zakazkaId)
            .then((summary) => {
              if (alive) setListingState(summary.state);
            })
            .catch(() => undefined);
        }
        const items = await listCaseTimeline(zakazkaId).catch(() => []);
        if (alive) {
          setTimeline(items);
          setJourney(caseJourney(item.stage, items, item.openedAt));
        }
        if (item.ownerUserId !== null) {
          const members = await listMembers().catch(() => []);
          const owner = members.find((member) => member.userId === item.ownerUserId);
          if (alive && owner !== undefined) setBrokerName(owner.name ?? owner.email);
        }
        if (item.propertyId !== null) {
          const covers = await loadCaseCovers([item.propertyId]).catch(
            () => new Map<string, CoverInfo>(),
          );
          if (alive) setCover(covers.get(item.propertyId) ?? null);
        }
      })
      .catch((cause: unknown) => {
        // Vypadek site nebo chyba serveru NENI „nenalezeno" — tam se stranka
        // chova jako driv (doplnkove udaje chybi), jinak by kazdy vypadek
        // tvrdil, ze zakazka neexistuje.
        if (alive && cause instanceof DomainApiError && (cause.status === 404 || cause.status === 403)) {
          setCaseMissing(true);
        }
      });
    return () => { alive = false; };
  }, [zakazkaId]);

  /*
   * Krok NOC6 body 3+4: cisla k volbe rozsahu. Chyba NENI fatalni — volba
   * zustane bez cisel (puvodni texty), obrazovka stoji.
   */
  React.useEffect(() => {
    if (!validCaseId) return;
    let alive = true;
    setReviewOptions(null);
    void getReviewOptions(zakazkaId).then(
      (result) => { if (alive) setReviewOptions(result); },
      () => undefined,
    );
    return () => { alive = false; };
  }, [zakazkaId]);

  React.useEffect(() => {
    if (!validCaseId) return;
    let alive = true;
    setCosts(null);
    setCostsLoading(true);
    // Chyba NENI fatalni — karta „Peníze" rekne, ze nic nevi, obrazovka stoji.
    void listCaseCosts(zakazkaId).then(
      (result) => { if (alive) { setCosts(result); setCostsLoading(false); } },
      () => { if (alive) setCostsLoading(false); },
    );
    return () => { alive = false; };
  }, [zakazkaId]);

  React.useEffect(() => {
    if (!validCaseId) return;
    let alive = true;
    setParticipants([]);
    setPartyNames(new Map());
    void listCaseParticipants(zakazkaId)
      .then(async (items) => {
        if (!alive) return;
        setParticipants(items);
        // Jmena stran chodi po jednom (`/v1/parties/:id`) — stran je na
        // zakazce par, ne stovky. Chyba u jedne nesmi zahodit ostatni.
        const names = new Map<string, string>();
        const phones = new Map<string, string>();
        await Promise.all(
          [...new Set(items.map((item) => item.partyId))].slice(0, 8).map(async (partyId) => {
            const party = await getParty(partyId).catch(() => null);
            if (party !== null) names.set(partyId, party.displayName);
            // Krok UXV4a2: telefon pro „volat" z teze davky — kontakt bez
            // telefonu radek jen nedostane, nic se nenahrazuje.
            const contacts = await listPartyContacts(partyId).catch(() => []);
            const phone = contacts.find((contact) => contact.kind === 'phone');
            if (phone !== undefined) phones.set(partyId, phone.value);
          }),
        );
        if (alive) {
          setPartyNames(names);
          setPhoneByParty(phones);
        }
      })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [zakazkaId]);

  /*
   * Druh a adresa nemovitosti — jen pro zakazku BEZ proverení (bod 4), aby
   * tlacitko „Spustit prověření" melo co poslat. Zakazka s proverením uz obojí
   * zna z auditniho pripadu, takze se dotaz nikdy nepolozi dvakrat.
   *
   * KROK QAFIX14b bod 2 — A PRO UKAZKOVY BEH V DEMU. Tam je auditni pripad
   * UKAZKA (jina nemovitost, jiny druh), takze nazev ani typ zakazky z nej
   * vzit nelze: musi prijit z nemovitosti, jako u zakazky bez proverení.
   */
  React.useEffect(() => {
    if ((!noAudit && !demoSampleRun) || propertyId === null) return;
    let alive = true;
    void fetchPropertyDetail(propertyId).then(
      (property) => {
        if (!alive) return;
        setPropertyKind(property.kind);
        setPropertyAddress(property.address?.formatted ?? null);
      },
      () => undefined,
    );
    return () => { alive = false; };
  }, [noAudit, demoSampleRun, propertyId]);

  /*
   * Krok P8F6 bod 3 — UDAJE Z KARTY NEMOVITOSTI (nalez P8-N11). Jeden dotaz
   * na fakta karty; kresli je `CasePropertyFactsCard` v sekci „Nemovitost",
   * a to I u zakazky bez proverení (tam uz cena a dispozice z naboru jsou).
   */
  React.useEffect(() => {
    if (propertyId === null) return;
    let alive = true;
    void fetchPropertyFacts(propertyId).then(
      // Starsi odpoved (i atrapa v testu) nemusi `items` mit — blok pak jen
      // nestoji, stranka se o to nesmi rozbit.
      (facts) => { if (alive) setPropertyFacts(facts ?? []); },
      () => undefined,
    );
    return () => { alive = false; };
  }, [propertyId]);

  /*
   * Krok APP3 bod 5 (nalez A14, R78 bod 3) — ROZPOR UREDNIHO A RUCNIHO UDAJE.
   *
   * Projekce uredni hodnotu nad potvrzenym zapisem NEPREPISE (spravne), ale do
   * APP3 ji mlcky zahodila a karta o rozporu nerekla nic. Ted se otevrene
   * rozpory nactou vedle faktu a makléřka u nich rozhoduje, ktery udaj plati.
   */
  const [factConflicts, setFactConflicts] = React.useState<readonly PropertyFactConflict[]>([]);
  const [conflictNote, setConflictNote] = React.useState<string | null>(null);
  const reloadFactConflicts = React.useCallback(() => {
    if (propertyId === null) return;
    void fetchPropertyFactConflicts(propertyId).then(
      (items) => setFactConflicts(items ?? []),
      () => undefined,
    );
  }, [propertyId]);
  React.useEffect(() => {
    reloadFactConflicts();
  }, [reloadFactConflicts]);

  async function resolveConflict(
    factKey: string,
    choice: 'manual' | 'registry',
  ): Promise<void> {
    if (propertyId === null) return;
    setConflictNote(null);
    try {
      await resolvePropertyFactConflict(propertyId, factKey, choice);
      setConflictNote(
        t(
          choice === 'manual'
            ? 'webAdmin.caseDetail.propertyFacts.conflictResolvedManual'
            : 'webAdmin.caseDetail.propertyFacts.conflictResolvedRegistry',
          { locale: 'cs' },
        ),
      );
      reloadFactConflicts();
      // Volba `registry` zapsala NOVY fakt — karta se musi precist znovu.
      if (choice === 'registry') {
        void fetchPropertyFacts(propertyId).then(
          (facts) => setPropertyFacts(facts ?? []),
          () => undefined,
        );
      }
    } catch {
      setConflictNote(t('webAdmin.caseDetail.propertyFacts.conflictFailed', { locale: 'cs' }));
    }
  }

  /*
   * Krok ZAK1 bod 5 — PODKLAD PANELU „CO ZBYVA ZJISTIT". Sablona naboru rika,
   * CO se ma zjistit a KDE, kontext naboru, co uz je zname (vcetne toho, co
   * vyplnila otevrena data). Obe odpovedi uz existuji pro obrazovku `/nabor`;
   * zadny novy endpoint tu nevznika. Chyba je ticha — panel pak nestoji.
   */
  React.useEffect(() => {
    if (!validCaseId) return;
    let alive = true;
    void getIntakeContext(zakazkaId).then(
      (context) => {
        if (!alive) return;
        setIntakeContext(context);
        const kind = context.checklistKind;
        if (kind === null) return;
        void getChecklistTemplate(kind).then(
          (template) => { if (alive) setIntakeTemplate(template); },
          () => undefined,
        );
      },
      () => undefined,
    );
    return () => { alive = false; };
  }, [validCaseId, zakazkaId]);

  React.useEffect(() => {
    if (!validCaseId) return;
    let alive = true;
    setRawDetail(null);
    setError(null);
    setNoAudit(false);
    loadCaseDetail(zakazkaId)
      .then((data) => {
        if (!alive) return;
        setRawDetail(data.detail);
        /*
         * Krok QAFIX14: pruh ukazky i cekani na beh se ridi BEHEM, ne tim,
         * co uzivatel prave kliknul — po refreshi (F5) zadna odpoved na
         * spusteni neexistuje a stav presto musi zustat tentyz.
         */
        setDemoSampleRun(isDemoSampleRun(data.run));
        setQueuedRunId(isRunPending(data.run.status) ? data.run.id : null);
      })
      .catch((e: Error) => {
        if (!alive) return;
        // Zakazka bez auditniho behu neni chyba — je to stav, ktery ma UI
        // pojmenovat (a nabidnout spusteni auditu), ne cervena hlaska.
        if (e instanceof NoAuditCaseError) setNoAudit(true);
        else setError(e.message);
      });
    return () => { alive = false; };
  }, [zakazkaId, reloadToken]);

  /**
   * KROK QAFIX14 bod 2 — NA BEH VE FRONTE SE CEKA, NESVITI NAVZDY.
   *
   * Dokud beh ceka nebo bezi, stranka se ho kazdych `REVIEW_POLL_MS` zepta na
   * stav (`GET /v1/audit-runs/:id`) a po dobehnuti nacte detail znovu — makler
   * tedy vysledek uvidi BEZ obnoveni stranky. Zadny `setInterval`: `useLater`
   * odlozene volani pri odchodu z obrazovky rusi (poucení MRGD7), takze na
   * odmontovane komponente uz nic nemeni stav.
   *
   * Strop pokusu je zamerny — beh, ktery se do `REVIEW_POLL_MAX` nedotahne,
   * uz neni „za chvilku hotovo" a stranka o nem prestane tvrdit cokoli; stav
   * zustane na „ve fronte" a obnoveni ho precte znovu ze serveru.
   */
  React.useEffect(() => {
    if (queuedRunId === null) return;
    let alive = true;
    let tries = 0;
    const again = (): void => {
      tries += 1;
      if (tries < REVIEW_POLL_MAX) later(tick, REVIEW_POLL_MS);
    };
    const tick = (): void => {
      void getAuditRunStatus(queuedRunId).then(
        (run) => {
          if (!alive) return;
          if (isRunPending(run.status)) {
            again();
            return;
          }
          setQueuedRunId(null);
          setReviewQueued(false);
          setDemoSampleRun(isDemoSampleRun(run));
          // Detail se slozi ze dobehnuteho behu (`reloadDetail` nize je tentyz
          // zapis — tady se na nej jeste nelze odkazat, stoji za vetvemi).
          setReloadToken((n) => n + 1);
        },
        () => {
          // Vypadek JEDNOHO dotazu neni dobehnuty beh — zkusi se znovu.
          if (alive) again();
        },
      );
    };
    later(tick, REVIEW_POLL_MS);
    return () => { alive = false; };
  }, [queuedRunId]);

  // Lidske ukoly drzi STRANKA, ne karta kokpitu: tentyz seznam potrebuje
  // hlavicka pro „Další krok: …" (viz `CockpitTab` prop `tasks`).
  const ddCaseId = rawDetail?.fixture.caseInfo.id ?? null;
  React.useEffect(() => {
    if (ddCaseId === null) return;
    let alive = true;
    void listHumanTasks(ddCaseId).then(
      (items) => {
        if (!alive) return;
        setHumanTasks(
          items.map((item) => ({
            id: item.id,
            sourceId: item.sourceId,
            taskKind: item.taskKind,
            status: item.status,
            questionKey: item.questionKeys[0] ?? null,
          })),
        );
      },
      () => { if (alive) setHumanTasks([]); },
    );
    return () => { alive = false; };
  }, [ddCaseId, humanTasksToken]);

  /*
    Krok LV1 — „Stáhnout výpis z LV" za kredit kancelare.

    O tom, jestli tlacitko vubec existuje, rozhoduje SERVER (`available`):
    priznak `LV_PURCHASE_ENABLED`, souhlas ČÚZK se sirenim (nebo vyjimka
    KNT1), prostredi WSDP a to, jestli je znamy predmet. Obrazovka si zadny
    priznak necte — jinak by mohla slibit nakup, ktery routa odmitne.
  */
  const [lv, setLv] = React.useState<LvAvailability | null>(null);
  const [lvBusy, setLvBusy] = React.useState(false);
  const [lvNote, setLvNote] = React.useState<string | null>(null);
  React.useEffect(() => {
    // Neplatne id v URL (V10) nesmi vyvolat ZADNY pozadavek na API - tutéž
    // podminku maji vsechny ostatni efekty teto obrazovky. Hooky pritom musi
    // stat PRED podminenymi returny (poradi hooku), takze se hlida uvnitr.
    if (!validCaseId) return;
    let alive = true;
    void getLvAvailability(zakazkaId)
      .then((value) => { if (alive) setLv(value); })
      .catch(() => { if (alive) setLv(null); });
    return () => { alive = false; };
  }, [zakazkaId, validCaseId]);
  const buyLv = (): void => {
    const price = lv?.priceCzk ?? 0;
    // Vydej penez se potvrzuje. Cena je v otazce, aby makler potvrzoval
    // CASTKU, ne „neco".
    if (!window.confirm(t('webAdmin.caseDetail.remaining.lvBuyConfirm', { locale: 'cs', values: { price: String(price) } }))) return;
    setLvBusy(true);
    setLvNote(t('webAdmin.caseDetail.remaining.lvBuying', { locale: 'cs' }));
    void purchaseLv(zakazkaId)
      .then(async (outcome) => {
        setLvNote(
          outcome.conflicts.length > 0
            ? t('webAdmin.caseDetail.remaining.lvConflicts', { locale: 'cs' })
            : outcome.chargedCzk === 0
              ? t('webAdmin.caseDetail.remaining.lvFree', { locale: 'cs' })
              : null,
        );
        // Udaje z vypisu jsou na karte i v naboru — obojí se musi prekreslit.
        const [facts, context, availability] = await Promise.all([
          fetchPropertyFacts(zakazkaId).catch(() => null),
          getIntakeContext(zakazkaId).catch(() => null),
          getLvAvailability(zakazkaId).catch(() => null),
        ]);
        if (facts !== null) setPropertyFacts(facts);
        if (context !== null) setIntakeContext(context);
        if (availability !== null) setLv(availability);
      })
      .catch((error: unknown) => {
        const code = error instanceof LvPurchaseError ? error.code : null;
        setLvNote(
          t(
            code === 'lv/credit_insufficient'
              ? 'webAdmin.caseDetail.remaining.lvNoCredit'
              : code === 'lv/eid_required'
                ? 'webAdmin.caseDetail.remaining.lvEidRequired'
                : 'webAdmin.caseDetail.remaining.lvFailed',
            { locale: 'cs' },
          ),
        );
      })
      .finally(() => { setLvBusy(false); });
  };
  // Neplatne id v URL (V10) i cizi/neexistujici zakazka (TOKYFIX2 P4/3)
  // koncí TOUTEZ obrazovkou — a VZDY pred kostrou karty, aby se o zakazce,
  // kterou uzivatel videt nesmi, nevykreslil ani jeden udaj.
  if (!validCaseId || caseMissing) {
    return (
      <div className="rh-card" style={{ padding: 16 }} data-testid="case-not-found">
        <h1 className="text-2xl font-bold mb-2">{CASE_TEXTS.notFoundTitle}</h1>
        <p className="mb-2 text-sm text-gray-600">{CASE_TEXTS.notFoundDetail}</p>
        <Link to="/zakazky" className="text-sm underline">{CASE_TEXTS.notFoundBack}</Link>
      </div>
    );
  }
  if (error !== null) {
    return (
      <p role="alert" data-testid="case-load-error" style={{ color: 'var(--rh-danger)' }}>
        {CASE_TEXTS.loadError}: {error}
      </p>
    );
  }
  /*
   * Zakazka BEZ proverení neni chyba ani prazdna stranka (krok UXV4a2 bod 4,
   * nalez FUNK1 bod 1): `rawDetail` zustane `null` a stranka se vykresli bez
   * sekce „Prověření" — s hlavickou, postupem a kartou „Spustit prověření".
   * Dokud ale jeste nevime, jestli beh existuje, se necaka na nic jineho.
   */
  if (rawDetail === null && !noAudit) {
    return <p style={{ color: 'var(--rh-text-muted)' }}>{CASE_TEXTS.loading}</p>;
  }

  // Odpoved maklere zapisuje `POST /v1/duediligence/cases/:id/answers` (krok
  // U1) primo do DB — po ulozeni se detail proste nacte znovu, zadny lokalni
  // prekryv (drive lib/case-answer-overlay.ts nad ulozistem prohlizece).
  const reloadDetail = (): void => { setReloadToken((n) => n + 1); };

  /**
   * Krok UXV4a2 bod 4 — SPUSTENI PRVNIHO PROVĚŘENÍ. Jde to TOUTEZ cestou jako
   * „Prověřit znovu" (`recheckCaseAudit` → `POST /v1/cases/:id/audit-runs`,
   * krok S1) — jedine misto v UI, ktere beh spousti.
   *
   * DRUH NEMOVITOSTI SE NEHADA (ROZHODNUTI FUNK1-1, prenesene do podoby v4).
   * Server ho u PRVNIHO behu vyzaduje — bez nej konci 400 a makler by videl
   * jen chybu. Predvyplni se z `properties.kind`, kdyz ho zakazka zna
   * (`complex` protejsek nema), jinak ho makler vybere; dokud neni vybrany,
   * tlacitko nejde zmacknout.
   */
  const kindFromProperty: GateCaseKind | '' =
    propertyKind === null ? '' : (DD_KIND_BY_PROPERTY_KIND[propertyKind] ?? '');
  const reviewKind: GateCaseKind | '' = pickedReviewKind ?? kindFromProperty;

  /**
   * Krok PRF1 (bod 4) — „Upravit" z nabidky „⋯". Sekce „Zadání" se otevre
   * sama (`defaultOpen`), ale muze byt pod ohybem: bez posunuti to na preview
   * vypadalo, ze se nic nestalo. Posun az po prekresleni (sekce v DOM jeste
   * neni), stejnym postupem jako `openTabAndScrollTo`.
   */
  const openEdit = (): void => {
    setEditOpen(true);
    setTab(CASE_TAB_DEFAULT);
    later(() => {
      document.querySelector('[data-testid="case-edit-form"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  /*
   * Krok ZDARMA1: text potvrzeni se RIDI UROVNI (u bezplatne zadna zminka
   * o kreditu) a demo ma prednost pred obojim — pravidlo zije v
   * `reviewConfirmBodyFor`, aby ho obe misto spusteni cetla stejne.
   */
  const reviewConfirmBody = reviewConfirmBodyFor(reviewLevel, publicDemo, reviewOptions);

  const startReview = (level: ReviewLevel = reviewLevel): void => {
    const query = propertyAddress ?? kase?.title ?? '';
    if (reviewKind === '' || startingReview || reviewQueued) return;
    setConfirmingReview(false);
    setStartingReview(true);
    setStartReviewError(null);
    setStartReviewNotice(null);
    /*
     * Krok NOC6 bod 6: PREDMET ZAKAZKY, ne prazdne zadani. Zivy beh
     * 14. 10. 2026 (Petrovice, 8 parcel LV 72) poslal odsud `parcels: []`
     * a `cadastralArea: null`; kolektor pak hlasil „zadna parcela nezamcena -
     * preskakuji environmentalni adaptery" a beh prisel o povodne, radon
     * i sesuvy. Parcely a k.u. jdou ze TEHOZ resolveru, jaky kresli hlavicku
     * a formular predmetu (`areaRows` / `caseHeader`) — zadny druhy vypocet.
     * LV v UI neni; prebere ho server z posledniho behu teze nemovitosti
     * (`inheritRunSubject`).
     */
    void recheckCaseAudit({
      caseId: zakazkaId,
      kind: reviewKind,
      reportLevel: level,
      addressQuery: query,
      parcels: areaList.map((row) => row.parcelLabel.replace(/^st\.\s*/, '')),
      cadastralArea: header?.cadastralAreaName ?? null,
      landRegistryNumber: null,
    })
      .then((run) => {
        setStartingReview(false);
        // Zamek drzi dal — beh je objednany, druhy klik by byl druha platba.
        setReviewQueued(true);
        /*
         * KROK QAFIX14 bod 2 — ODPOVED SE ZPRACUJE, NEZAHODI.
         *
         * Do QAFIX14 tady stalo `.then(() => …)`: stranka napsala „Prověření
         * je ve frontě…" bez ohledu na to, co server vratil. V DEMU pritom
         * server nic nezaradi — vrati HOTOVY ukazkovy beh (status `DONE`,
         * `acceptance.status = DEMO_SAMPLE`), takze hlaska o fronte byla lez
         * a uzivatel ji mel na obrazovce natrvalo (cilene QA 22. 10., nalez 5).
         */
        const sample = isDemoSampleRun(run);
        setDemoSampleRun(sample);
        if (isRunPending(run.status)) {
          setQueuedRunId(run.id);
          setStartReviewNotice(t('webAdmin.caseDetailV4.startReviewQueued', { locale: 'cs' }));
          return;
        }
        // Vysledek uz existuje (demo ukazka i beh, ktery dobehl hned) —
        // stranka ho ukaze TED, bez obnoveni a bez cekani na frontu.
        setQueuedRunId(null);
        setStartReviewNotice(
          t(
            sample
              ? 'webAdmin.caseDetailV4.startReviewSampleReady'
              : 'webAdmin.caseDetailV4.reviewDoneNotice',
            { locale: 'cs' },
          ),
        );
        reloadDetail();
      })
      .catch((cause: unknown) => {
        setStartingReview(false);
        setStartReviewError(
          t('webAdmin.caseDetailV4.startReviewFailed', {
            locale: 'cs',
            values: { reason: cause instanceof Error ? cause.message : String(cause) },
          }),
        );
      });
  };

  /* ======================================================================
   * Krok UXV4a2 — DETAIL ZAKAZKY JE JEDNA STRANKA (predloha
   * `docs/design/ux-v4/png/Zakazka.png`).
   *
   * Dvanact zalozek (`CASE_PRIMARY_TABS` + „⋯") ZANIKLO: predloha ma jednu
   * stranku s rozbalovacimi sekcemi. Nic se ale neztratilo — sekce, ktere se
   * do rozbaleni nevejdou (cely audit, inzerce, poptavky, terminy, prohlidky,
   * nabidky, smlouva, naklady, provize), jsou dal na SVE ADRESE `?sekce=<id>`
   * a vykresli se jako PODSTRANKA s vlastni navigacni listou. Vede k nim
   * nabidka „⋯" a odkazy ze sekci (ROZHODNUTI UXV4a2-PODSTRANKA v reportu).
   *
   * Tahle cast se pocita i BEZ `rawDetail` — vsechno v ni stoji na zakazce
   * samotne (`GET /v1/cases/:id`, strany, naklady, historie).
   * ====================================================================== */

  const nameTexts: CaseNameTexts = {
    untitled: t('webAdmin.kanban.untitled', { locale: 'cs' }),
    untitledHint: (id) => t('webAdmin.kanban.untitledHint', { locale: 'cs', values: { id } }),
  };
  const subject = rawDetail === null ? null : caseSubjectOf(rawDetail);
  const gateKind = subject === null ? null : gateCaseKindOf(subject);
  /**
   * Krok NOC4 bod 3 (nalez N21) — DRUH NEMOVITOSTI MA NA STRANCE JEDNU
   * HODNOTU.
   *
   * Formular „Upravit" bral jen druh ZJISTENY z proverení (`gateKind`), takze
   * u zakazky bez behu psal „—", zatimco karta „Prověřit nemovitost" na teze
   * strance ukazovala „Byt" z `properties.kind`. Oba selecty proto ctou tutez
   * hodnotu: zjisteny druh, a dokud zadny neni, to, co nabizi karta (vcetne
   * rucni volby maklere).
   */
  const caseKind: GateCaseKind | '' = gateKind ?? reviewKind;
  const review = rawDetail === null ? null : caseReviewAreas(rawDetail, gateKind);
  const header = rawDetail === null ? null : caseHeader(rawDetail);
  /**
   * KROK QAFIX14b bod 2 — HLAVICKA ZAKAZKY NIKDY NEMLUVI O UKAZCE.
   *
   * V demu neni auditni pripad zakazky, ale UKAZKA ze seedu (`acceptance.status
   * = DEMO_SAMPLE`, krok QAFIX14): jina nemovitost, jiny druh, jine parcely.
   * Detail z nej proto NESMI brat nazev, druh ani predmet — zakazce „Rodinný
   * dům, Sojovice" delal nadpis „Pozemek · prodej" a kartu „Dokončete
   * prověření — Pozemek" (cilene QA 22. 10. 2026).
   *
   * Nazev a druh maji vlastni zdroj, ktery zakazce opravdu patri: `cases.title`
   * / adresa nemovitosti a `properties.kind` — presne to, z ceho stranka zije
   * u zakazky BEZ proverení. Ostre kancelare se tim nemeni: tam zadny ukazkovy
   * beh neni a `displayHeader` je `header` auditniho pripadu.
   *
   * Vypocty prevereni (brany, oblasti, ukoly) dal stoji na auditnim pripade —
   * obsah, ktery se v demu zobrazi, JE ukazkovy a prepocitavat ho na druh
   * zakazky by znamenalo tvrdit cisla, ktera nikdo nespocital.
   */
  const displayHeader = demoSampleRun ? null : header;
  const displaySubject =
    demoSampleRun && kindFromProperty !== ''
      ? resolveCaseSubject({
          declaredKind: kindFromProperty,
          parcelCount: null,
          hasBuildingEvidence: false,
        })
      : subject;
  /*
   * Parcely, katastralni uzemi a vymera jdou ze SDILENEHO resolveru
   * `areaRows` / `caseHeader` (@reos/reports) — tedy z tehoz zdroje, jaky
   * pouziva PDF reportu.
   *
   * POZOR NA VYMERU V HLAVICCE: predloha pise „2 078 m²", protoze jeji
   * zakazka ma POTVRZENY rozsah. Hlavicka proto bere `header.totalAreaM2` =
   * `confirmedSubjectTotalM2`, coz je `null`, dokud neni ověřena oblast „Co se
   * prodává" (Milnik 1 bod 7). Soucet KN vymer tu byt NESMI: u Lysé se fakta
   * rozchazeji (2 078 vs 2 048 m²) a hlavicka by jedno z cisel vydala za
   * potvrzenou vymeru predmetu prodeje. Soucet patri do karty „Výměry".
   */
  const areaList = rawDetail === null ? [] : areaRows(rawDetail);
  const areaTotal = displayHeader?.totalAreaM2 ?? null;
  /*
   * Krok UXV4a2 — TATAZ FUNKCE, JAKOU VOLA DNESEK I SEZNAM ZAKAZEK
   * (`caseProgressView`): procenta, veta „co chybí" i pet kroku. Rozpracovanost
   * kroku „Prověření" umi zmerit JEN detail (zna brany), proto ji pridava jen
   * on — ROZHODNUTI UXV4a-PROCENTA v `lib/case-progress.ts`.
   */
  const progress = caseProgressView(kase?.stage ?? null,
    review === null ? {} : { review: { done: review.verified, total: review.total } });
  const lastEvents = caseEventLines(timeline, 3);
  const persons = casePersonsOf(participants, partyNames);
  const openedAt = kase?.openedAt ?? header?.openedAt ?? null;

  /*
   * Nazev zakazky BEZ proverení: `cases.title` casto nese zastupny text s
   * internim id („Nemovitost 01a05672"), ktery se makleri neukazuje nikdy
   * (krok O8). Nahradou je ADRESA NEMOVITOSTI — tutez, kterou uz stranka
   * nacetla kvuli spusteni proverení; teprve pak „Bez názvu".
   */
  const addressFallback =
    kase?.propertyId == null || propertyAddress === null
      ? new Map<string, string>()
      : new Map([[kase.propertyId, propertyAddress]]);
  const humanTitle =
    displayHeader === null
      ? (kase === null ? CASE_TEXTS.loading : caseDisplayName(kase, addressFallback, nameTexts).text)
      : displayHeader.parcelCount !== null && displayHeader.parcelCount > 1
        ? t('webAdmin.caseDetail.v2.titleParcels', {
            locale: 'cs',
            values: { address: displayHeader.address, count: displayHeader.parcelCount },
          })
        : displayHeader.address;

  /*
   * Krok QAFIX14b bod 2: parcely, k.u. i vymera jsou PREDMET auditniho pripadu
   * — u ukazkoveho behu tedy predmet UKAZKY. V podtitulku zakazky pak stalo
   * „k.ú. Úmyslovice" u domu v Sojovicich; radeji nic nez cizi parcela.
   */
  const titleParts = (displayHeader === null
    ? []
    : [
        areaList.length > 0
          ? t('webAdmin.caseDetail.v2.parcelsLine', {
              locale: 'cs',
              values: { parcels: areaList.map((row) => row.parcelLabel).join(', ') },
            })
          : null,
        displayHeader.cadastralAreaName === null
          ? null
          : t('webAdmin.caseDetail.v2.cadastralArea', { locale: 'cs', values: { name: displayHeader.cadastralAreaName } }),
      ]
  ).filter((part): part is string => part !== null && part !== '');

  const kindBadge = [
    displaySubject?.label ?? null,
    kase !== null ? CASE_TYPE_LABELS[kase.type as KanbanCaseType]?.toLowerCase() ?? null : null,
  ]
    .filter((part): part is string => part !== null && part !== undefined)
    .join(' · ');

  /** Predloha: „Pozemek · prodej · Lysá nad Labem" pod velkym nadpisem. */
  const subtitle = (
    <>
      {/* Krok PRU1: ukazkova zakazka pruvodce je VZDY jasne oznacena. */}
      {kase?.isDemo === true ? (
        <>
          <V4Pill tone="accent" testId="case-demo-badge">
            {t('webAdmin.guide.demo.badge', { locale: 'cs' })}
          </V4Pill>{' '}
        </>
      ) : null}
      {kindBadge === '' ? null : (
        <span data-testid="case-kind-badge">{kindBadge}</span>
      )}
      {/*
        Krok PRF1 (vada V1, bod 3): hlavicka pojmenuje IDENTIFIKOVANY PREDMET
        (parcely + katastralni uzemi), aby makler poznal zamenu hned — pri
        pruchodu 13. 10. se zakazka navazala na cizi parcelu v jine obci a
        poznalo se to az z faktu uvnitr proverení.
      */}
      {titleParts.length > 0 ? (
        <span data-testid="case-subject-line">
          {`${kindBadge === '' ? '' : ' · '}${titleParts.join(' · ')}`}
        </span>
      ) : null}
      {areaTotal !== null ? (
        <>
          {' · '}
          <b className="rh-a2-num">{m2(areaTotal)}</b>
        </>
      ) : null}
      {displaySubject?.declaredDiffers === true
        ? ` · ${t('webAdmin.caseSubject.declaredNote', { locale: 'cs', values: { label: displaySubject.declaredLabel } })}`
        : ''}
    </>
  );

  /* ---------------------------------------- navigacni lista predlohy */
  const nav = (
    <V4NavBar
      testId="case-nav"
      back={
        <Link to="/zakazky" data-testid="case-breadcrumb-list">
          <ChevronLeft size={24} strokeWidth={2.4} aria-hidden="true" />
          {CASE_TEXTS.breadcrumbRoot}
        </Link>
      }
      actions={
        <CaseMoreMenu
          caseId={zakazkaId}
          onOpenTab={setTab}
          onShare={() => { setTab('dokumenty'); }}
          onEdit={openEdit}
          {...(kase !== null && kase.allowedTransitions.includes(CANCELLED_STAGE) && kase.canUpdate
            ? { onArchived: () => { void navigate({ to: '/zakazky' }); } }
            : {})}
        />
      }
    />
  );

  /* ------------------------------------------------- hero fotka predlohy */
  const hero = (
    <V4PhotoCard
      variant="hero"
      height={176}
      image={
        rawDetail === null ? (
          <PropertyImage
            photoUrl={cover?.thumbUrl ?? null}
            propertyId={cover?.propertyId ?? null}
            lat={cover?.lat ?? null}
            lon={cover?.lon ?? null}
            size={{ width: 716, height: 352 }}
            testIdPrefix="case-hero"
          />
        ) : (
          <CaseHero
            inset
            ddCaseId={rawDetail.fixture.caseInfo.id}
            fallback={{
              coverUrl: cover?.thumbUrl ?? null,
              lat: cover?.lat ?? null,
              lon: cover?.lon ?? null,
              propertyId: cover?.propertyId ?? null,
            }}
          />
        )
      }
      {/* Predloha: stitek vlevo dole — „3 parcely · 2 078 m²". Soucet KN vymer
          tu smi stat, protoze se o nem NETVRDI, ze je potvrzena vymera predmetu
          prodeje (to je `areaTotal` v podtitulku, viz komentar vyse). */
      ...(areaList.length > 0
        ? {
            chip: t('webAdmin.caseDetailV4.heroChip', {
              locale: 'cs',
              values: {
                count: areaList.length,
                area: m2(areaList.reduce((sum, row) => sum + row.areaM2, 0)),
              },
            }),
          }
        : {})}
      button={
        /*
          Krok UXP5 bod 10: tlacitko „Fotky a mapa" vedlo na `?sekce=geometrie`,
          kde ZADNE fotky nejsou — galerie nemovitosti zije v sekci Dokumenty
          (`PhotosCard`, ROZHODNUTI O6). Nataceni 11. 10. to videlo jako
          „nic neotevre". Ted otevre sekci, kde fotky opravdu jsou; mapa je
          hero obrazek nad tlacitkem.
        */
        <button
          type="button"
          className="rh-v4-photo__btn"
          aria-label={t('webAdmin.caseDetailV4.photoAction', { locale: 'cs' })}
          onClick={() => { setTab('dokumenty'); }}
          data-testid="case-hero-open"
        >
          <Camera size={20} strokeWidth={2} aria-hidden="true" />
        </button>
      }
    />
  );

  /* ------------------------------------- sekce, ktere nepotrebuji audit */
  const sectionIcon = (tone: string, icon: React.ReactNode): React.JSX.Element => (
    <span className={`rh-v4-ico rh-v4-ico--sm rh-v4-ico--${tone}`} aria-hidden="true">
      {icon}
    </span>
  );

  const firstPerson = persons[0];
  /** Krok ZAK1 bod 5: rozpad „vyplneno samo / zbyva" — pocita se jednou. */
  const remaining = caseRemaining(intakeTemplate, intakeContext);
  /*
    Krok ZAK1 bod 2: stav predvyplneni z otevrenych dat. Cte se z casove osy,
    kterou stranka uz ma — zadny dalsi dotaz a zadna druha pravda o tom, kdy
    se naposledy nacitalo.
  */
  const prefill = prefillStatus(timeline);
  const reloadPrefill = (): void => {
    void requestPropertyPrefill(zakazkaId)
      .then(() => listCaseTimeline(zakazkaId))
      .then((items) => {
        setTimeline(items);
        /*
         * Krok APP3 bod 5: beh nad potvrzenym udajem muze otevrit ROZPOR.
         * Bez tohohle ctení by ho karta ukazala az po obnoveni stranky.
         * (Beh je ve workeru, takze prvni cteni jeste nemusi nic videt —
         * stav se dorovna pri dalsim otevreni zakazky.)
         */
        reloadFactConflicts();
      })
      .catch(() => undefined);
  };
  const commonSections: readonly CaseV4Section[] = [
    /*
      Krok P8F6 bod 3 (nalez P8-N11) — UDAJE Z KARTY NEMOVITOSTI. Stoji ve
      `commonSections`, tedy v OBOU navratech stranky: po naboru zakazka
      prověření jeste nema, a presne tam makler cenu, dispozici a jednotku
      hledal. Sekce se nekresli, dokud karta zadny z tech udaju nenese.
    */
    ...(casePropertyFactRows(propertyFacts).length === 0 && factConflicts.length === 0
      ? []
      : [
          {
            id: 'udaje-nemovitosti',
            icon: sectionIcon('', <Building2 size={18} strokeWidth={2} />),
            label: t('webAdmin.caseDetail.propertyFacts.heading', { locale: 'cs' }),
            sub: casePropertyFactRows(propertyFacts)
              .map((row) => `${row.label}: ${row.value ?? ''}`)
              .slice(0, 2)
              .join(' · '),
            body: (
              <>
                <CasePropertyFactsCard
                  facts={propertyFacts}
                  conflicts={factConflicts}
                  onResolveConflict={(factKey, choice) => void resolveConflict(factKey, choice)}
                />
                {conflictNote !== null && (
                  <p role="status" data-testid="case-fact-conflict-note-result">
                    {conflictNote}
                  </p>
                )}
              </>
            ),
          },
        ]),
    /*
      Krok ZAK1 bod 5 — CO ZBYVA ZJISTIT. Druha strana predvyplneni: nahore
      „Vyplněno samo: X polí · zbývá Y", pod tim skupiny podle zdroje
      s proklikem do naboru. Sekce stoji jen kdyz uz o naboru teto zakazky
      neco vime (sablona dosla) — jinak by ukazovala prazdny ramec.
    */
    ...(remaining.groups.length === 0 && prefill === null
      ? []
      : [
          {
            id: 'co-zbyva',
            icon: sectionIcon('amber', <ListChecks size={18} strokeWidth={2} />),
            label: t('webAdmin.caseDetail.remaining.heading', { locale: 'cs' }),
            sub: remaining.groups
              .map(
                (group) =>
                  `${t(`webAdmin.caseDetail.remaining.source.${group.source}` as MessageKey, { locale: 'cs' })} ${String(group.count)}`,
              )
              .slice(0, 3)
              .join(' · '),
            body: (
              <CaseRemainingCard
                caseId={zakazkaId}
                template={intakeTemplate}
                context={intakeContext}
                prefill={prefill}
                {...(kase?.propertyId == null ? {} : { onReload: reloadPrefill })}
                lv={lv}
                lvNote={lvNote}
                {...(lvBusy ? {} : { onBuyLv: buyLv })}
              />
            ),
          },
        ]),
    {
      id: 'lide',
      icon: sectionIcon('', <Users size={18} strokeWidth={2} />),
      label: t('webAdmin.caseDetail.propertyCard.peopleHeading', { locale: 'cs' }),
      sub:
        firstPerson === undefined
          ? t('webAdmin.caseDetailV4.peopleSubNone', { locale: 'cs' })
          : firstPerson.displayName === ''
            ? (brokerName ?? t('webAdmin.caseDetailV4.peopleSubNone', { locale: 'cs' }))
            : firstPerson.displayName,
      body: (
        <>
          {persons.map((person) => {
            const phone = phoneByParty.get(person.partyId);
            if (phone === undefined) return null;
            return (
              <V4Row
                key={`phone-${person.partyId}`}
                testId={`case-person-phone-${person.partyId}`}
                title={phone}
                sub={t('webAdmin.caseDetailV4.phoneMobile', { locale: 'cs' })}
                right={
                  <a
                    href={`tel:${phone.replace(/\s/g, '')}`}
                    className="rh-v4-btn rh-v4-btn--chip"
                    aria-label={t('webAdmin.caseDetailV4.callLabel', {
                      locale: 'cs',
                      values: { name: person.displayName },
                    })}
                    data-testid={`case-person-call-${person.partyId}`}
                  >
                    <Phone size={16} strokeWidth={2} aria-hidden="true" />
                    {t('webAdmin.caseDetailV4.call', { locale: 'cs' })}
                  </a>
                }
              />
            );
          })}
          {openedAt === null ? null : (
            <CasePeopleCard brokerName={brokerName} openedAt={openedAt} persons={persons} />
          )}
        </>
      ),
    },
    /*
      Krok UXP1 (nalez P1-1): cenova predstava, identita a zadani z prvniho
      hovoru se ukladaji do `cases.description`, ale detail je NEKRESLIL —
      makler je po zalozeni nikde nenasel. Sekce stoji jen kdyz popis existuje.
    */
    ...(kase === null || (!editOpen && (kase.description == null || kase.description.trim() === ''))
      ? []
      : [
          {
            id: 'zadani',
            icon: sectionIcon('grey', <FileText size={18} strokeWidth={2} />),
            label: editOpen
              ? t('webAdmin.caseDetailV4.editHeading', { locale: 'cs' })
              : t('webAdmin.caseDetailV4.briefHeading', { locale: 'cs' }),
            sub: '',
            /* Krok PRF1 (bod 4): „Upravit" sekci OTEVRE — jinak se formular
               schova do zavrene sekce a akce vypada jako by nic neudelala. */
            defaultOpen: editOpen,
            body: editOpen ? (
              <CaseEditForm
                kase={kase}
                subject={{
                  addressQuery: propertyAddress ?? kase.title,
                  cadastralArea: header?.cadastralAreaName ?? '',
                  parcels: areaList.map((row) => row.parcelLabel),
                  kind: caseKind,
                }}
                onSaved={(updated) => {
                  setKase(updated);
                  setEditOpen(false);
                }}
                onSubjectChanged={() => {
                  setEditOpen(false);
                  // Novy beh nad opravenym predmetem — detail se po nem nacte
                  // znovu tutez cestou jako po „Prověřit znovu" (UX2).
                  later(() => { reloadDetail(); }, 4000);
                }}
                onCancel={() => { setEditOpen(false); }}
              />
            ) : (
              <p
                className="text-sm m-0"
                style={{ whiteSpace: 'pre-line', color: 'var(--rh-text-muted)' }}
                data-testid="case-brief-description"
              >
                {kase.description}
              </p>
            ),
          } satisfies CaseV4Section,
        ]),
    {
      id: 'dokumenty',
      icon: sectionIcon('grey', <FileText size={18} strokeWidth={2} />),
      label: t('webAdmin.caseDetailV4.documentsHeading', { locale: 'cs' }),
      sub:
        rawDetail === null
          ? ''
          : t('webAdmin.caseDetailV4.documentsSub', {
              locale: 'cs',
              values: { count: evidenceRows(rawDetail).length },
            }),
      body: (
        <>
          <V4Row
            testId="case-open-dokumenty"
            title={t('webAdmin.caseTabs.dokumenty.label', { locale: 'cs' })}
            chevron
            onClick={() => { setTab('dokumenty'); }}
          />
          <V4Row
            testId="case-open-smlouva"
            title={t('webAdmin.caseTabs.smlouva.label', { locale: 'cs' })}
            chevron
            onClick={() => { setTab('smlouva'); }}
          />
          {/*
            Krok PIL1 (vada V14): smlouva sla zalozit jen ze seznamu Smlouvy,
            takze makléřka musela zakazku vybirat znovu (snimek `GEN1/6-01`).
            Odkaz nese `?zakazka=`, ktere `/smlouvy/nova` uz umi (krok UXS1).
          */}
          <V4Row
            title={t('webAdmin.caseDetailV4.newContract', { locale: 'cs' })}
            chevron
            render={(className, content) => (
              <Link
                to="/smlouvy/nova"
                search={{ zakazka: zakazkaId }}
                className={className}
                data-testid="case-new-contract"
              >
                {content}
              </Link>
            )}
          />
        </>
      ),
    },
    /*
      Krok NOC3 bod 1 (nalez N18) — INZERCE JE TRVALY VSTUP, NE JEN „DALSI KROK".
      Odkaz na `/zakazky/:id/inzerat` stal do NOC3 JEN v karte „zakazku zatim
      nikdo neproveril"; jak makler spustil provereni, karta zmizela a inzerat
      se z UI nedal otevrit (jen rucnim prepisem URL). Sekce stoji v
      `commonSections`, tedy v OBOU navratech stranky (bez provereni i s nim),
      takze plati ve vsech stavech zakazky vcetne NEEDS_REVIEW.
    */
    {
      id: 'inzerce',
      icon: sectionIcon('grey', <Megaphone size={18} strokeWidth={2} />),
      label: t('webAdmin.caseTabs.inzerce.label', { locale: 'cs' }),
      sub: t('webAdmin.listingDraft.nextStep.action', { locale: 'cs' }),
      body: (
        <>
          <V4Row
            title={t('webAdmin.listingDraft.nextStep.action', { locale: 'cs' })}
            chevron
            render={(className, content) => (
              <Link
                to="/zakazky/$id/inzerat"
                params={{ id: zakazkaId }}
                className={className}
                data-testid="case-open-inzerat"
              >
                {content}
              </Link>
            )}
          />
          <V4Row
            testId="case-open-inzerce"
            title={t('webAdmin.caseTabs.inzerce.label', { locale: 'cs' })}
            chevron
            onClick={() => { setTab('inzerce'); }}
          />
        </>
      ),
    },
    {
      id: 'penize',
      icon: sectionIcon('ok', <CreditCard size={18} strokeWidth={2} />),
      label: t('webAdmin.caseTabGroups.penize.label', { locale: 'cs' }),
      sub:
        formatMoney(costs?.commission ?? null) === null
          ? t('webAdmin.caseDetailV4.moneySubNone', { locale: 'cs' })
          : t('webAdmin.caseDetailV4.moneySub', {
              locale: 'cs',
              values: { commission: formatMoney(costs?.commission ?? null) ?? '' },
            }),
      body: (
        <CaseMoneyCard
          commission={costs?.commission ?? null}
          costTotals={costs?.totalsByCurrency ?? []}
          loading={costsLoading}
          onOpenMoney={() => { setTab('provize'); }}
        />
      ),
    },
    {
      id: 'historie',
      icon: sectionIcon('muted', <Clock size={18} strokeWidth={2} />),
      label: t('webAdmin.caseTabGroups.historie.label', { locale: 'cs' }),
      sub:
        lastEvents[0] === undefined
          ? t('webAdmin.caseDetailV4.historySubNone', { locale: 'cs' })
          : t('webAdmin.caseDetailV4.historySub', { locale: 'cs', values: { text: lastEvents[0].text } }),
      body: (
        <>
          {/*
            Krok SEZ1 bod 2 (R.B. 29. 10., Realman bod 9): „Zapsat" stoji
            NAD historii, do ktere zapis spadne — makler po telefonu zapise
            jednani nebo „nezveda" a hned to vidi v „Poslední dění". Tvar
            zapisu i cil (historie zakazky / ukol s terminem) resi
            `lib/record-activity.ts`, viz ROZHODNUTI SEZ1-ZAPIS.
          */}
          <div className="rh-a2-actions" style={{ marginBottom: 8 }}>
            <RecordActivityButton
              target={{ kind: 'case', caseId: zakazkaId }}
              onRecorded={() => {
                void listCaseTimeline(zakazkaId)
                  .then((items) => { setTimeline(items); })
                  .catch(() => undefined);
              }}
            />
          </div>
          <CaseLastActivityCard
            lines={lastEvents}
            formatWhen={(iso) => czDateTime(iso)}
            onOpenHistory={() => { setTab('aktivity'); }}
          />
        </>
      ),
    },
  ];

  const screen = (nextStep: React.ReactNode, sections: readonly CaseV4Section[]): React.JSX.Element => (
    <CaseDetailV4
      nav={nav}
      title={humanTitle}
      subtitle={subtitle}
      hero={hero}
      progress={progress}
      /*
       * Krok QAFIX11 bod 1 (nalez V5): krok pruhu postupu, ktery ma sekci
       * (dnes „Inzerce"), otevre tutez sekci jako radek v obsahu — pres
       * `setTab`, tedy pres `?sekce=` v URL, ne vlastni cestou.
       */
      onOpenSection={(section) => { if (isCaseTabId(section)) setTab(section as CaseTabId); }}
      /*
       * Krok UXF4 bod 3 (nalez #43): druhy ukazatel „Podklady N z M". Cisla
       * jdou z TEHOZ vypoctu (`caseReviewAreas`), jaky uz kresli sekce
       * „Prověření" a jaky krmi rozpracovanost kroku v procentech — dve
       * kopie by rekly dve ruzna cisla presne o tom, co GPT vytykal.
       */
      {...(review === null ? {} : { materials: { done: review.verified, total: review.total } })}
      /*
       * Krok UXP4b bod 1 (nalez P2-5): deset fazi zakazky je ROZBALENI teto
       * jedine karty postupu, ne druha karta s vlastnim procentem. Bez faze
       * zakazky (`stage`) se cesta nevykresluje — vymyslet ji z niceho by
       * znamenalo tvrdit o zakazce neco, co o ni nevime.
       */
      {...(kase?.stage === undefined || kase.stage === null || journey === null ? {} : { journey })}
      /*
       * Krok NOC3 bod 1 (nalez N18): ve „Všech 10 fází" je faze Inzerce
       * KLIKACI — vede na rozpracovany inzerat zakazky. Klic je `stage`
       * z `KANBAN_STAGE_ORDER` (`listing`), ne vymysleny nazev.
       */
      journeyActions={{
        listing: (
          <Link
            to="/zakazky/$id/inzerat"
            params={{ id: zakazkaId }}
            className="rh-v4-btn rh-v4-btn--chip"
            data-testid="case-journey-listing-open"
          >
            {t('webAdmin.listingDraft.nextStep.action', { locale: 'cs' })}
          </Link>
        ),
      }}
      nextStep={nextStep}
      sections={sections}
    />
  );

  /* ------------------------------------------------------------------
   * Krok UXP5b bod 1 — OBSAH SEKCI OBCHODNI FAZE (`CASE_TABS_WITHOUT_AUDIT`).
   *
   * Stoji TADY, nad vetvi „zakazka bez proverení", protoze nic z nej nepotrebuje
   * `rawDetail` — jen zakazku, nemovitost a doklady. Do `tabContent` nize se
   * vlozi tentyz objekt, takze podstranka ma obsah na JEDNOM miste.
   *
   * Nataceni 11. 10.: makler zalozil zakazku, otevrel kartu „Peníze" → URL
   * `?sekce=provize` se prepnula, ale stranka zustala na prehledu (vetev
   * `rawDetail === null` se vracela driv, nez se podstranka vubec vykreslila)
   * a na fakturu provize se ze zakazky nedostal.
   * ------------------------------------------------------------------ */
  const businessTabContent: Partial<Record<CaseTabId, React.JSX.Element>> = {
    // Krok UI3: karta „Jak se inzerat dari" (statistiky, report pro majitele,
    // import CSV) - jadro z SR3 uz existovalo, chybela jen tahle obrazovka.
    // Krok LIST1: stav inzeratu na portalech + nabidka po rezervaci/prodeji
    // STOJI NAD statistikami — kdyz je nemovitost prodana, cisla uz nerozhoduji.
    inzerce: (
      <>
        <ListingDealBlock caseId={zakazkaId} />
        <AdvertisingPerformanceCard caseId={zakazkaId} />
      </>
    ),
    // Krok CRM1, bod 4: zalozka "Poptavky" neni placeholder - seznam zajemcu
    // o tuto nemovitost (filtrovano podle `properties.id`, jinak podle zakazky).
    poptavky: <CaseDemandsPanel propertyId={propertyId} caseId={zakazkaId} />,
    // Krok CRM3, zadani bod 2: zalozka "Terminy" neni placeholder - panel
    // pro vytvoreni udalosti v kalendari navazane na tuto zakazku.
    terminy: <CaseEventsPanel caseId={zakazkaId} caseTitle={header?.location ?? undefined} />,
    prohlidky: <PreparingSection copy={CASE_TAB_PREPARING.prohlidky} compact />,
    nabidky: <PreparingSection copy={CASE_TAB_PREPARING.nabidky} compact />,
    // POZOR na `zakazkaId` vs. `caseId`: `caseId` nize je id pripadu
    // due-diligence (`dd_cases.id`), ktere se lisi od `cases.id`. Smlouva,
    // doklady i provize se vazou na ZAKAZKU (`contracts.case_id`,
    // `client_invoices.case_id` -> `cases.id`).
    smlouva: <ContractsScreen caseId={zakazkaId} caseTitle={header?.location ?? undefined} />,
    naklady: <PreparingSection copy={CASE_TAB_PREPARING.naklady} compact />,
    // FAK2 bod 4: zalozka "Obchod › Provize" ukazuje doklady zakazky a
    // tlacitko "Vyfakturovat provizi" - uz neni placeholder.
    // Krok PROV1 bod 1: pod doklady stoji ROZPAD provize (makler / pobocka /
    // kancelar) - zadava se tam, kde se provize fakturuje.
    provize: (
      <>
        <CaseInvoicesPanel caseId={zakazkaId} />
        <CommissionSharesCard caseId={zakazkaId} />
      </>
    ),
  };

  /**
   * Obal PODSTRANKY sekce — navigacni lista v4 vedouci ZPET na zakazku a nadpis
   * sekce. Pouzivaji ho oba navraty (s proverením i bez), aby lista a nadpis
   * nebyly ve dvou kopiich.
   */
  const subPage = (content: React.ReactNode): React.JSX.Element => (
    <div className="rh-v4-page" data-testid="zakazka-detail" style={{ color: 'var(--rh-text)' }}>
      <V4NavBar
        testId="case-nav"
        back={
          <button type="button" onClick={() => { setTab(CASE_TAB_DEFAULT); }} data-testid="case-back-to-case">
            <ChevronLeft size={24} strokeWidth={2.4} aria-hidden="true" />
            {humanTitle}
          </button>
        }
        actions={
          <CaseMoreMenu
            caseId={zakazkaId}
            onOpenTab={setTab}
            onShare={() => { setTab('dokumenty'); }}
            onEdit={openEdit}
          />
        }
      />
      <h1 className="rh-v4-head__title" data-testid="case-section-title">
        {CASE_TABS.find((item) => item.id === tab)?.label ?? ''}
      </h1>
      {content}
    </div>
  );

  /* ------------------------------------------------------------------
   * Bod 4 — ZAKAZKA BEZ PROVĚŘENÍ. Misto vety „Zakázka zatím nemá spuštěný
   * audit" (jedine, co tu do tohoto kroku bylo) stoji cela stranka: hlavicka,
   * postup (0–20 %) a karta „Další krok" s jedinym tlacitkem.
   * ------------------------------------------------------------------ */
  if (rawDetail === null) {
    /*
     * Krok UXP5b bod 1: sekce obchodni faze se otevre i tady — bez auditnich
     * pruhu a bocnich sloupcu (ty cely stoji na `detail`), ale s obsahem.
     */
    const businessContent = businessTabContent[tab];
    if (businessContent !== undefined && CASE_TABS_WITHOUT_AUDIT.includes(tab)) {
      return subPage(
        <div className="case-detail__main" id={`case-tabpanel-${tab}`}>{businessContent}</div>,
      );
    }
    return screen(
      <V4NextStep
        testId="case-next-step"
        kicker={t('webAdmin.caseAdvice.kicker', { locale: 'cs' })}
        icon={<ShieldCheck size={18} strokeWidth={2.2} />}
        title={
          <span data-testid="case-next-step-title">
            {t('webAdmin.caseDetailV4.noAuditTitle', { locale: 'cs' })}
          </span>
        }
        note={
          <>
            {CASE_TEXTS.noAuditRun}
            <span data-testid="case-no-audit" hidden />
            {startReviewNotice !== null ? (
              <span role="status" style={{ display: 'block' }} data-testid="case-start-review-notice">
                {startReviewNotice}
              </span>
            ) : null}
            {startReviewError !== null ? (
              <span role="alert" style={{ display: 'block', color: 'var(--rh-danger)' }} data-testid="case-start-review-error">
                {startReviewError}
              </span>
            ) : null}
          </>
        }
        actions={
          <>
            {/* Druh nemovitosti — viz `startReview`: server ho u prvniho behu
                vyzaduje a hadat ho je horsi nez se zeptat (ROZHODNUTI FUNK1-1). */}
            <label className="rh-v4-next__note" style={{ display: 'block' }}>
              {t('webAdmin.caseAdvice.startReviewKindLabel', { locale: 'cs' })}
              <select
                className="rh-input"
                style={{ display: 'block', width: '100%' }}
                value={reviewKind}
                onChange={(event) => { setPickedReviewKind(event.target.value as GateCaseKind | ''); }}
                data-testid="case-start-review-kind"
              >
                <option value="">—</option>
                {REVIEW_KIND_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            {/*
              Krok ZDARMA1 bod 1: dve jasne volby rozsahu PRED tlacitkem —
              vychozi je bezplatna zakladni. Potvrzovaci text se podle ni meni
              (`reviewConfirmBodyFor`).
            */}
            <ReviewLevelChoice
              value={reviewLevel}
              onChange={setReviewLevel}
              disabled={startingReview || reviewQueued}
              testId="case-review-level"
              options={reviewOptions}
              hideBasicQuota={publicDemo}
            />
            {/*
              Krok UXP4a bod 2 (nalez P2-2): VLASTNI potvrzeni, ne
              `window.confirm` — systemovy dialog se v prohlizeci bez obsluhy
              zavre sam (prvni dva kliky pak „nic neudelaly") a cenu rika mimo
              stranku. Podoba je TATAZ jako u „Prověřit znovu"
              (`components/CaseFreshnessBar.tsx`) — zadny novy vzor.
            */}
            {confirmingReview ? (
              /* Potvrzeni NAHRAZUJE tlacitko (jako u „Prověřit znovu") — dve
                 tlacitka „Spustit prověření" vedle sebe by mataly. */
              <div
                className="rh-v4-next__note"
                role="group"
                aria-label={t('webAdmin.caseDetailV4.startReviewConfirmTitle', { locale: 'cs' })}
                data-testid="case-start-review-confirm"
              >
                <strong>{t('webAdmin.caseDetailV4.startReviewConfirmTitle', { locale: 'cs' })}</strong>{' '}
                {reviewConfirmBody}
                <div className="flex flex-wrap gap-2 mt-2">
                  <button
                    type="button"
                    className="rh-v4-btn rh-v4-btn--filled"
                    onClick={() => { startReview(); }}
                    data-testid="case-start-review-accept"
                  >
                    {t('webAdmin.caseDetailV4.startReviewConfirmAccept', { locale: 'cs' })}
                  </button>
                  <button
                    type="button"
                    className="rh-v4-btn"
                    onClick={() => { setConfirmingReview(false); }}
                    data-testid="case-start-review-cancel"
                  >
                    {t('webAdmin.caseDetailV4.startReviewConfirmCancel', { locale: 'cs' })}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="rh-v4-btn rh-v4-btn--filled rh-v4-btn--block"
                disabled={startingReview || reviewQueued || reviewKind === ''}
                onClick={() => { setConfirmingReview(true); }}
                data-testid="case-next-step-primary"
              >
                {startingReview || reviewQueued
                  ? t('webAdmin.caseDetailV4.reviewRunning', { locale: 'cs' })
                  : t('webAdmin.caseDetailV4.startReview', { locale: 'cs' })}
              </button>
            )}
            {/*
              Krok UXZ1 bod 2: zalozeni zakazky uz provereni NESPOUSTI, takze
              prvni obrazovka detailu je rozcestnik dalsich kroku — vedle
              provereni jde na misto nabor a z detailu se zaklada inzerat
              (vyber zakazky v `/inzerat` se tim preskakuje, zadani bod 3).
            */}
            <Link
              to="/nabor"
              search={{ caseId: zakazkaId }}
              className="rh-v4-btn rh-v4-btn--block"
              data-testid="case-next-step-field-intake"
            >
              {t('webAdmin.caseDetailV4.fieldIntake', { locale: 'cs' })}
            </Link>
            <Link
              to="/zakazky/$id/inzerat"
              params={{ id: zakazkaId }}
              className="rh-v4-btn rh-v4-btn--block"
              data-testid="case-next-step-listing"
            >
              {t('webAdmin.listingDraft.nextStep.action', { locale: 'cs' })}
            </Link>
          </>
        }
      />,
      commonSections,
    );
  }

  const caseId = rawDetail.fixture.caseInfo.id;
  const detail = rawDetail;
  const nodeLabels = nodeLabelById(detail.fixture);
  const research = detail.fixture.research;
  // Krok UX2: jmenovatel kontrol bere ZJISTENY typ predmetu (dum s pozemky
  // -> HOUSE = 5), ne zadani maklere v pruvodci. Server pocita stale podle
  // `dd_cases.kind` — NALEZ pro linku A, tady je to jen zobrazeni.
  const gates = gateProgress(detail, gateKind);
  const freshness = caseFreshness(detail);
  const readinessExplain = t('webAdmin.caseDetail.metrics.readinessExplain', {
    locale: 'cs',
    values: { closed: String(gates.closed), total: String(gates.total) },
  });
  const bandedFacts = detail.facts.filter((f) => f.trustBand !== null);
  const highBandedFacts = bandedFacts.filter((f) => f.trustBand === 'HIGH' || f.trustBand === 'VERY_HIGH');
  // Ukoly makléře — jeden vypocet pro sekci „Prověření" i pro kartu reportu.
  const tasks = cockpitTasks(detail, {
    subject: subject as CaseSubject,
    humanTasks,
    confirmedNotes: detail.fixture.research.inputs
      .map((input) => input.note)
      .filter((note): note is string => note !== null),
    /*
     * Krok PIL1 (vada V6): neoverene oblasti jsou ukoly. Jde TENTYZ seznam,
     * jaky o par radku niz vykresli `CaseReviewSection` — dva vypocty by
     * znamenaly, ze „chybí ověřit 3 věci" a „Teď nemáte žádný otevřený úkol"
     * stoji vedle sebe (snimek `GEN1/2-07`).
     */
    ...(review === null ? {} : { reviewAreas: review.areas }),
  });

  const openTabAndScrollTo = (targetTab: CaseTabId, anchor: string | null): void => {
    setTab(targetTab);
    if (anchor === null) return;
    later(() => {
      document.querySelector(`[data-testid="${anchor}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  /**
   * Krok UX2 — „Prověřit znovu“. Záměr běhu jde z katalogu (viz
   * `recheckCaseAudit`), typ ze ZJIŠTĚNÉHO předmětu a parcely z faktů této
   * zakázky — žádný nový údaj od nikoho, jen zopakovaný průzkum.
   */
  const startRecheck = (level: ReviewLevel = REVIEW_LEVEL_DEFAULT): void => {
    if (gateKind === null) return;
    setRechecking(true);
    setRecheckError(null);
    void recheckCaseAudit({
      caseId: zakazkaId,
      kind: gateKind,
      // Krok ZDARMA1: rozsah vybrany v karte Prověření; bez volby je to
      // BEZPLATNA uroven, ne placeny beh.
      reportLevel: level,
      addressQuery: detail.fixture.caseInfo.addressQuery,
      parcels: areaList.map((row) => row.parcelLabel.replace(/^st\.\s*/, '')),
      cadastralArea: header?.cadastralAreaName ?? null,
      landRegistryNumber: null,
    })
      .then(() => {
        setRechecking(false);
        // Detail se nacte znovu az po obnoveni stranky - do te doby ukazujeme
        // „ve fronte“, ne vymysleny novy datum pruzkumu.
        later(() => { reloadDetail(); }, 4000);
      })
      .catch((cause: unknown) => {
        setRechecking(false);
        setRecheckError(cause instanceof Error ? cause.message : String(cause));
      });
  };

  /**
   * Potvrzeni placeneho behu — „Dohledat" spousti nove proverení (UX2).
   *
   * Krok ZDARMA1: tady se uroven NEVYBIRA a zustava KOMPLETNI. „Dohledat" je
   * akce u konkretni neoverene oblasti (list vlastnictvi, exekuce, SVJ) —
   * presne to, co zakladni bezplatny beh zamerne nezkousi, takze bezplatnou
   * urovni by se oblast nikdy nedoplnila a potvrzeni o kreditu plati dal.
   */
  const confirmLookup = (): void => {
    if (typeof window !== 'undefined' && !window.confirm(t('webAdmin.caseAdvice.lookupConfirm', { locale: 'cs' }))) {
      return;
    }
    startRecheck('plny');
  };

  const runAdviceAction = (action: CaseAdviceAction): void => {
    switch (action.kind) {
      case 'lookup':
        confirmLookup();
        return;
      case 'areas':
        // Oblasti uz nejsou na jine zalozce — jsou v sekci „Prověření", ktera
        // je na teto strance rozbalena; staci na ni posunout.
        document.querySelector('[data-testid="case-review"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      case 'section':
        setTab(action.section as CaseTabId);
        return;
      case 'listing':
        void navigate({ to: '/zakazky/$id/inzerat', params: { id: zakazkaId } });
        return;
      case 'task': {
        // Ukol se vyrizuje v seznamu ukolu v sekci „Prověření" — tlacitko tam
        // uz je (vcetne potvrzeni typu), takze se jen posuneme na nej.
        const task = tasks.find((item) => item.id === action.taskId);
        if (task !== undefined) {
          document.querySelector(`[data-testid="${task.testId}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        return;
      }
    }
  };

  const runAreaAction = (area: ReviewArea): void => {
    const action = area.action;
    switch (action.kind) {
      case 'lookup':
        confirmLookup();
        return;
      case 'answer': {
        const question = detail.fixture.questions.find((item) => item.id === action.questionId);
        if (question !== undefined) setAnswerTarget({ kind: 'question', question });
        return;
      }
      case 'view':
      case 'open':
        openTabAndScrollTo(action.tab, action.anchor);
        return;
    }
  };

  /*
   * Obsah SEKCI, ktere se do rozbaleni nevejdou — kazda ma dal svou adresu
   * `?sekce=<id>` a vykresli se jako podstranka (viz komentar vyse).
   * `prehled` tu zamerne NENI: to je tahle stranka.
   */
  const tabContent: Partial<Record<CaseTabId, React.JSX.Element>> = {
    'co-vime': <CoVimeTab detail={detail} nodeLabels={nodeLabels} />,
    'co-nevime': <CoNevimeTab detail={detail} onAnswer={setAnswerTarget} />,
    konflikty: <KonfliktyTab detail={detail} />,
    oceneni: <OceneniTab detail={detail} nodeLabels={nodeLabels} caseId={zakazkaId} />,
    geometrie: <GeometryTab detail={detail} nodeLabels={nodeLabels} />,
    pristup: <AccessTab detail={detail} nodeLabels={nodeLabels} />,
    site: <UtilitiesTab detail={detail} nodeLabels={nodeLabels} />,
    'uzemni-plan': <PlanningTab detail={detail} nodeLabels={nodeLabels} />,
    rizika: <RisksTab detail={detail} nodeLabels={nodeLabels} />,
    dokumenty: <DocumentsTab detail={detail} propertyId={propertyId} zakazkaId={zakazkaId} tasks={tasks} />,
    aktivity: <ActivityTab detail={detail} />,
    // Sekce obchodni faze jsou TENTYZ objekt, ktery umi i zakazka bez
    // proverení (krok UXP5b bod 1) — zadna druha kopie obsahu.
    ...businessTabContent,
  };

  const answerPanel = (
    <AnswerQuestionPanel
      target={answerTarget}
      caseId={caseId}
      onClose={() => { setAnswerTarget(null); }}
      onSaved={reloadDetail}
    />
  );

  /* ------------------------------------------------------ JEDNA STRANKA */
  if (tab === CASE_TAB_DEFAULT) {
    const status = cockpitStatus(detail, subject as CaseSubject, gateKind);
    const reviewAreas = review as CaseReviewAreas;
    const reviewPercent =
      reviewAreas.total === 0 ? 0 : Math.round((reviewAreas.verified / reviewAreas.total) * 100);
    const sections: readonly CaseV4Section[] = [
      {
        id: 'proveri',
        standalone: true,
        defaultOpen: true,
        icon: (
          <span className="rh-v4-ico rh-v4-ico--sm rh-v4-ico--ok" aria-hidden="true">
            <ShieldCheck size={18} strokeWidth={2.2} />
          </span>
        ),
        label: t('webAdmin.caseReview.heading', { locale: 'cs' }),
        /* Predloha: „2 z 6 v pořádku · 1 problém". Veta „X ze Y ověřeno" je
           z JEDNE funkce (`reviewProgressText`) a stoji na obrazovce jednou —
           prouzek postupu nahore ji nenese (nalez UXR5 bod 15). */
        sub: (
          <>
            <span data-testid="case-review-progress">{reviewProgressText(reviewAreas)}</span>
            {/*
              Krok UXP4a bod 2 (nalez P2-2): dokud beh BEZI, pocet problemu se
              NEUVADI. Fakta pribyvaji po castech, takze hlavicka skakala
              („Zásadní problém" → „Pozor na 2 věci" → „Pozor na 32 věcí") a
              makler to cetl jako hotovy vysledek. Pocet se ukaze az po behu.
            */}
            {freshness.running
              ? ` · ${t('webAdmin.caseCockpit.freshness.running', { locale: 'cs' })}`
              : status.issues.length === 0
                ? ''
                : ` · ${status.headline}`}
          </>
        ),
        extra: (
          <V4Bar
            size="xs"
            tone="done"
            percent={reviewPercent}
            label={reviewProgressText(reviewAreas)}
            testId="case-review-bar"
          />
        ),
        body: (
          <>
            {/*
              KROK QAFIX14 bod 2 — PRUH UKAZKOVEHO REPORTU.
              V demu se nepocita nic: beh je opsany z ukazkove fixtury
              (`acceptance.status = DEMO_SAMPLE`), takze obrazovka MUSI rict,
              ze cisla nejsou o teto nemovitosti. Rozpoznani je v
              `isDemoSampleRun` (SSOT, cte ho i spusteni provereni).
            */}
            {demoSampleRun ? (
              <p
                className="rh-v4-next__note"
                role="status"
                data-testid="case-review-demo-sample"
              >
                {t('webAdmin.caseDetailV4.demoSampleReport', { locale: 'cs' })}
              </p>
            ) : null}
            <CaseReviewSection
              status={status}
              review={reviewAreas}
              onAreaAction={runAreaAction}
              /*
                Krok UXP4b bod 2 (nalez P2-6): „Zdroje a celý report" vede do
                sekce DOKUMENTY — tam stoji karta reportu pro klienta i doklady
                se zdroji. Do UXP4b to vedlo na technickou sekci „Co víme",
                kde zadne tlacitko pro report neni, a makléř report nenasel.
              */
              onOpenReport={() => { setTab('dokumenty'); }}
              onOpenInAudit={openTabAndScrollTo}
              formatDate={(iso) => czDate(iso)}
              freshness={
                <CaseFreshnessBar
                  freshness={freshness}
                  onRecheck={startRecheck}
                  rechecking={rechecking}
                  recheckError={recheckError}
                  publicDemo={publicDemo}
                  /* Krok NOC6 body 3+4: tataz cisla jako u prvniho spusteni. */
                  reviewOptions={reviewOptions}
                />
              }
            >
              {/*
                „Co musím udělat?" z UXR2 ZUSTAVA — je to hotova funkce (seznam
                ukolu s akcemi a potvrzenim rozporu typu). Predloha ji jako
                vlastni kartu nema, ale ukoly jsou presne to, cim se proverení
                dokonci, takze stoji tady (ROZHODNUTI UXV4a2-UKOLY).
              */}
              <CockpitTab
                detail={detail}
                tasks={tasks}
                onOpenAudit={openTabAndScrollTo}
                onAnswer={setAnswerTarget}
                onChanged={reloadDetail}
                onTasksReload={() => { setHumanTasksToken((n) => n + 1); }}
              />
            </CaseReviewSection>
          </>
        ),
      },
      {
        id: 'nemovitost',
        icon: (
          <span className="rh-v4-ico rh-v4-ico--sm rh-v4-ico--property" aria-hidden="true">
            <Building2 size={18} strokeWidth={2} />
          </span>
        ),
        label: t('webAdmin.caseTabGroups.nemovitost.label', { locale: 'cs' }),
        sub:
          areaList.length === 0
            ? t('webAdmin.caseDetailV4.propertySubNone', { locale: 'cs' })
            : titleParts.join(' · '),
        body: (
          <>
            <CaseAreasCard rows={areaList} />
            {/*
              Karta „Nemovitost" se stitky puvodu udaje (krok UXR2-3) — JEDINE
              misto, kde makler vidi, ODKUD kazdy udaj je. Mapa parcel je nad
              strankou jako hero; druha ziva mapa v rozbaleni by znamenala
              druhe stazeni vrstev kvuli temuz obrazku (ROZHODNUTI UXV4a2-MAPA).
            */}
            <CasePropertyCard detail={detail} onOpenAudit={() => { setTab(CASE_AUDIT_ENTRY_TAB); }} />
            <V4Row
              testId="case-open-nemovitost"
              title={t('webAdmin.caseDetailV4.openSection', { locale: 'cs' })}
              chevron
              onClick={() => { setTab('geometrie'); }}
            />
          </>
        ),
      },
      ...commonSections,
    ];
    return (
      <>
        {screen(
          <CaseNextStepCard
            /* Krok QAFIX14b bod 2: „Dokončete prověření — <druh>" bere druh ze
               ZAKAZKY (`displaySubject`), ne z predmetu ukazkoveho behu. */
            input={{ progress, review: reviewAreas, subject: displaySubject as CaseSubject, tasks, clientReportSent: null, listingState }}
            onAction={runAdviceAction}
          />,
          sections,
        )}
        {answerPanel}
      </>
    );
  }

  /* ---------------------------------------------------------- PODSTRANKA */
  /*
   * Sekce, ktera se do rozbaleni nevejde (cely audit, inzerce, smlouva,
   * naklady, provize…). Zustava na sve adrese `?sekce=<id>`, takze odkaz z
   * e-mailu i z rozbaleni dal funguje; nad ni stoji tataz navigacni lista v4,
   * jen vede ZPET NA ZAKAZKU.
   */
  const primaryTab = CASE_PRIMARY_TABS.find((item) => item.id === casePrimaryTabOf(tab)) ?? null;
  const groupTabs = CASE_TABS.filter((item) =>
    primaryTab === null ? false : (primaryTab.members as readonly string[]).includes(item.id),
  );
  return subPage(
    <>
      {/*
        Ctyri ruzne stavy, ktere se driv michaly do jednoho „audit dokoncen"
        (Milnik 1, bod 1). Misto agregovane „Duvery 22 %" je rozpad, ktery jde
        precist: kolik zjisteni ma vysokou jistotu. Na jedne strance detailu
        uz nestoji — je to pohled AUDITORA a patri k sekcim auditu.
      */}
      <div className="flex flex-wrap gap-6 mb-3" data-testid="case-header-status">
        <StatusItem label={CASE_STATUS_LABELS.researchRun}>
          {research.finishedAt !== null
            ? t('webAdmin.caseDetail.status.researchRunFinished', { locale: 'cs', values: { date: czDateTime(research.finishedAt) } })
            : research.startedAt !== null
              ? t('webAdmin.caseDetail.status.researchRunRunning', { locale: 'cs' })
              : NOT_DETERMINED}
        </StatusItem>
        <StatusItem label={CASE_STATUS_LABELS.caseState}>
          {gates.allClosed
            ? t('webAdmin.caseDetail.status.readyToDecide', { locale: 'cs' })
            : CASE_STATE_WAITING}
        </StatusItem>
        <StatusItem label={CASE_STATUS_LABELS.readiness}>
          {t('webAdmin.caseDetail.status.readinessGates', {
            locale: 'cs',
            values: { closed: String(gates.closed), total: String(gates.total) },
          })}
        </StatusItem>
        <StatusItem label={CASE_STATUS_LABELS.publishedRevision} testId="case-published-revision">
          {CASE_REVISION_NOT_AVAILABLE}
        </StatusItem>
      </div>

      <div className="flex flex-wrap gap-6 mb-3" data-testid="case-header-metrics">
        <MetricBar
          label={t('webAdmin.caseDetail.metrics.readinessLabel', { locale: 'cs' })}
          pct={gates.pct}
          explain={readinessExplain}
        />
        <p style={{ fontSize: 'var(--rh-size-footnote)', color: 'var(--rh-text-muted)', margin: 0, alignSelf: 'center' }} data-testid="case-confidence-breakdown">
          {t('webAdmin.caseDetail.metrics.confidenceBreakdown', {
            locale: 'cs',
            values: { high: String(highBandedFacts.length), total: String(bandedFacts.length) },
          })}
        </p>
      </div>

      <div className="case-detail__grid">
        <LeftColumn detail={detail} gateKind={gateKind} />
        <div className="case-detail__main" id={`case-tabpanel-${tab}`}>
          {/*
            Krok UXR3 — jedna uroven: podstranka vykresli vsechny sekce sve
            skupiny pod sebou. Kazda si drzi `data-testid="case-section-<id>"`,
            takze `openTabAndScrollTo` na ni posune presne jako driv.
          */}
          {groupTabs.length > 1
            ? groupTabs.map((item) => (
                <section key={item.id} data-testid={`case-section-${item.id}`} style={{ marginBottom: 20 }}>
                  <h2 className="rh-a2-section-title" id={`case-tab-${item.id}`}>
                    {item.label}
                  </h2>
                  {tabContent[item.id]}
                </section>
              ))
            : tabContent[tab]}
        </div>
        <RightColumn detail={detail} gateKind={gateKind} onOpenTab={setTab} />
      </div>

      {answerPanel}
    </>,
  );
}

/**
 * Nabidka „⋯" navigacni listy (predloha `Zakazka.dc.html`: „Sdílet s klientem /
 * Upravit / Archivovat").
 *
 * Krok UXV4a2 k nim pribrala SEZNAM SEKCI, ktere jedna stranka nema v
 * rozbaleni (cely audit, inzerce, poptavky, terminy, prohlidky, nabidky,
 * naklady, provize). Nic nezmizelo — jen uz to nema vlastni listu zalozek
 * (ROZHODNUTI UXV4a2-PODSTRANKA).
 *
 * ROZHODNUTI UXV4a2-ARCHIV: REOS zadny stav „archivovano" nema; jediny
 * koncovy stav mimo „Uzavřeno" je `cancelled` („Zrušené"), ze ktereho uz
 * stavovy automat nikam nevede. Polozka proto (a) je videt JEN kdyz server
 * prechod povoluje (`allowedTransitions`) a makler ma pravo editace,
 * (b) pred odeslanim se zepta a v otazce DOSLOVA rekne, co se stane.
 */
/**
 * Krok UXP1 (nalez P1-8) — EDITACE ZAKAZKY.
 *
 * Rucni pruchod 10. 10.: „Upravit" v nabidce zakazky jen prepsalo URL na
 * `?sekce=geometrie` a zadny formular se neotevrel, takze udaje z formulare
 * „Založit zakázku" (nazev, cenova predstava, cislo jednotky) nebylo jak
 * opravit.
 *
 * ROZHODNUTI UXP1-3: meni se to, co nese RADEK ZAKAZKY — nazev a zadani
 * makléře. Strany maji vlastni cestu (sekce „Lidé" nad
 * `POST /v1/cases/:id/participants`).
 *
 * Krok PRF1 (vada V1, bod 4) k tomu pribral PREDMET ZAKAZKY — adresu,
 * katastralni uzemi, parcely a druh. Pri pruchodu 13. 10. se zakazka navazala
 * na cizi parcelu v jine obci a makler ji nemel cim opravit. Predmet se
 * NEUKLADA druhou cestou: jde TOUTEZ cestou, jakou ho urcuje prvni i opakovane
 * proverení (`recheckCaseAudit` → `POST /v1/cases/:id/audit-runs` → snapshot
 * behu → `resolveSubject`). Novy beh zalozi novy auditni pripad, takze fakta
 * stareho predmetu zustavaji v historii (dd_facts jsou append-only) a prestavaji
 * byt soucasnou pravdou zakazky — ROZHODNUTI PRF1-SUPERSEDE.
 *
 * Pocatecni stav se plni V RENDERU podle `kase.id` (ne v `useEffect`) —
 * efekt po pripojeni je zavod, ktery se projevi jen pod plnou branou.
 */
function CaseEditForm({
  kase,
  subject,
  onSaved,
  onSubjectChanged,
  onCancel,
}: {
  readonly kase: KanbanCase;
  /** Dnes identifikovany predmet — presne to, co o nem rika hlavicka. */
  readonly subject: {
    readonly addressQuery: string;
    readonly cadastralArea: string;
    readonly parcels: readonly string[];
    readonly kind: GateCaseKind | '';
  };
  readonly onSaved: (updated: KanbanCase) => void;
  /** Predmet se zmenil → bezi nove proverení, detail se ma nacist znovu. */
  readonly onSubjectChanged: () => void;
  readonly onCancel: () => void;
}): React.JSX.Element {
  const [loadedFor, setLoadedFor] = React.useState<string | null>(null);
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [addressQuery, setAddressQuery] = React.useState('');
  const [cadastralArea, setCadastralArea] = React.useState('');
  const [parcelsText, setParcelsText] = React.useState('');
  const [kind, setKind] = React.useState<GateCaseKind | ''>('');
  /**
   * Krok QAFIX4b bod 3 (mezera POB1): POBOČKA zakázky. Bez ní neodlišil
   * přepínač `broker_read_scope='office'` „jinou pobočku" od „žádné" —
   * zakázky založené dřív žádnou neměly a makléř se zúženým dosahem je
   * neviděl vůbec.
   */
  const [officeId, setOfficeId] = React.useState<string | null>(null);
  const [officeOptions, setOfficeOptions] = React.useState<readonly OfficeDto[]>([]);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  /** Parcely se pisi do jednoho pole, oddelene carkou („38/13, st. 12"). */
  const initialParcels = subject.parcels.join(', ');
  if (loadedFor !== kase.id) {
    setLoadedFor(kase.id);
    setTitle(kase.title);
    setDescription(kase.description ?? '');
    setAddressQuery(subject.addressQuery);
    setCadastralArea(subject.cadastralArea);
    setParcelsText(initialParcels);
    setKind(subject.kind);
    // Stav se plní z props V RENDERU, ne v useEffect — efekt po připojení
    // přepíše rozepsaný výběr (poučení `reos-web-admin-mount-effect-race`).
    setOfficeId(kase.officeId);
    setError(null);
  }

  /*
   * Pobočky se načtou JEDNOU na otevření formuláře. Selhání je neškodné:
   * přepínač se nenakreslí a ostatní pole se ukládají dál.
   */
  React.useEffect(() => {
    let cancelled = false;
    void listOffices(false).then(
      (items) => { if (!cancelled) setOfficeOptions(items); },
      () => undefined,
    );
    return () => { cancelled = true; };
  }, []);

  const parcelList = parcelsText
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '');
  const subjectChanged =
    addressQuery.trim() !== subject.addressQuery.trim() ||
    cadastralArea.trim() !== subject.cadastralArea.trim() ||
    parcelList.join(', ') !== initialParcels ||
    kind !== subject.kind;

  /**
   * Zmena predmetu = novy beh proverení nad opravenym zadanim. Chyba se hlasi
   * U POLE, ktere ji zpusobilo (fokus + zvyrazneni), ne jen vedle tlacitka.
   */
  const recheckAfterSubjectChange = (updated: KanbanCase): void => {
    void recheckCaseAudit({
      caseId: kase.id,
      ...(kind === '' ? {} : { kind }),
      addressQuery: addressQuery.trim(),
      parcels: parcelList.map((label) => label.replace(/^st\.\s*/, '')),
      cadastralArea: cadastralArea.trim() === '' ? null : cadastralArea.trim(),
      landRegistryNumber: null,
    })
      .then(() => {
        setBusy(false);
        onSaved(updated);
        onSubjectChanged();
      })
      .catch((cause: unknown) => {
        setBusy(false);
        setError(
          t('webAdmin.caseDetailV4.editSubjectFailed', {
            locale: 'cs',
            values: { reason: cause instanceof Error ? cause.message : String(cause) },
          }),
        );
      });
  };

  const save = (): void => {
    if (title.trim() === '') {
      setError(t('webAdmin.caseDetailV4.editTitleRequired', { locale: 'cs' }));
      focusField('case-edit-title');
      return;
    }
    /*
     * Parcela bez katastralniho uzemi je v CR nejednoznacna (cislo „38/13" je
     * v kazdem k.u. jine) — presne tim zacala vada V1. Lepsi srozumitelna
     * chyba u pole nez beh, ktery si uzemi domysli.
     */
    if (subjectChanged && parcelList.length > 0 && cadastralArea.trim() === '') {
      setError(t('webAdmin.caseDetailV4.editParcelNeedsArea', { locale: 'cs' }));
      focusField('case-edit-cadastral-area');
      return;
    }
    if (subjectChanged && kind === '') {
      setError(t('webAdmin.caseDetailV4.editKindRequired', { locale: 'cs' }));
      focusField('case-edit-kind');
      return;
    }
    if (subjectChanged && addressQuery.trim() === '' && parcelList.length === 0) {
      setError(t('webAdmin.caseDetailV4.editSubjectRequired', { locale: 'cs' }));
      focusField('case-edit-address');
      return;
    }
    setBusy(true);
    setError(null);
    void updateCase(kase.id, {
      title: title.trim(),
      // Prazdne zadani = `null`, ne prazdny retezec — detail pak sekci nekresli.
      description: description.trim() === '' ? null : description.trim(),
      officeId,
    })
      .then((updated) => {
        if (subjectChanged) {
          // Nazev a zadani jsou ulozene; zbyva novy beh nad opravenym predmetem.
          // Formular zustava otevreny, dokud beh neni zarazeny — jinak by se
          // chyba zarazeni nemela kde ukazat.
          recheckAfterSubjectChange(updated);
          return;
        }
        setBusy(false);
        onSaved(updated);
      })
      .catch((cause: unknown) => {
        setBusy(false);
        setError(
          t('webAdmin.caseDetailV4.editFailed', {
            locale: 'cs',
            values: { reason: cause instanceof Error ? cause.message : String(cause) },
          }),
        );
      });
  };

  return (
    <div data-testid="case-edit-form">
      {error !== null ? (
        <p role="alert" className="rh-a2-error" data-testid="case-edit-error">
          {error}
        </p>
      ) : null}
      <div className="rh-v4-field mb-3">
        <label className="rh-v4-field__label" htmlFor="case-edit-title">
          {t('webAdmin.caseDetailV4.editTitleLabel', { locale: 'cs' })}
        </label>
        <input
          id="case-edit-title"
          value={title}
          onChange={(event) => { setTitle(event.target.value); }}
          data-testid="case-edit-title"
        />
      </div>
      <div className="rh-v4-field">
        <label className="rh-v4-field__label" htmlFor="case-edit-description">
          {t('webAdmin.caseDetailV4.editBriefLabel', { locale: 'cs' })}
        </label>
        <textarea
          id="case-edit-description"
          rows={4}
          value={description}
          onChange={(event) => { setDescription(event.target.value); }}
          data-testid="case-edit-description"
        />
      </div>
      <p className="text-sm m-0" style={{ color: 'var(--rh-text-muted)' }}>
        {t('webAdmin.caseDetailV4.editBriefHint', { locale: 'cs' })}
      </p>
      {/*
        Krok QAFIX4b bod 3: zařazení zakázky na POBOČKU. Kreslí se jen když
        kancelář nějakou má — jinak by to byl přepínač s jedinou volbou.
      */}
      {officeOptions.length > 0 ? (
        <div className="rh-v4-field mt-3">
          <label className="rh-v4-field__label" htmlFor="case-edit-office">
            {t('webAdmin.settings.team.officeLabel', { locale: 'cs' })}
          </label>
          <select
            id="case-edit-office"
            value={officeId ?? ''}
            onChange={(event) => { setOfficeId(event.target.value === '' ? null : event.target.value); }}
            data-testid="case-edit-office"
          >
            <option value="">{t('webAdmin.settings.team.officeNone', { locale: 'cs' })}</option>
            {officeOptions.map((office) => (
              <option key={office.id} value={office.id}>
                {office.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      {/*
        Krok PRF1 (vada V1, bod 4) — PREDMET ZAKAZKY. Adresa je doplnek,
        identita je „katastralni uzemi + parcela": tak to cte i server
        (`resolveSubject`), takze makler meni presne ty udaje, podle kterych
        se predmet urcuje.
      */}
      <h3 className="text-sm mt-3 mb-2" data-testid="case-edit-subject-heading">
        {t('webAdmin.caseDetailV4.editSubjectHeading', { locale: 'cs' })}
      </h3>
      <div className="rh-v4-field mb-3">
        <label className="rh-v4-field__label" htmlFor="case-edit-address">
          {t('webAdmin.caseDetailV4.editAddressLabel', { locale: 'cs' })}
        </label>
        {/*
          Krok NOC3 bod 5 (nalez N17): tataz napoveda jako v pruvodci — nas
          registr adres (RUIAN), Mapy.com jen jako zaloha. Souradnice tady
          nikam nejdou (predmet se urcuje parcelou a k. u., viz komentar
          vyse); bere se jen vybrany text adresy.
        */}
        <AddressAutocomplete
          value={{ query: addressQuery, lat: null, lon: null, source: 'manual' }}
          onChange={(address) => { setAddressQuery(address.query); }}
          inputId="case-edit-address"
          testIdPrefix="case-edit-address"
        />
      </div>
      <div className="rh-v4-field mb-3">
        <label className="rh-v4-field__label" htmlFor="case-edit-cadastral-area">
          {t('webAdmin.caseDetailV4.editCadastralAreaLabel', { locale: 'cs' })}
        </label>
        <input
          id="case-edit-cadastral-area"
          value={cadastralArea}
          onChange={(event) => { setCadastralArea(event.target.value); }}
          data-testid="case-edit-cadastral-area"
        />
      </div>
      <div className="rh-v4-field mb-3">
        <label className="rh-v4-field__label" htmlFor="case-edit-parcels">
          {t('webAdmin.caseDetailV4.editParcelsLabel', { locale: 'cs' })}
        </label>
        <input
          id="case-edit-parcels"
          value={parcelsText}
          onChange={(event) => { setParcelsText(event.target.value); }}
          data-testid="case-edit-parcels"
        />
      </div>
      <div className="rh-v4-field">
        <label className="rh-v4-field__label" htmlFor="case-edit-kind">
          {t('webAdmin.caseAdvice.startReviewKindLabel', { locale: 'cs' })}
        </label>
        <select
          id="case-edit-kind"
          className="rh-input"
          value={kind}
          onChange={(event) => { setKind(event.target.value as GateCaseKind | ''); }}
          data-testid="case-edit-kind"
        >
          <option value="">—</option>
          {REVIEW_KIND_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      {subjectChanged ? (
        <p className="text-sm m-0 mt-2" style={{ color: 'var(--rh-text-muted)' }} data-testid="case-edit-subject-notice">
          {t('webAdmin.caseDetailV4.editSubjectNotice', { locale: 'cs' })}
        </p>
      ) : null}
      <p className="text-sm m-0" style={{ color: 'var(--rh-text-muted)' }} data-testid="case-edit-other-hint">
        {t('webAdmin.caseDetailV4.editOtherHint', { locale: 'cs' })}
      </p>
      <div className="flex gap-6 mt-3">
        <button
          type="button"
          className="rh-a2-btn rh-a2-btn--primary"
          disabled={busy}
          onClick={save}
          data-testid="case-edit-save"
        >
          {t('webAdmin.caseDetailV4.editSave', { locale: 'cs' })}
        </button>
        <button
          type="button"
          className="rh-a2-btn"
          disabled={busy}
          onClick={onCancel}
          data-testid="case-edit-cancel"
        >
          {t('webAdmin.caseDetailV4.editCancel', { locale: 'cs' })}
        </button>
      </div>
    </div>
  );
}

function CaseMoreMenu({
  caseId,
  onOpenTab,
  onShare,
  onEdit,
  onArchived,
}: {
  readonly caseId: string;
  readonly onOpenTab: (tab: CaseTabId) => void;
  readonly onShare: () => void;
  /** Krok UXP1 (nalez P1-8) — „Upravit" otevre editaci zakazky. */
  readonly onEdit: () => void;
  /** `undefined` = server prechod do „Zrušené" nepovoluje → polozka tam neni. */
  readonly onArchived?: () => void;
}): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const items = CASE_OVERFLOW_TABS.map((id) => ({
    id,
    label: CASE_TABS.find((item) => item.id === id)?.label ?? id,
  }));

  const archive = (): void => {
    setOpen(false);
    if (typeof window !== 'undefined' && !window.confirm(t('webAdmin.caseDetailV4.archiveConfirm', { locale: 'cs' }))) {
      return;
    }
    setBusy(true);
    setError(null);
    void transitionCase(caseId, CANCELLED_STAGE)
      .then(() => { setBusy(false); onArchived?.(); })
      .catch((cause: unknown) => {
        setBusy(false);
        setError(
          t('webAdmin.caseDetailV4.archiveFailed', {
            locale: 'cs',
            values: { reason: cause instanceof Error ? cause.message : String(cause) },
          }),
        );
      });
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        className="rh-v4-nav__dots"
        aria-label={t('webAdmin.zakazky.rowMenuLabel', { locale: 'cs' })}
        aria-expanded={open}
        disabled={busy}
        onClick={() => { setOpen((value) => !value); }}
        data-testid="case-more-menu"
      >
        <Ellipsis size={18} strokeWidth={2} aria-hidden="true" />
      </button>
      {error !== null ? (
        <p role="alert" className="rh-a2-error" data-testid="case-archive-error">
          {error}
        </p>
      ) : null}
      {open ? (
        <div className="rh-a2-menu__pop" style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)' }}>
          {/*
            Krok PIL1 (vada V8): do náboru na míste vedla cesta jen z „Nová"
            a z kalendáře — z detailu zakazky nikam (ROZHODNUTI GEN1-5,
            snimek `GEN1/4-01`). Polozka stoji v nabidce listy, takze plati
            i pro zakazku BEZ proverení (tataz lista, obe obrazovky).
          */}
          <Link
            to="/nabor"
            search={{ caseId }}
            className="rh-a2-menu__row"
            onClick={() => { setOpen(false); }}
            data-testid="case-more-field-intake"
          >
            {t('webAdmin.caseDetailV4.fieldIntake', { locale: 'cs' })}
          </Link>
          {/*
            Krok NOC3 bod 1 (nalez N18): tataz lista je na obou obrazovkach
            detailu i na podstrankach sekci, takze inzerat je odsud dostupny
            ve VSECH stavech zakazky — i kdyz prave bezi provereni.
          */}
          <Link
            to="/zakazky/$id/inzerat"
            params={{ id: caseId }}
            className="rh-a2-menu__row"
            onClick={() => { setOpen(false); }}
            data-testid="case-more-listing"
          >
            {t('webAdmin.listingDraft.nextStep.action', { locale: 'cs' })}
          </Link>
          <button
            type="button"
            className="rh-a2-menu__row"
            onClick={() => { setOpen(false); onShare(); }}
            data-testid="case-share-with-client"
          >
            {t('webAdmin.caseDetail.shareWithClient', { locale: 'cs' })}
          </button>
          {/*
            Krok UXP1 (nalez P1-8): „Upravit" DOSUD jen prepnulo zalozku na
            Nemovitost (URL `?sekce=geometrie`) a zadny formular se neotevrel —
            makler nemel jak opravit nazev ani zadani, ktere pri zakladani
            napsal. Ted otevre editaci zakazky nad `PATCH /v1/cases/:id`.
          */}
          <button
            type="button"
            className="rh-a2-menu__row"
            onClick={() => { setOpen(false); onEdit(); }}
            data-testid="case-more-edit"
          >
            {t('webAdmin.caseDetailV4.edit', { locale: 'cs' })}
          </button>
          {onArchived !== undefined ? (
            <button
              type="button"
              className="rh-a2-menu__row"
              onClick={archive}
              data-testid="case-more-archive"
            >
              {t('webAdmin.caseDetailV4.archive', { locale: 'cs' })}
            </button>
          ) : null}
          <div className="rh-a2-menu__sep" />
          <button
            type="button"
            className="rh-a2-menu__row"
            onClick={() => { setOpen(false); onOpenTab(CASE_AUDIT_ENTRY_TAB); }}
            data-testid="case-more-audit"
          >
            {t('webAdmin.caseDetail.v2.auditDetails', { locale: 'cs' })}
          </button>
          {items
            .filter((item) => item.id !== CASE_AUDIT_ENTRY_TAB)
            .map((item) => (
              <button
                key={item.id}
                type="button"
                className="rh-a2-menu__row"
                onClick={() => { setOpen(false); onOpenTab(item.id); }}
                data-testid={`case-more-${item.id}`}
              >
                {item.label}
              </button>
            ))}
        </div>
      ) : null}
    </div>
  );
}
